import {
  NUGGET_THEMES,
  type StandardTrackCode,
  Tier,
  appStrings as commonAppStrings,
  numberToWords,
} from '@backrs_ui/common-ui';

import { SchoolLevel } from '../models/models';
import { SEARCH_PAGE_CATEGORIES } from '../pages/Search/search.types';
import type { SchoolTier } from '../redux/satActBooster/satActBooster.types';

export const formatPageTitle = (title: string): string => `${title} | Backrs`;

export const appStrings = {
  modules: {
    satActBooster: {
      nav: {
        back: 'Back',
        next: 'Next',
        analyzeScores: 'Analyze Scores',
        buildPlan: 'Build my plan',
        reportSeamPrompt: 'Walk me through my ACT/SAT Test Booster score report.',
        targetSchoolsPrompt: 'Analyze the target schools I have picked so far.',
        targetSchoolChatMessage: (school: string) => `Analyze ${school} as a target school`,
      },
      steps: {
        INTAKE: 'Your Test History',
        SCORE_ACT: 'Your ACT scores',
        SCORE_SAT: 'Your SAT scores',
        TARGETS: 'Your goals',
        SCHEDULE: 'Your study schedule',
      },
      insufficientSchedule: {
        calloutTitle: 'Your schedule is on the light side',
        hours_per_week_below_minimum:
          "You've scheduled under 5 hours of study a week. Adding study days gives this plan more room to work.",
        hours_per_week_critically_low:
          "You've scheduled under 2 hours of study a week. A pace this light won't build momentum even over a long stretch, so a few more hours each week gives this plan real room to work.",
        fewer_than_minimum_weeks:
          'Your start and test dates are less than 4 weeks apart. More runway before test day makes a bigger score jump realistic.',
        total_hours_below_minimum:
          'Your plan adds up to under 25 hours of study before test day. Fewer days off, or another study day, would give you more practice time.',
        generic: 'Your study schedule may be too light to reach your target.',
        confirmContinue: 'Continue anyway',
        confirmGoBack: 'Go back and adjust',
      },
      compositeCheck: {
        title: 'Double-check your composite',
        body: "Your composite doesn't match the average of your section scores. If that's from a superscore, you're good to continue — otherwise, go back and check your report.",
        confirmContinue: 'Continue anyway',
        confirmGoBack: 'Go back and check',
      },
      intake: {
        title: 'About you',
        body: 'A few quick details so we can tailor your plan to you.',
        firstNameLabel: 'First name',
        firstNamePlaceholder: 'Your first name',
        zipLabel: 'ZIP code',
        zipPlaceholder: '12345',
        zipError: 'Enter a valid 5-digit ZIP code',
        schoolLabel: 'Your school',
        schoolPlaceholder: 'Search for your school',
        gradYearLabel: 'Expected graduation year',
        gradYearPlaceholder: 'Select a year',
        startYearLabel: 'Start year',
        startYearPlaceholder: 'Select a year',
        whichTestsLabel: 'Which test(s) have you taken?',
      },
      whichTestsOptions: { ACT: 'ACT', SAT: 'SAT', Both: 'Both', none: "Haven't taken yet" },
      focusPrefOptions: {
        single: {
          label: 'One content area per day',
          helper:
            'One focused topic per session — best for students who prefer depth over breadth.',
        },
        double: {
          label: 'Two content areas per day',
          helper: 'Two topics per session — best for students who prefer variety.',
        },
      },
      scoreInput: {
        actTitle: 'Your ACT scores',
        actBody: 'Enter your most recent ACT report — composite and section scores are required.',
        satTitle: 'Your SAT scores',
        satBody: 'Enter your most recent SAT report — total and section scores are required.',
        sectionScores: 'Section scores',
        subscoresHeading: {
          act: 'Detailed Subscores',
          sat: 'Knowledge and Skills',
        },
        subscoresBody: {
          act: "Subscores unlock a sharper skill-by-skill diagnosis, but you can skip them if you don't have your reporting categories handy.",
          sat: "Subscores unlock a sharper skill-by-skill diagnosis, but you can skip them if you don't have your content domains handy.",
        },
        rangeLow: 'Low',
        rangeHigh: 'High',
        rangeInverted: "Low can't be higher than High",
        totalMismatch: 'Total should equal Reading and Writing plus Math',
        compositeMismatch:
          'Composite is usually the average of your section scores — double-check your report',
        sectionLabels: {
          composite: 'Composite',
          english: 'English',
          math: 'Math',
          reading: 'Reading',
          science: 'Science',
          writing: 'Writing',
          total: 'Total Score',
          erw: 'Reading & Writing',
        },
      },
      scoreField: {
        emptyPlaceholder: '—',
        optional: 'optional',
        percentUnit: '%',
        outOfRange: (min: number, max: number) => `Please enter a value between ${min} and ${max}`,
      },
      report: {
        error: "We couldn't generate your score report. Please try again.",
        categoriesError:
          "We couldn't load your score categories, so your report can't be saved yet. Please try again.",
        notSaved: "Your progress isn't saved yet — we'll try again when you build your plan.",
        calculating: 'Calculating score report',
        calculatingHint: 'Lubav is writing it in your chat — it will appear there in a moment.',
        ready: 'Score report calculated — see Lubav for details.',
        streamFailed:
          "We couldn't generate your score report, but you can keep going and build your plan.",
      },
      generatingPlan: {
        title: 'Building Your Study Plan...',
      },
      loadError: {
        title: "We couldn't load your Test Booster",
        body: 'Something went wrong loading your saved progress. Please try again.',
        retry: 'Try again',
      },
      calendar: {
        kinds: {
          rest: 'Rest',
          off: 'Off',
          timed: 'Timed',
          test_day: 'Test Day',
        },
        emptyState: 'Your study plan hasn’t been generated yet.',
      },
      datePicker: {
        placeholder: 'Select a date',
        previousMonth: 'Previous month',
        nextMonth: 'Next month',
        today: 'Today',
        cancel: 'Cancel',
        apply: 'Apply',
        weekdayInitials: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
      },
      targets: {
        title: 'Your goals',
        body: 'Set your target score and the schools you want to aim for.',
        scheduleTitle: 'Your study schedule',
        scheduleBody:
          "Tell us when you test and how you'll study — your test date and weekly routine shape the plan we build.",
        targetScore: 'Target score',
        current: (score: number, pct: string) => `Current: ${score}${pct}`,
        currentPct: (ordinalValue: string) => ` (${ordinalValue} pct)`,
        targetHint: (test: string) =>
          test === 'act'
            ? 'Most students gain 2–4 points with focused prep.'
            : 'Most students gain 80–150 points with focused prep.',
        decrementAria: 'Decrease target score',
        incrementAria: 'Increase target score',
        targetScoreRange: (min: number, max: number) =>
          `Enter a target score between ${min} and ${max}.`,
        testDate: 'Next official test date',
        testDateRequired: 'Pick your test date to build your plan.',
        testDateOutOfRange: 'Pick a test date between tomorrow and two years from now.',
        startDate: 'Study start date',
        startDatePrompt:
          'The day your plan begins. Today by default — pick a later day to start then.',
        startDateOutOfRange: 'Pick a start date between today and the day before your test date.',
        startDateOutOfWindow: 'Pick a start date between today and two years from now.',
        whichTest: 'Which test?',
        upcomingDates: (test: string) => `Upcoming ${test} dates:`,
        schoolsLabel: (max: number) => `Target schools (up to ${max})`,
        schoolsSupporting:
          'Search any accredited 4-year US college. We look up the official mid-50% score ranges.',
        schoolSearchPlaceholder: 'Search any accredited 4-year US college…',
        schoolSearchHint: (min: number) => `Type at least ${min} characters to search`,
        schoolSearchEmpty: 'No matches',
        schoolSearchError: 'School search is unavailable right now. Try again in a moment.',
        schoolSearchLoading: 'Searching…',
        hours: 'Hours per study day',
        hoursPrompt: 'How many hours can you commit on a typical study day?',
        hoursAria: 'Hours per study day',
        hoursUnit: 'hrs',
        focusPref: 'Daily focus preference',
        studyDays: 'Study days',
        studyDaysPrompt: "Which days can you study? Uncheck days you're unavailable.",
        studyDaysRequired: 'Select at least one study day to build your plan.',
        weekdayLabels: {
          0: 'Sunday',
          1: 'Monday',
          2: 'Tuesday',
          3: 'Wednesday',
          4: 'Thursday',
          5: 'Friday',
          6: 'Saturday',
        },
        weekdayInitials: {
          0: 'S',
          1: 'M',
          2: 'T',
          3: 'W',
          4: 'T',
          5: 'F',
          6: 'S',
        },
        daysOff: 'Days off',
        daysOffPrompt:
          "Add specific dates you won't be able to study. These will be blacked out on your calendar.",
        dayOffReasonPlaceholder: 'e.g. family trip',
        addDayOff: '+ Add',
        coverageBanner: (weeks: number, sessions: number) =>
          `Based on your test date, your plan will cover approximately ${weeks} ${
            weeks === 1 ? 'week' : 'weeks'
          } · ${sessions} total study ${sessions === 1 ? 'session' : 'sessions'}.`,
        fitTable: {
          columnSchool: 'School',
          columnAct: 'ACT mid 50%',
          columnSat: 'SAT mid 50%',
          columnYourScore: (test: string) => `Your ${test}`,
          columnStatus: 'Status',
          range: (low: number, high: number) => `${low}–${high}`,
          notReported: 'Not reported',
          actNotReportedTooltip:
            "Some schools report an SAT range only. Check the school's admissions page for ACT data.",
          satNotReportedTooltip:
            "Some schools report an ACT range only. Check the school's admissions page for SAT data.",
          tierLabels: {
            reach: 'Reach',
            target: 'Target',
            safety: 'Safety',
          } as const satisfies Record<SchoolTier, string>,
          emptyScore: '—',
          unknownSchool: 'This school',
          disclaimer:
            "Score ranges are estimates from the US Dept. of Education's College Scorecard. Verify with each school.",
        },
        schoolContext: {
          reached: (school: string, low: number, target: number) =>
            `${school}'s mid-50% starts at ${low} — a ${target} puts you in range.`,
          nearest: (school: string, low: number, points: number) =>
            `${school}'s mid-50% starts at ${low} — ${points} more ${
              points === 1 ? 'point' : 'points'
            } puts you in range.`,
        },
      },
      noScoreModal: {
        title: 'Take a practice test first',
        body: 'We build your study plan from real section scores. Take an official practice ACT or SAT, then come back and enter your results to get started.',
        dismiss: 'Got it',
      },
      results: {
        gateTitle: 'Your study plan is ready!',
        gateSupporting:
          'Create a free Backrs account to view your study plan. It only takes a minute.',
        signupCta: 'Create an account',
        goBack: 'Go back',
        planAtAGlance: {
          title: 'Your Plan at a Glance',
          empty: 'Your plan summary is being prepared and will appear here shortly.',
          summary: {
            intro: (test: string) =>
              `Your plan combines content review on Khan Academy with timed practice sections from official ${test} materials.`,
            sessionsWithTimed: (
              weeks: number,
              totalSessions: number,
              contentDays: number,
              timedDays: number,
            ) =>
              `Across the next ${weeks} ${weeks === 1 ? 'week' : 'weeks'} you have ${totalSessions} ${totalSessions === 1 ? 'study session' : 'study sessions'} (${contentDays} ${contentDays === 1 ? 'content-focus day' : 'content-focus days'} + ${timedDays} ${timedDays === 1 ? 'timed-section day' : 'timed-section days'} every third week). Content days follow your prioritized focus areas; timed days rotate through different official practice tests.`,
            sessionsNoTimed: (weeks: number, totalSessions: number) =>
              `Across the next ${weeks} ${weeks === 1 ? 'week' : 'weeks'} you have ${totalSessions} ${totalSessions === 1 ? 'study session' : 'study sessions'}, each following your prioritized focus areas.`,
            focus: (test: string) =>
              `Each study day below names the specific skill you'll practice — pulled directly from your weakest ${test} bands. Use Khan Academy and the official PDFs below for the actual drills.`,
            insufficient:
              'Heads up: your current schedule is on the light side. Adding study days or moving your test date back will give this plan more room to work.',
          },
        },
        studyProfile: {
          label: 'Your Study Profile',
          phases: {
            foundation: 'Foundation',
            standard: 'Standard',
            sprint: 'Sprint',
            final: 'Final Stretch',
          },
        },
        stats: {
          pointsToGain: 'Points to Gain',
          totalWeeks: 'Total Weeks',
          hoursPerWeek: 'Hours/Week',
        },
        projection: {
          label: 'Projected Test-Day Score',
          band: (ci95: number) => `± ${ci95}`,
          rangeAria: (ci95: number) => `Estimated range, plus or minus ${ci95} points`,
          summary: (weeks: number, current: number) =>
            `Based on your current pace, mastery data, and ${weeks} ${weeks === 1 ? 'week' : 'weeks'} remaining. You're currently at ${current}.`,
          summaryNoScore: (weeks: number) =>
            `Based on your current pace, mastery data, and ${weeks} ${weeks === 1 ? 'week' : 'weeks'} remaining.`,
          whatIf: (hours: number) => `What if I added ${hours} hrs a week?`,
          whatIfAria: 'Extra study hours per week',
        },
        studyPlan: {
          title: 'Your Study Plan',
          subtitle: 'Your full calendar of study topics, resources, and practice tests.',
        },
        recommendedTools: {
          title: 'Recommended Tools & Resources',
          subtitle: 'Our best Backrs solutions to meet your specific needs.',
        },
        lubav: {
          summarizeLabel: 'Summarize my plan',
          summarizeChatMessage: 'Summarize my study plan',
          summarizePrompt:
            'Summarize my ACT/SAT study plan and tell me what to focus on first, using my computed plan facts.',
          discussLabel: 'Discuss with Lubav',
          discussChatMessage: 'Discuss my recommended tools',
          discussPrompt:
            'Walk me through the recommended tools and resources shown on my results page. For each one, tell me what it does and whether it fits me, then say which to use first. Use my study-plan goal only as brief framing for why a tool helps; do not re-summarize my whole plan.',
        },
        actions: {
          bookmarkError: 'We couldn’t save that bookmark. Please try again.',
        },
        replan: {
          cta: 'Replan',
          confirmTitle: 'Retake the booster?',
          confirmBody:
            "If you retake the booster, all of your current results will be deleted and you'll start over from the beginning.",
          confirmCta: 'Retake booster',
          cancel: 'Cancel',
        },
      },
      dailyPractice: {
        tabTitle: 'Daily Practice',
        noDaySelected: 'Select a day on your calendar to see that day’s practice.',
        header: {
          weekChip: (week: number) => `Week ${week}`,
          prevDayAria: 'Previous day',
          nextDayAria: 'Next day',
          today: 'Today',
          complete: 'Complete',
          incomplete: 'Incomplete',
          toggleAria: 'Toggle day completion',
          quizRequired: 'Finish today’s practice quiz to mark this day complete',
        },
        studyTargets: {
          title: 'Today’s Study Targets',
          testDayTitle: 'Test Day',
          empty: 'No study targets for this day — take it as a breather or use it to catch up.',
          timed: (section: string) =>
            `Take the ${section} section under timed conditions, then review your answers — the official practice test is linked on this page.`,
          timedNoResource:
            'Take today’s practice section under timed conditions, then review your answers.',
          testDay: 'Good luck today! Don’t forget to bring your ID and a calculator.',
        },
        resource: {
          khanTitle: (category: string) => `Khan Academy - ${category}`,
          practiceTestTitle: (section: string) =>
            section ? `Official ${section} Practice Test` : 'Official Practice Test',
          practiceTestBlurb:
            'Official full-length practice. Read the instructions below before you start, then review your answers.',
          timedBlurb: (testLabel: string, minutes: number, questions: number) =>
            `${testLabel} times this section — ${minutes} minutes, ${questions} questions.`,
          openAria: 'Open resource in a new tab',
        },
        quiz: {
          title: 'Today’s Practice Quiz',
          progress: (current: number, total: number) => `Question ${current} of ${total}`,
          optionLabel: (letter: string) => `${letter}.`,
          optionFigureAlt: (letter: string) => `Answer option ${letter} figure`,
          questionFigureAlt: 'Figure for this question',
          back: 'Back',
          submit: 'Submit answer',
          next: 'Next',
          seeResults: 'See results',
          comingSoonTitle: 'Practice quiz coming soon',
          comingSoonBody:
            'We’re still building practice questions for this topic. Study today’s targets with the resource on this page — you can mark the day complete when you’re done.',
          saveError: 'We couldn’t save your quiz. Check your connection and try again.',
          questionsError:
            'We couldn’t load today’s practice questions. Check your connection and try again.',
          answersError:
            'We couldn’t load your saved quiz results. Check your connection and try again.',
        },
        feedback: {
          correct: 'Correct',
          incorrect: 'Incorrect',
          overallTitle: 'Overall',
          correctOptionAria: 'Correct answer',
        },
        skill: {
          strand: (strandId: number | string) => `Strand ${strandId}`,
          general: 'General practice',
        },
        completionError: 'We couldn’t update this day. Check your connection and try again.',
        results: {
          title: 'Quiz complete',
          score: (score: number, total: number) => `${score} / ${total}`,
          percent: (pct: number) => `${pct}%`,
          bySkillTitle: 'By skill',
          reviewToggle: 'Review your answers',
          takeNextQuiz: 'Take next quiz',
          answerLine: (your: string, correct: string) =>
            `Your answer: ${your} · Correct: ${correct}`,
          noAnswer: '—',
          discussLabel: 'Discuss my results',
          discussChatMessage: 'Discuss my quiz results',
          discussPrompt: (date: string, score: number, total: number, breakdown: string) =>
            `I just finished my ${date} practice quiz and scored ${score} out of ${total}. My by-skill breakdown was: ${breakdown}. Can you walk me through the specific questions I missed and what I should focus on next?`,
          discussPromptPending: (date: string) =>
            `I'm working through my ${date} practice quiz. Can you help me think through these questions?`,
        },
      },
    },
    sharedSession: {
      blueprintLoadError:
        'Unable to load the shared blueprint. The link may have expired or the data is temporarily unavailable.',
    },
    shareResourceModal: {
      title: (resourceName: string) => `Share ${resourceName}`,
      description:
        'Create a shareable link to send to family, friends, counselors, etc. Link will be valid for 90 days.',
      shareLinkLabel: 'Share link',
      cancelButton: 'Cancel',
      copyLinkButton: 'Copy Link',
      copiedTooltip: 'Copied!',
      confirmDelete: {
        title: (resourceName: string) => `Hide Shared ${resourceName}`,
        description:
          'Are you sure you want to hide this link? This will revoke access for anyone who previously had shared.',
      },
    },
    minimumTrack: {
      header: {
        title: 'Your Minimum Track',
        subtitle: (date: string) => `Based on your Blueprint as of ${date}`,
        careerTrack: 'What is a career track?',
        myBlueprint: (name?: string) => (name ? `${name}'s Blueprint` : 'My Blueprint'),
      },
      title: 'All the Career Tracks',
      description: 'Explore other career tracks and their trade-offs.',
      careerTrack: 'Career Track',
    },
    signup: {
      title: 'Sign Up',
      subtitle: 'Sign up in less than 2 minutes.',
      haveAccount: 'Already have an account?',
      login: 'Log in',
      fourteenPlus: 'I certify that I am 14 years or older',
      copyright: `© Backrs ${new Date().getFullYear()}`,
      supportEmail: 'support@backrs.com',
    },
    signupPromptModal: {
      title: 'Create an account',
      subtitle: 'Create a free account to access all Backrs content.',
      subtitleBlueprint: 'Your results are ready — create a free account to see them.',
      subtitleSatActBooster: 'Your study plan is ready — create a free account to unlock it.',
      accountCreatedLoginFailed:
        "Your account was created, but we couldn't sign you in. Please log in to continue.",
    },
    profileCompletionModal: {
      title: 'About your education',
      subtitle: 'A few quick details so we can tailor what we show you.',
      graduatedTitle: "What's next for you?",
      graduatedSubtitle:
        "Our records say you've finished. Update these so we can keep up with what you're doing now.",
      enrollmentTypeLabel: 'Enrollment Type',
      enrollmentTypePlaceholder: 'Select an Enrollment Type',
      graduationYearLabel: 'Expected Graduation Year',
      graduationYearPlaceholder: 'Select a Graduation Year',
      startTermLabel: 'Start Term',
      startTermPlaceholder: 'Select a Term',
      startYearLabel: 'Start Year',
      startYearPlaceholder: 'Select a Year',
      saveButton: 'Save',
      saveFailed: "We couldn't save your answers. Please try again.",
    },
    sandboxPromptModal: {
      title: 'Want to give your counselors superpowers?',
      subtitle:
        'Unlock world class advising at your school, scale the impact of your counseling staff.',
      cta: "Let's Talk",
      cancel: 'Cancel',
    },
    sandboxContactCta: {
      headerButton: 'Connect with us',
    },
    sandboxUnavailable: {
      title: 'The demo is unavailable right now',
      subtitle:
        'Please try again in a moment. If it keeps happening, get in touch and we will set you up directly.',
      cta: 'Try again',
    },
    blueprintOutput: {
      signUpToView: {
        title: 'Your Blueprint is ready!',
        subtitle:
          'Create a free Backrs account to view your Blueprint results. It only takes a minute.',
        cta: 'Create an account',
        goBack: 'Go back',
      },
    },
    verifyEmail: {
      loading: 'Verifying your email...',
      error: {
        title: 'Verification Failed',
        subtitle: 'We were unable to verify your email. The link may have expired or is invalid.',
        contactSupport: 'Contact Support',
      },
    },
    oauthCallback: {
      loading: 'Signing you in...',
      needsRegistration: {
        title: 'No account found',
        subtitle:
          "We couldn't find a Backrs account linked to this Google identity. Please contact support if you believe this is a mistake.",
      },
      serviceUnavailable: {
        title: 'OAuth provider temporarily unavailable',
        subtitle: 'Please try again in a moment.',
      },
      genericError: {
        title: 'Sign-in failed',
        subtitle: 'Something went wrong while signing you in. Please try again.',
      },
      backToLogin: 'Back to log in',
      contactSupport: 'Contact Support',
    },
    connectApp: {
      loading: 'Preparing your connection...',
      genericError: {
        title: 'Connection failed',
        subtitle:
          'Something went wrong while connecting your account. Please close this window and try again.',
      },
    },
    suggestedNuggetsSidebar: {
      title: {
        nuggetPage: 'You might also like',
        competencyPage: 'Recommended for you',
      },
      description: {
        nuggetPage: 'Resources similar to this one',
        competencyPage: 'Playbooks and featured resources',
      },
    },
    educationalInvestment: {
      header: {
        title: 'Educational Investment',
        subtitle: 'Your recommended education based on your track',
        myBlueprint: (name?: string) => (name ? `${name}'s Blueprint` : 'My Blueprint'),
      },
      sections: {
        targetEducationalInvestments: {
          title: 'Your Target Education',
          description: (numEducationalInvestments: number) =>
            `Based on your minimum career track, we believe these ${numberToWords(numEducationalInvestments)} investments to be the best for you:`,
        },
        otherEducationalInvestments: {
          title: 'Other Educational Investments',
          description: 'These are less likely to lead to the career track you aspire to reach.',
        },
        educationTradeOff: {
          title: 'The Education Trade-off',
          description: 'Choose an education that will pay off in the long run.',
          content1:
            'Education is an investment in yourself. It costs time and money now, but it builds skills, opens career paths, and can raise your future pay. Thinking like an investor helps you compare choices—college or grad school, program, and major—and figure out how long it might take to earn back what you spend. When you weigh cost, likely results, and your goals, you can choose a path that fits you and helps you get to where you want to be sooner.',
          content2:
            'Explore tools on the right to explore ways to improve your return (what you get back). Look for ways to lower the cost of college, improve your chances for admission, and focus on schools that match your goals, budget, and career plans. Smart choices now can save you money today and lead to bigger benefits down the road.',
          imageCards: {
            majorMalfunction: {
              title: 'Major Malfunction',
              description:
                'Picking a major? Don’t pick a loser. Choosing a major is one of the most important career decisions a student can make.',
              cta: 'Explore Majors',
            },
            findScholarships: {
              title: 'Find Scholarships',
              description:
                'Browse vetted scholarships that may be a good fit for your qualifications.',
              cta: 'Search for Scholarships',
            },
          },
        },
      },
      educationalInvestmentCardToSucceed: {
        highSchoolOnly: [
          'Learn a skill or trade on the job or through apprenticeships',
          'Show up on time, work hard, and earn trust',
          'Look for employers or unions that invest in training or promote from within',
        ],
        tradeTechnicalSchool: [
          'Choose a high-quality program with strong job placement and industry connections',
          'Get licensed or certified if the field requires it',
          'Build a reputation for reliability and skill—your network will matter',
        ],
        communityCollege: [
          "Take courses that transfer cleanly if you plan to pursue a bachelor's degree",
          'Meet with advisors every semester to stay on track',
          'Finish with good grades and a plan—either a job or a transfer',
        ],
        lessSelective4Year: [
          'Choose a rigorous, in-demand major (like nursing, CS, accounting, etc.)',
          'Graduate with a very high GPA (3.7+) to stand out',
          'Gain real-world experience through internships, jobs, or projects',
        ],
        selective4Year: [
          'Pick a useful or rigorous major that employers value',
          'Build relationships with professors, alumni, and recruiters',
          'Combine academics with leadership roles, internships, or research',
        ],
        verySelective4Year: [
          'Maintain a strong GPA (even a 3.3–3.5 here counts) in a challenging major',
          'Take full advantage of recruiting pipelines and networking events',
          'Line up elite internships early—these schools offer access but not guarantees',
        ],
        graduateProfessionalSchool: [
          "Know the ROI (return on investment) before you start—some degrees pay off, some don't",
          'Crush the tests (GRE, MCAT, LSAT) and keep undergrad GPA strong',
          'Be ready to commit time, energy, and money for multiple years',
        ],
        entrepreneurship: [
          'Try small things early: sell, build, or solve something for someone else',
          'Work with or around real businesses—interning, freelancing, or pitching',
          'Learn from wins and failures—everything is a rep',
        ],
      },
    },
    priorities: {
      header: {
        title: 'Your Playbooks',
        addTask: 'Add Task',
        addPlaylist: 'Add Playbook',
        viewBookmarks: 'Bookmarks',
      },

      playbookPage: {
        backToPlaybooks: 'All Playbooks',
        newTask: 'New Task',
        sortBy: 'Sort By',
        reorder: 'Reorder',
        doneReordering: 'Save Order',
        edit: 'Edit',
        delete: 'Delete',
        playbookBadge: 'Playbook',
        share: 'Share',
        linkCopied: 'Link copied!',
        openInSidebar: 'Open in sidebar',
      },

      taskPage: {
        due: 'Due',
        viewPlaybook: 'View Playbook',
        edit: 'Edit',
        duplicate: 'Duplicate',
        delete: 'Delete',
        complete: 'Complete',
        incomplete: 'Incomplete',
        toggleCompleteAria: 'Toggle task complete',
        description: 'Description',
        priority: 'Priority',
      },

      sectionHeader: {
        searchError: 'Your search did not return any matches',
        filter: 'Filter',
      },

      taskDialog: {
        title: 'Create a Task',
        description: 'Create a to-do that you can add to a playbook.',
        editTitle: 'Edit Task',
        editDescription: 'Update your task details.',
        cancel: 'Cancel',
        addTask: 'Add task',
        updateTask: 'Update task',
        unassignedTaskCreated: 'Added to your unassigned tasks',
        dueDatePlaceholder: 'Type or select date',
      },

      playlistDialog: {
        editTitle: 'Edit Playbook',
        description: 'Create a new playbook to organize your tasks',
        editDescription: 'Update your playbook details',
        cancel: 'Cancel',
        addPlaylist: 'Create Playbook',
        updatePlaylist: 'Update Playbook',
        imageLabel: 'Playbook Image',
        uploadImage: 'Upload Image',
        removeImage: 'Remove Image',
        uploadingImage: 'Uploading...',
        invalidImageType: 'Please select a PNG or JPG image file',
      },

      taskCard: {
        editTask: 'Edit Task',
        deleteTask: 'Delete Task',
        duplicateTask: 'Duplicate Task',
        deleteTaskConfirmBody:
          'Are you sure you want to delete this task? This action cannot be undone.',
        statusPopoverFooter: 'Update',
      },

      playlistDrawer: {
        title: 'Add task to playbook',
        searchBarPlaceholder: 'Search',
        dontAdd: "Don't add to playbook",
        reorderPlaylist: 'Reorder Playbook',
        editPlaylist: 'Edit Playbook',
        deletePlaylist: 'Delete Playbook',
        createPlaylist: 'Create new playbook',
        newTask: 'New Task',
        sortBy: 'Sort By',
        sortOptions: 'Sort Options',
        actions: 'Actions',
        deletePlaylistConfirmBody:
          'Are you sure you want to delete this playbook? Doing so will also delete all tasks in this playbook. This action cannot be undone.',
        doneReordering: 'Save Order',
      },

      playlistTemplateDrawer: {
        addPlaylist: 'Add Playbook',
        addingPlaylist: 'Adding Playbook',
      },

      suggestedPlaylistsSection: {
        title: 'Suggested Playbooks',
        description: 'Based on your aspirations and current progress.',
        searchPlaceholder: 'Search suggested playbooks',
        blueprintButtonText: 'View more playbooks',
      },

      playlistsSection: {
        title: 'Your Playbooks',
        description: 'Supercharge your workflow and connect the tool you use every day.',
        searchPlaceholder: 'Search playbooks',
        emptyState: {
          buildPlaylist: {
            title: 'Build a playbook',
            description: 'Customize lists for whatever you need.',
            buttonLabel: 'Start a new playbook',
          },
          collegeShortlist: {
            title: 'Colleges to Research',
            description: 'Create a college shortlist and add to it over time.',
            buttonLabel: 'Create a college shortlist',
          },
          scholarshipOpportunities: {
            title: 'Scholarship Opportunities',
            description: 'Place scholarships that you could apply to here!',
            buttonLabel: 'Add scholarships',
          },
        },
      },

      unassignedTasksSection: {
        title: 'Unassigned Tasks',
        description: 'Content, to-dos, or notes that have not been added to a playbook',

        searchTasksPlaceholder: 'Search tasks',

        exampleTasks: {
          title: 'Create a task',
          description:
            'A task is a note to yourself that can include everything from links (in and outside of Backrs) to due dates, priority levels, and more.',
        },
      },

      playlistCard: {
        tasksCount: (count: number) => {
          if (count === 1) {
            return `${count} Task`;
          }

          return `${count} Tasks`;
        },
        addPlaylist: 'Add Playbook',
      },
    },
    accountSettings: {
      header: {
        title: 'Account Settings',
        description: 'Manage your account settings and preferences.',
      },
      settings: {
        header: {
          title: 'Settings',
          description: 'Update your settings and personal details here.',
        },
        sections: {
          name: {
            title: 'Name',
          },
          password: {
            title: 'Password',
            description: 'Enter a new password if you’d like to change it.',
          },
          activationCode: {
            title: 'Activation code',
            description: 'Have a code from your school or program? Apply it to your account here.',
          },
        },
      },
      billing: {
        header: {
          title: 'Billing',
          description: 'Manage your billing information and subscription.',
        },
      },
      seats: {
        tabLabel: 'Seats',
        header: {
          title: 'Seats',
          description: 'The seats your organization holds on each program roster.',
        },
      },
      family: {
        sections: {
          invites: {
            title: 'Invite Family Members',
            description: 'Share your progress and aspirations with up to six family members.',
            submit: 'Send Invites',
            submitLoading: 'Sending invites...',
            addAnother: 'Add another',
            namePlaceholder: 'Name',
            emailPlaceholder: 'you@example.com',
            rolePlaceholder: 'Role',
            invitesSent: 'Invites sent!',
            invitesFailed: 'Invites failed. Please retry.',
          },
          members: {
            title: 'Family Members',
            description: 'Manage your existing family members and change roles.',
            nameLabel: 'Name',
            roleLabel: 'Role',
            deleteLabel: 'Delete',
            revokeLabel: 'Revoke',
            leaveLabel: 'Leave',
            emptyOwnerTitle:
              'Your family currently has no other members. Invite someone to join above.',
            emptyMemberTitle: 'You are not currently a member of any family.',
            confirmLeave: {
              header: 'Leave Family Plan',
              body: 'Are you sure you want to leave this family plan? You will lose access to shared benefits.',
              confirmButton: 'Leave',
            },
            confirmRemove: {
              header: 'Remove Family Member',
              body: 'Are you sure you want to remove this member from your family plan? They will lose access to shared benefits.',
              confirmButton: 'Remove',
            },
            confirmRevoke: {
              header: 'Revoke Invitation',
              body: 'Are you sure you want to revoke this invitation? The recipient will no longer be able to join your family plan.',
              confirmButton: 'Revoke',
            },
          },
        },
      },
    },
    activationCode: {
      haveCode: 'Have an activation code?',
      enterItHere: 'Enter it here.',
      modalTitle: 'Enter your activation code',
      placeholder: 'Activation code',
      submit: 'Submit',
      codeRequired: 'Enter a code to continue.',
      errorProcessing: 'Error processing activation code.',
      success: 'Activation code applied.',
      refreshFailed:
        'Activation code applied, but we couldn’t refresh your account. Please reload the page.',
      reload: 'Reload page',
    },
    subscription: {
      subscriptionPageContainer: {
        successfulPayment: 'Payment method successfully added! Your subscription is now active.',
        successfulPayment2: 'Subscription created successfully! Welcome to your new plan.',
        checkoutCancelled: 'Checkout was cancelled. You can try again anytime.',
        creatingCheckout: 'Creating secure checkout ...',
        completeYourSubscription: 'Complete Your Subscription',
        troubleWithYourSubscription: 'Trouble with your subscription?',
        contactSupport: 'Contact support.',
      },
      paymentSetup: {
        title:
          'Your payment information is encrypted and secure. You can update or cancel your subscription at any time.',
        cancel: 'Cancel',
        subscribe: 'Subscribe',
      },
      subscriptionPlans: {
        currentPlan: 'Current Plan',
        selectPlan: 'Select Plan',
        selectedPlan: 'Selected ✓',
        failedToLoadPlans: 'Failed to load subscription plans. Please try again later.',
        noAvailablePlans: 'No subscription plans are currently available.',
        upgradeYourPlan: 'Upgrade your plan',
        continue: 'Continue',
        chooseBestPlan: 'Choose the best option for your needs.',
        free: 'Free',
        basicPlan: {
          name: 'Basic',
          description:
            'Build your Blueprint and explore careers, colleges, and majors. Upgrade anytime.',
        },
      },
      embeddedCheckout: {
        initializing: 'Initializing secure checkout...',
        error: 'Failed to load payment system',
        completeYourSubscription: 'Complete Your Subscription',
        backToPlans: '← Back to plan selection',
      },
      returnPage: {
        success: {
          title: 'Thank you for subscribing!',
          subtitle:
            'Your subscription is now active and you have full access to all Backrs features such as:',
          features: [
            'Backrs Success System',
            'Scholarships',
            'Internships & Job Opportunities',
            'Conflict-Free Content',
            'Cutting Edge Tools',
          ],
          goToDashboard: 'Back to Dashboard',
          completeYourOnboarding: 'Complete your Onboarding',
          confirmationEmail: (email: string) =>
            `A confirmation email will be sent to your email address: ${email}.`,
          amountPaid: (amount: string, currency: string) => `Amount: $${amount} ${currency}/month`,
        },
        error: {
          title: 'Payment Failed',
          subtitle: 'There was an issue processing your payment. Please try again.',
          tryAgain: 'Try Again',
          contactSupport: 'Contact Support',
        },
        loading: 'Loading...',
      },
      subscriptionStatus: {
        cancelAtPeriodEnd: (date: string) =>
          `Your subscription will be canceled at the end of the current billing period on ${date}. You can reactivate it anytime before then.`,
        portalError:
          'Unable to open billing portal. Please try again or contact support if the issue persists.',
        plan: 'Plan',
        foundationsPlan: 'Foundation Plan',
        cancelsOn: 'Cancels On',
        nextBillingDate: 'Next Billing Date',
        openingPortal: 'Opening Portal...',
        manageBilling: 'Manage Billing & Payment',
        updatePaymentInfo: 'Update payment method, download invoices, or manage your subscription',
      },
    },
    library: {
      header: {
        title: 'Competency Library',
        subtitle: 'Build skills and mindsets and learn what it takes to succeed in the real world.',
        bookmarks: 'Bookmarks',
        builtOnFourUnlocks: 'Built on Four Unlocks',
      },
    },
    deepDives: {
      header: {
        title: 'Coaching',
        subtitle:
          'Structured programs designed to help you succeed at every step of your education and career journey.',
        meetOurCoaches: 'Meet our coaches',
        searchPlaceholder: 'Search all deep dives',
      },
      subHeader: {
        title: 'Available Deep Dives',
        subtitle: (count: number) => `${count} deep dives currently available`,
      },
      tabs: {
        all: 'All',
        highSchool: 'High School',
        college: 'College',
        academic: 'Academic',
        career: 'Career',
      },
      categories: [
        'Most Popular',
        'Recommended Offerings',
        'Essays & Applications',
        'Academics & GPA',
        'Test Prep',
        'College Exploration & Decisions',
        'Career Exploration',
        'Internships & Professional Skills',
        'Scholarships & Financial Strategy',
        'Graduate School Prep',
      ],
      detail: {
        viewAll: 'View all deep dives',
        backToDeepDives: 'Back to Deep Dives',
        whatYouWillLearn: "What You'll Learn",
        whatsIncluded: "What's Included",
        bookSession: 'Book a Session',
        scheduleIntroCall: 'Schedule a free intro call',
        scheduleACall: 'Schedule a Call',
        scheduleAConsultation: 'Schedule a Consultation',
        bookmark: 'Bookmark',
        share: 'Share',
        coachesSidebar: {
          title: 'Offered by these coaches',
          subtitle: 'Select a coach to learn more',
        },
        price: (price?: number) => (price ? `$${price}` : null),
      },
      emptyState: {
        title: 'No deep dives found',
        subtitle: 'Try adjusting your search or filters',
      },
      reviewCards: {
        testPrep: {
          quote:
            "[My coach] has been phenomenal. I feel way more prepared for the SAT and ACT. I feel like I finally have a real plan instead of just guessing what I'm going to do...I definitely recommend doing a Backrs session. It makes everything feel a lot more manageable.",
          name: 'Oliver',
          subtitle: 'High School Senior',
        },
        careerExploration: {
          quote:
            'What I love about Backrs is how innovative and detailed they are in helping students. [They] really helped me pinpoint the best career paths and exactly what I need to do to improve my chances of success in my career journey.',
          name: 'Nesha',
          subtitle: 'Senior at Kennesaw State University',
        },
        internshipsProfessionalSkills: {
          quote:
            "I can't recommend Backrs enough. I'm seeing so much value through my sessions with my coach. If you want to start truly locking in on your personal, career, and life goals, [sign up for] Backrs. You won't regret it.",
          name: 'Jaron',
          subtitle: 'Senior at Johns Hopkins University',
        },
      },
    },
    calendarBookingModal: {
      title: 'Schedule Your Consultation',
      description: 'Select a time that works for you',
    },
    coaches: {
      header: {
        title: 'Backrs Coaches',
        subtitle:
          'Our coaching team brings together industry professionals and expert college and career advisors',
        exploreDeepDives: 'Explore Deep Dives',
        scheduleConsultation: 'Schedule a Consultation',
        searchPlaceholder: 'Find a coach',
      },
      sidebar: {
        title: 'Coaching categories',
      },
      tabs: {
        all: 'View all',
      },
      emptyState: {
        title: 'No coaches found',
        subtitle: 'Try adjusting your search or filters',
      },
    },
    profile: {
      gradeChangeConfirm: {
        header: 'Check your current grade',
        notStartedHeader: 'Check your start year',
        body: {
          changed: (from: string, to: string) =>
            `Based on what you've entered, we will change your grade from ${from} to ${to}, and move your past Are You On Track results to match. Does that look right?`,
          set: (to: string) =>
            `Based on what you've entered, we will set your grade to ${to}, and place your past Are You On Track results to match. Does that look right?`,
          finished: (from: string) =>
            `Based on what you've entered, you have finished ${from}. We will clear your grade. Does that look right?`,
          notStarted:
            "Based on what you've entered, you haven't started your program yet. We will clear your grade. Does that look right?",
        },
        confirm: "Yes, that's right",
        cancel: 'Cancel',
      },
      public: {
        header: {
          message: 'Message',
          back: 'Back',
          edit: 'Edit Profile',
          scheduleConsultation: 'Schedule a Consultation',
        },
        story: 'Story',
        snapshot: 'Snapshot',
        currentEnrollment: 'Current Enrollment',
        role: 'Role',
        sponsoringInstitution: 'Sponsoring Institution',
        organizations: 'Organizations',
        resume: 'Resume',
        interests: 'Interests',
        relationships: 'Relationships',
        languages: 'Languages',
        personalityTypes: 'Personality Types',
        introVideo: 'Intro Video',
        coachProfile: {
          aboutMe: 'About me',
          education: 'Education',
          experience: 'Experience',
          meetCoach: (name: string) => `Meet ${name}`,
          deepDives: 'Deep Dives',
        },
        linkedIn: {
          deriveHeader: (name?: string) => (name ? `${name}'s LinkedIn` : 'LinkedIn'),
          mainLink: 'View on LinkedIn',
          viewDetails: 'View Details',
          career: 'Career',
          education: 'Education',
          skills: 'Skills',
        },
      },
      edit: {
        editProfile: 'Edit Profile',
        save: 'Save',
        viewProfile: 'View Profile',
        privateField: '(PRIVATE)',
        update: 'Update',
        delete: 'Delete',
        deleteStoryConfirmHeader: 'Delete story',
        deleteStoryConfirmBody:
          'Are you sure you want to delete this story? This action cannot be undone.',
        deleteDocumentConfirmHeader: (isIntroVideo: boolean) =>
          `Delete ${isIntroVideo ? 'intro video' : 'document'}`,
        deleteDocumentConfirmBody: (isIntroVideo: boolean) =>
          `Are you sure you want to delete ${isIntroVideo ? 'your intro video' : 'this document'}? This action cannot be undone.`,
        deleteConfirmHeader: (isFieldProfileImage: boolean) =>
          isFieldProfileImage ? 'Delete profile image' : 'Delete banner image',
        deleteConfirmBody: (isFieldProfileImage: boolean) =>
          isFieldProfileImage
            ? 'Are you sure you want to delete your profile image? This action cannot be undone.'
            : 'Are you sure you want to delete your banner image? This action cannot be undone.',
        imageUpload: {
          headshot: {
            title: 'Upload Profile Image',
            description: 'Upload a new profile image to your profile',
          },
          banner: {
            title: 'Upload Banner Image',
            description: 'Upload a new banner image to your profile',
          },
          changeImage: 'Change Image',
          selectImage: 'Select Image',
          uploading: 'Uploading...',
          upload: 'Upload',
        },

        rigor: {
          modal: {
            title: 'Is My Coursework Rigorous Enough?',
            description:
              'The table below outlines different rigor levels, their definitions, and typical student profiles, helping you assess where your coursework falls and what colleges might expect.',
            modalCards: [
              {
                iconName: 'LayerSingle',
                color: 'Brand/100',
                title: 'Standard',
                subtitle: '',
                content:
                  'You have taken mostly on-level (non-honors) courses, with few or no Advanced Placement, International Baccalaureate, or dual enrollment classes. Your schedule consists primarily of standard core subjects such as Algebra I, grade-level English, General Science, and U.S. History. You may have taken one advanced course, but not across multiple subjects. This level typically reflects minimal exposure to college-level coursework.',
              },
              {
                iconName: 'LayersTwo01',
                color: 'Brand/400',
                title: 'Medium',
                subtitle: '',
                content:
                  'You have taken some, but not most, of the most rigorous classes available at your school. Your schedule includes a mix of honors, Advanced Placement, International Baccalaureate (at the Standard Level), or dual enrollment courses—often in subjects of strength or interest. You have likely completed two to three such courses by graduation, such as Honors Algebra II, Advanced Placement Psychology, Advanced Placement U.S. History, or International Baccalaureate Standard Level Biology.',
              },
              {
                iconName: 'LayersThree01',
                color: 'Brand/700',
                title: 'High',
                subtitle: '',
                content:
                  'You have taken most or all of the most demanding courses available at your school, especially in core academic areas like mathematics, science, English, and social studies. You have likely completed four to eight Advanced Placement, International Baccalaureate at the Higher Level, or dual enrollment courses, including advanced classes such as Advanced Placement Calculus BC, Advanced Placement Physics C, International Baccalaureate Higher Level subjects, or rigorous college-level math and science through dual enrollment. Students completing the full International Baccalaureate Diploma, Advanced Placement Capstone program, or a science, technology, engineering, and mathematics–focused schedule typically fall in this category.',
              },
              {
                iconName: 'LayersThree01',
                color: 'Brand/900',
                title: 'Very High',
                subtitle: '',
                content:
                  'You have pursued the maximum level of academic challenge offered at your school, with eight or more Advanced Placement, International Baccalaureate Higher Level, or dual enrollment courses across a broad range of disciplines. Your schedule includes advanced coursework in nearly every subject, often through the full International Baccalaureate Diploma with multiple Higher Level courses, Advanced Placement Capstone combined with six or more Advanced Placement classes, or extensive college-level courses through dual enrollment. Students in this category consistently take the most rigorous academic path available and are often among the top-performing students in their class.',
              },
            ],
          },
        },
      },
      permissionDenied: {
        titleBackgroundCheck: 'Profile Locked!',
        descriptionBackgroundCheck:
          "In order to view profiles of users under the age of 18, you'll need to pass a one-time background check.",
        titleSafety: 'Profile Restricted!',
        descriptionSafety:
          'This profile has been blocked from view for safety reasons. Please reach out to Coach Lubav for more information.',
      },

      modalUploader: {
        document: {
          uploading: 'Uploading',
          title: 'Upload a Document',
          description: 'Upload a document to your profile',
          modal: {
            title: 'Title*',
            documentType: 'Document Type*',
            description: 'Description*',
            fileType: 'File Type*',
            visibility: 'Visibility*',
            addDocument: 'Add document',
            addStory: 'Add story',
            resume: 'Resume',
            transcript: 'Transcript',
            other: 'Other',

            public: 'Public',
            panel: 'Panel',
          },
          link: {
            placeholder: 'Or paste a document link (Google Doc, Drive, etc.)',
            addError: "We couldn't add that link. Please try again.",
            saveError: "We read your link but couldn't save it to your profile. Please try again.",
            invalidUrl: 'Please enter a valid URL',
            unsupportedHost: (host: string, speaker: string) =>
              `${speaker} can't read ${host} links — download the file and upload it here instead.`,
            privateGoogle: (speaker: string) =>
              `This link is private. Set sharing to 'Anyone with the link → Viewer' so ${speaker} can read it.`,
            unreadable: (speaker: string) =>
              `${speaker} couldn't read this link. Try downloading the file and uploading it here instead.`,
            unreadableTooltip: "Lubav can't read this link.",
          },
        },
      },
    },
    lifeReadiness: {
      cta: 'Start Now',
      ctaContinue: 'Continue',
      backToCategories: 'Back to all categories',
      noAnswersForCategory: 'No answers recorded for this category yet.',
      keyTitle: 'What each level means',
      keyDescription:
        'This is a self-assessment — your ratings show how you see your own progress right now. Be honest with yourself and use these levels to notice what’s going well and where you can grow. There are no right or wrong answers — just a snapshot of where you are today.',
      subjectKeyDescription: (name: string) =>
        `This is a self-assessment — the ratings are ${name}’s own view of their progress right now. There are no right or wrong answers, so read them as a snapshot of where they are today rather than a score.`,
      categoryCard: {
        level1_count: '1',
        level2_count: '2',
        level3_count: '3',
        level4_count: '4',
      },
      header: {
        title: 'How life ready are you?',
        subjectTitle: (name: string) => `How life ready is ${name}?`,
        description:
          'Measure where you stand against the unlocks needed to succeed in today’s economy. Track your progress, celebrate milestones, and create evidence that opens doors to top schools, scholarships, and great jobs.',
        subjectDescription: (name: string) =>
          `Where ${name} stands against the unlocks needed to succeed in today’s economy. Follow their progress, mark the milestones, and see the evidence that opens doors to top schools, scholarships, and great jobs.`,
        buttonText: 'Take the assessment',
        ctaHeader: 'How life ready are you?',
      },
      explainerCard: {
        title: 'Your future starts now',
        description:
          "Learn what it really takes to succeed in today's economy and measure where you stack up.",
        cardBodyTitle: 'Welcome to Your Life-Readiness Assessment!\n\n',
        cardBodyDescription: [
          'This assessment will determine how you score on our seven markers of life-readiness.',
          "With your results, you'll be able to build a personal action plan to invest your energy wisely and unlock opportunities at great schools, scholarships, and jobs.",
          'You\'ll review key life-ready skills and mark where you stand today. Don\'t worry if you\'re "Not Yet" or "Early"—that\'s a normal place to begin!',
          'Click for examples and guidance as you go.',
        ],
      },
      switchTo: (subcategory: string) =>
        `Switch to ${subcategory.charAt(0).toUpperCase() + subcategory.slice(1)}`,
      questionTooltips: {
        knowledge: {
          notYet: {
            title: 'Not Yet',
            tooltip:
              'I’m not familiar enough with this yet to explain it or use it and/or I haven’t really focused on demonstrating achievement just yet.',
          },
          early: {
            title: 'Early',
            tooltip:
              'Understanding its purpose and basic concepts but lacking the ability to apply it effectively in real-world situations.',
          },
          strong: {
            title: 'Strong',
            tooltip:
              'Has a fundamental understanding of the competency and can perform basic tasks independently. Has discussed the competency with at least one person with at least advanced proficiency. May require guidance or supervision for more complex situations',
          },
          advanced: {
            title: 'Advanced',
            tooltip:
              'Possesses a strong understanding of the competency and can confidently perform a wide range of related tasks. Can apply their knowledge to solve problems and adapt to new situations. Has helped another person achieve basic proficiency.',
          },
        },
        achievement: {
          notYet: {
            title: 'Not Yet',
            tooltip:
              'Lacks meaningful action or achievement, may have made poor choices that hinder progress.',
          },
          early: {
            title: 'Early',
            tooltip:
              'Demonstrated engagement, but require modest effort or skill. Unlikely to be valued in and of itself by successful people, high quality universities and/or high quality employers.',
          },
          strong: {
            title: 'Strong',
            tooltip:
              'Requires meaningful effort and/or skill. Delivers value, effectiveness, and/or impact. Valued by many successful people, high quality universities and/or high quality employers.',
          },
          advanced: {
            title: 'Advanced',
            tooltip:
              'Requires meaningful effort and skill. Delivers significant value, effectiveness, and/or impact. Valued by most successful people, high quality universities and/or high quality employers.',
          },
        },
        consolidated: {
          notYet: {
            title: 'Not Yet',
            tooltip: 'I don’t understand this well yet and/or haven’t shown meaningful progress.',
          },
          early: {
            title: 'Early',
            tooltip: 'I know the basics but haven’t applied them in ways that matter.',
          },
          strong: {
            title: 'Strong',
            tooltip:
              'I understand this well and/or can do the basics on my own, showing effort and real value. Ideally achieved by high school graduation.',
          },
          advanced: {
            title: 'Advanced',
            tooltip:
              'I have deep understanding, can apply it in many situations, and/or my work makes a strong impact. Ideally achieved by college graduation. ',
          },
        },
      },
    },
    onboarding: {
      loadingMessages: [
        'Orchestrating account existence...',
        'Aligning digital chakras...',
        'Manifesting dashboard into being...',
      ],
      exploreDashboard: 'Explore your dashboard',
      dataLockedCards: {
        'data-locked': {
          title: 'Data Locked',
          description:
            'While personalization is at the heart of what we do, your trust and privacy are even more important. That’s why we’ve built an industry-leading privacy policy to ensure your data is always protected.',
          learnMore: 'Learn More',
        },
        'team-will-contact': {
          title: 'Our team will contact you!',
          description:
            'While personalization is at the heart of what we do, your trust and privacy are even more important. That’s why we’ve built an industry-leading privacy policy to ensure your data is always protected.',
          learnMore: 'Learn More',
        },
      },
      reviewCards: {
        matteo: {
          quote:
            "I love that Backrs has such an amazing tool to help me search for job opportunities and scholarships. It's versatile and easy to use.",
          name: 'Matteo A.',
          subtitle: 'High School Sophomore',
        },
        aurella: {
          quote:
            'Backrs has helped me get connected with people in roles I have interest in AND secure an internship with a company that aligns with my major and minor--which will give me insight into the business and let me see if I really want to go after it as a career.',
          name: 'Aurella K.',
          subtitle: 'College Sophomore',
        },
        sayvon: {
          quote:
            "I didn't realize how much I'd have to juggle in Freshman year: classes, making friends, figuring out what I actually want to do with my life, and, of course, finances. \n\nThat's where Backrs came in, and honestly, I don't think I'd be in the position I am now without them.",
          name: 'Sayvon B.',
          subtitle: 'College Freshman',
        },
        nesha: {
          quote:
            'The Backrs platform really helped me pinpoint the best career paths and exactly what I need to do to improve my chances of success in my career journey. It gave me a clear action plan to follow.',
          name: 'Nesha H.',
          subtitle: 'College Senior',
        },
      },
      inviteFamily: {
        title: 'Invite family members',
        subtitle:
          'Up to 6 family members can share a subscription to get access to Backrs tools and content.',
        addAnother: 'Add another',
        sendInvites: 'Send Invites',
        dontWantToAddFamilyMembers: "Don't want to add family members?",
        skip: 'Skip',
        invitesSent: 'Invites sent!',
        validation: {
          nameRequired: 'Name is required',
          roleRequired: 'Role is required',
          emailRequired: 'Email is required',
          emailInvalid: 'Please enter a valid email address',
        },
      },
      questions: {
        roleSelection: {
          title: "What's your role?",
          subtitle: 'This helps us personalize your experience.',
          ruleHelperText: 'Please select your role',
          options: {
            student: 'Student',
            youngAdult: 'Young Adult (working)',
            parentGuardian: 'Parent/Guardian',
            educator: 'Educator',
            employer: 'Employer',
            other: 'Other',
          },
        },
        completeAccount: {
          title: 'Complete your account',
          subTitle: 'Sign up in less than 2 minutes',
          zipCode: {
            placeholder: 'Enter your zip code',
            ruleHelperText: 'Zip code is required',
          },
          enrollmentType: {
            placeholder: 'Enrollment Type',
            ruleHelperText: 'Please select an enrollment type',
            options: {
              highSchool: 'High School',
              trade: 'Trade/Technical School',
              college: 'College/University',
              communityCollege: 'Community College',
              graduate: 'Graduate School',
            },
          },
          currentEnrollment: {
            placeholder: 'School',
          },
          programStartTerm: {
            placeholder: 'Start Term',
            ruleHelperText: 'Please select a start term',
          },
          programStartYear: {
            placeholder: 'Start Year',
            ruleHelperText: 'Please select a start year',
          },
          expectedGraduation: {
            placeholder: 'Expected Graduation Date',
            ruleHelperText: 'Please select an expected graduation date',
            afterStartHelperText: 'Graduation year must be after your start year',
          },
        },
        activationCode: {
          title: 'Activation code',
          subTitle: 'Enter your activation code if you have one.',
          placeholder: 'Activation code (optional)',
          subactionTitle: "Don't have an activation code?",
          subactionText: 'Skip',
        },
        selectPlan: {
          title: 'Select your plan',
          subTitle: 'Choose the plan that works best for you.',
        },
        inviteFamily: {
          title: 'Invite family members',
          subTitle: 'Add up to 6 family members to your plan.',
        },
      },
      sidebar: {
        createAccount: 'Create account',
        yourRole: 'Your role',
        selectYourPlan: 'Select your plan',
        inviteFamily: 'Invite family members',
      },
    },
    tools: {
      header: {
        title: 'Backrs Tools',
        subtitle: 'Solution Sessions, guides, and tools to help you succeed',
        searchBarPlaceholder: 'Search all tools',
      },
      cardTitles: {
        blueprint: 'Backrs Blueprint',
        lifeReadiness: 'Life Readiness Assessment',
        boostYourProfile: 'Boost your profile/portfolio',
        collegeMajorGuide: 'College Major Guide',
      },
    },
    home: {
      header: {
        welcome: (name?: string) => `Welcome${name ? `, ${name}` : ''}!`,
        catchUp: 'Catch Up',
        networks: 'Networks',
        searchBarPlaceholder: 'Search',
      },
      blueprintCta: {
        title: 'Start by exploring your lifestyle, career goals, and family dreams.',
        description: 'Take the Backrs Blueprint to define your life aspirations.',
        ctaText: 'Build Your Life Blueprint',
      },
      trackAssessmentCta: {
        title: 'Are you on track for the life you want?',
        description:
          "Complete a quick checklist to unlock your track status and see how you're progressing.",
        ctaText: 'Assess My Track',
      },
      sections: {
        getBackedYourWay: {
          title: 'Get Backed, Your Way',
          description: `Backrs is a community committed to succeeding in school, great careers, and life. Discover opportunities, get coaching, build competency, and make new connections. Everything is personalized to you and based on FACTS, because we've got your back.`,
          greatCollege: {
            title: 'College',
            legacyTitle: 'Go to a Great College',
            description:
              'Tools to help you choose, apply, and finance your post-secondary journey.',
            actionStickerCards: {
              collegeShortlist: {
                id: 'college-shortlist',
                title: 'College shortlist',
                description: 'Expand your vision of what colleges might be for you.',
              },
              whatAreYourChances: {
                title: 'What are your chances?',
                description: 'Assess your odds of admission and learn to improve them.',
              },
              essayIdeas: {
                title: 'Essay ideas',
                description: 'Topics based on your personal experiences and aspirations.',
              },
              improveYourTestScores: {
                title: 'Improve your test scores',
                description: 'Get your ACT and/or SAT scores where you want them.',
              },
              testBooster: {
                title: 'Test Booster',
                description: 'Turn your ACT/SAT scores into a personalized study plan.',
              },
              majors: {
                title: 'Major decisions',
                description: 'Picking a major is a major decision. Let us help!',
              },
            },
          },
          scoreJobInternship: {
            title: 'Career',
            description:
              'Learn about career opportunities and enhance your ability to apply and land roles.',
            actionStickerCards: {
              resumeBuilder: {
                title: 'Resume Builder',
                description: 'Refine or develop a new resume in less than 30 minutes.',
              },
              updateSocialMedia: {
                title: 'Update Social Media',
                description: 'Optimize your profile to help (not hurt) your odds.',
              },
              jobsYouMightLove: {
                title: 'Jobs you might love',
                description: 'Explore career paths that fit your life aspirations and preferences.',
              },
              interviewing: {
                title: 'Interviewing',
                description: 'Prepare for interviews with confidence and to have fun.',
              },
              boostOfferOdds: {
                title: 'Boost odds of an offer',
                description: 'Assess your fit for a role and how you can improve your odds.',
              },
            },
          },
        },
        solutionSessions: {
          title: 'Solution Sessions',
          description: 'Get the hinge moments right with the help of Coach Lubav.',
          ctaLabel: 'Chat with Coach Lubav',
        },
        exploreOpportunities: {
          title: 'Discover Opportunities',
          legacyTitle: 'Explore Opportunities',
          description:
            'Scroll through hundreds of opportunities and filter them based on your needs.',
          ctaLabel: 'Explore all Backrs resources',
          imageCards: {
            exploreUniversities: {
              title: 'Explore Universities',
              description:
                'Explore potential colleges. Includes FACTs you won’t find anywhere else.',
            },
            exploreEmployers: {
              title: 'Browse Employers',
              description: 'Browse employers and find companies you’d want to work for.',
            },
            discoverJobsInternships: {
              title: 'Discover Jobs & Internships',
              description: 'Explore active positions through our community connections.',
            },
            findScholarships: {
              title: 'Find Scholarships',
              description:
                'Browse vetted scholarships that may be a good fit for your qualifications.',
            },
            majors: {
              title: 'Major Calculator',
              description:
                'Picking a major? Don’t pick a loser. Choosing a major is one of the most important career decisions a student can make.',
            },
            library: {
              title: 'Competency Library',
              description:
                'A holistic progression system to guide young people on what they must build to succeed in school, career, and life.',
            },
            lifeReadiness: {
              title: 'Life Readiness',
              description: 'How life-ready are you? Take the assessment to find out.',
            },
          },
        },
        upYourGame: {
          title: 'Up Your Game',
          description:
            'Determine the best ways to build networks and a portfolio that will give you the best shot at your life aspirations.',
          buildStrongProfile: {
            title: 'Build a Strong Profile',
            description: 'Strengthen your standing for opportunities that will get you ahead.',
            actionStickerCards: {
              resumeOptimizer: {
                title: 'Resume optimizer',
                description: 'Practical tips to make your resume application-ready.',
              },
              majorsAndCourses: {
                title: 'Majors and Courses',
                description: 'Explore and choose academic classes, majors, and degrees.',
              },
              studying: {
                title: 'Studying',
                description: 'Hacks for studying more efficiently and effectively.',
              },
              boostYourProfile: {
                title: 'Boost your profile',
                description: 'Ideas to help you become the strongest applicant you can be.',
              },
            },
          },
          exploreCareers: {
            title: 'Explore Careers You Might Love',
            description:
              'Learn the difference between career tracks and career paths, and where you stand on both.',
            actionStickerCards: {
              tracksToSucceed: {
                title: 'Tracks to Succeed',
                description: 'Get on and stay on track to achieve your aspirations.',
              },
              shadowing: {
                title: 'Shadowing',
                description: 'Tips to uncover shadowing opportunities.',
              },
              jobsPathsForYou: {
                title: 'Jobs/paths for you',
                description: 'Explore career paths that fit your life aspirations and preferences.',
              },
              successDecoded: {
                title: 'Success Decoded',
                description: 'Understand the markers of success for your chosen career path.',
              },
            },
          },
          buildSocialCapital: {
            title: 'Build Social Capital',
            description:
              'Determine how to form meaningful relationships within your community and build your network.',
            actionStickerCards: {
              networking: {
                title: 'Networking',
                description: 'Learn to build relationships and advance your career.',
              },
              commonThreads: {
                title: 'Common threads',
                description: 'Find people in our community & learn your commonalities.',
              },
              seekSupport: {
                title: 'Seeking support',
                description: 'Get comfortable reaching out for help, guidance, and resources.',
              },
              engageTeachers: {
                title: 'Engaging teachers',
                description: 'Build positive relationships with teachers and professors.',
              },
            },
          },
        },
        personalHealthAndWellness: {
          title: 'Personal Health & Wellness',
          description:
            'Mindsets, fundamentals, and guidance to build a healthy and rewarding life. Develop the skills to boost your physical and mental health, from eating well to forming strong interpersonal relationships.',
          attainFinancialFreedom: {
            title: 'Attain Financial Freedom',
            description: 'Tips to finance college, manage money, and build your portfolio.',
            actionStickerCards: {
              extraCash: {
                title: 'Extra Cash',
                description: 'Find ways to earn additional income when needed.',
              },
              lowerTheCostOfCollege: {
                title: 'Lower the cost of college',
                description: 'Find the best sources of financial aid for you.',
              },
              buildCredit: {
                title: 'Build Credit',
                description: 'Understand and manage credit and debt responsibly.',
              },
              savingsAndInvesting: {
                title: 'Savings & investing',
                description: 'Learn how to invest money to grow your wealth over time.',
              },
            },
          },
          beWell: {
            title: 'Be Well',
            description: 'Take care of yourself with these emotional and social health tools.',
            actionStickerCards: {
              navigatingStress: {
                title: 'Navigating stress',
                description: 'Understand and cope with feelings of stress and anxiety.',
              },
              sleepRestoration: {
                title: 'Sleep/restoration',
                description: 'Habits to restore energy & improve overall health.',
              },
              familyDynamics: {
                title: 'Family dynamics',
                description: 'Understand and manage relationships within your family.',
              },
              healthcare: {
                title: 'Accessing healthcare',
                description: 'Know how to find and use healthcare services.',
              },
            },
          },
          masterMindsets: {
            title: 'Master Mindsets',
            description:
              'Build the foundations of a great life with these core mindsets and fundamental skills.',
            actionStickerCards: {
              agency: {
                title: 'Agency',
                description: 'Take control of your life and make things happen.',
              },
              learnToLearn: {
                title: 'Learn to learn',
                description: 'Acquire the best habits for study, practice, and experience.',
              },
              growthMindset: {
                title: 'Growth mindset',
                description: 'Learn to use challenges and failure to your advantage. ',
              },
              problemSolve: {
                title: 'Problem solve like a pro',
                description: 'Practice identifying solutions to challenges.',
              },
            },
          },
        },
        completeYourProfile: {
          title: 'Complete your profile',
          description:
            'Backrs works better when we can personalize information to you, based on your preferences and qualifications. Fill out your profile checklist to get started.',
          dataLockedAndLoaded: {
            title: 'Your data, loaded and locked.',
            description:
              'While personalization is at the heart of what we do, your trust and privacy are even more important. That’s why we’ve built an industry-leading privacy policy to ensure your data is always protected. Here’s our promise to you:',
            checkboxItems: [
              'Your data is yours: We will never share your information with anyone without your consent.',
              'No selling out: Your personal data will never be sold to advertisers or companies trying to take advantage of you.',
              'You’re in control: We make it simple to delete all of your data whenever you want.',
            ],
            learnMore: 'Learn More',
          },
          profileChecklist: {
            title: 'Profile Checklist',
            checklistItems: {
              profilePicture: {
                title: 'Add a profile picture',
                description: 'Upload a professional photo',
              },
              introVideo: {
                title: 'Record an intro video',
                description: 'Introduce yourself',
              },
              uploadDocuments: {
                title: 'Upload your documents',
                description: 'Resume, transcript, test scores',
              },
              profileDetails: {
                title: 'Add profile details',
                description: 'Get the best Backrs experience',
              },
              socialCapitalQuiz: {
                title: 'Who do you know?',
                description: 'Take our social capital quiz',
              },
            },
          },
        },
      },
      lubav: {
        title: 'Coach Lubav',
        subtitle:
          'Your personal AI coach, here to help you navigate school, careers, and everything in between.', // TODO: Replace this with correct description when we have it
      },
    },
    resourceWrapper: {
      scoop: {
        title: 'The Scoop',
      },
    },
    employer: {
      scoop: {
        title: 'The Scoop',
        about: 'About',
        roles: 'Roles',
        industry: 'Industry',
        size: 'Size',
        track: {
          title: 'Track',
          modal: {
            header: {
              title: 'Understanding Career Tracks',
              description:
                'A career "track" reflects your current level of skill, achievement, and marketability in the workforce. More selective tracks typically offer greater compensation, prestige, and growth—but demand higher education, stronger credentials, and the ability to thrive in competitive environments. While moving into less selective tracks is relatively easy, climbing into more selective ones requires strategic planning, consistent effort, and deliberate choices around education, skills, and experiences. Identifying your target track early can help you make smarter career decisions and build toward your long-term goals.',
            },
            examples: 'Examples',
            body: {
              semiSkilled: {
                title: 'Semi-Skilled',
                description:
                  'These positions often require little formal education and offer limited advancement. Many in this track earn $25,000–$45,000 annually and face financial instability, especially in high-cost areas. Notably, about half hold some college education.',
                examples: [
                  'Retail Sales Associate',
                  'Food Service Worker',
                  'Security Guard/Receptionist',
                ],
              },
              tradesTechnical: {
                title: 'Trades/Technical',
                description:
                  'This track includes skilled trades that typically requires certifications or technical training instead of a bachelor’s degree. Compensation ranges from $40,000 to $110,000, with solid job security but physically demanding and occasionally hazardous work.',
                examples: ['Electrician', 'HVAC Technician', 'Plumbing Technician'],
              },
              entrepreneurship: {
                title: 'Entrepreneurship',
                description:
                  'Entrepreneurs start or own businesses, engage in side hustles, or pursue freelance work. This track offers high autonomy, creative freedom, and unlimited income potential—but comes with financial risk, inconsistent earnings, and intense workloads. Compensation can range from nothing to well over $500,000 depending on success.',
                examples: [
                  'Small Business Owner',
                  'Freelancer (design, consulting)',
                  'E-Commerce Seller',
                ],
              },
              middleTierProfessional: {
                title: 'Middle-Tier Professional',
                description:
                  'These positions usually require a bachelor’s degree and may include additional certifications. They offer balanced work-life schedules and steady incomes between $40,000 and $120,000, though advancement opportunities can be limited.',
                examples: [
                  'Administrative Assistant',
                  'Paralegal',
                  'Registered Nurse',
                  'K-12 Teacher',
                ],
              },
              upperTierProfessional: {
                title: 'Upper-Tier Professional',
                description:
                  'Selective careers in law, medicine, engineering, and university-level teaching fall here. These roles demand advanced education, specific skills, and significant experience. They offer salaries between $80,000 and $350,000+, along with respect and leadership opportunities—but often involve high stress and long hours.',
                examples: [
                  'University Professor',
                  'Corporate Lawyer',
                  'Senior Software Engineer',
                  'Financial Analyst',
                ],
              },
              topTierProfessional: {
                title: 'Top-Tier Professional',
                description:
                  'This elite track includes management consultants, investment bankers, world-class researchers, and more. Success requires top-tier education, advanced credentials, and major achievements. While annual compensation often exceeds $500,000, these roles involve intense pressure, demanding hours, and frequent travel.',
                examples: [
                  'Investment Banker at Elite Firm',
                  'Specialist Physician (surgeon)',
                  'Partner at Top Law Firm',
                  'Principal Investigator',
                ],
              },
            },
          },
        },
      },
      resources: {
        title: 'Resources',
        filter: 'Filter',
      },
      opportunities: {
        title: 'Opportunities',
        description: 'Current internships and job openings.',
        filter: 'Filter',
      },
      people: {
        title: 'People',
        networkCompetency: 'Networking Competency',
        description: (employerName: string) =>
          `Highlight backrs who have worked at or currently working at ${employerName}.`,
      },
      careerPath: {
        title: 'Career Paths',
        description: (employerName: string) => `Common career paths and roles at ${employerName}.`,
        filterByTrack: 'Filter by Track',
      },
    },
    internship: {
      header: {
        apply: 'Apply',
        companyWebsite: 'Company Website',
      },
      sections: {
        theScoop: 'The Scoop',
      },
      scoop: {
        about: 'About',
        positionType: 'Position Type',
        company: 'Company',
        location: 'Location',
        industry: 'Industry',
        careerPath: 'Career Path',
        compensation: 'Compensation',
        applicationDeadline: 'Application Deadline',
        sponsors: 'Sponsors',
      },
      qualifications: {
        title: 'Qualifications',
        description: 'Requirements and guideposts for this opportunity.',
        requirementsHeading: 'Requirements',
        advantagesHeading: 'Advantages',
      },
      resources: {
        title: 'Resources',
        description: 'Tools to help you choose, apply, and interview for opportunities.',
      },
    },
    assessMyTrack: {
      assessmentBreakdown: {
        academics: 'Academics',
        courseworkRigor: 'Coursework Rigor',
        testScores: 'Test Scores',
        gpa: 'GPA',
        collegeSelectivity: 'College Selectivity',
        majorRigor: 'Major Rigor',
        noTestScoresReported: 'None reported',
        noGpaReported: 'No GPA reported',
        gpaDescription: (gpa: number) => `${gpa}`,
      },
      collegeSelectivityLevels: {
        lessSelective: 'Less Selective',
        somewhatSelective: 'Somewhat Selective',
        highlySelective: 'Highly Selective',
      },
      transcriptNeededText: {
        highSchool: (gpa: number, courseworkRigorLevel: string, track: string) =>
          `Your ${gpa} GPA against a ${courseworkRigorLevel.toLowerCase()} rigorous set of classes suggests that you are on, at best, on ${track.toLowerCase()} track. However, we need your full transcript to accurately assess your academic strengths and opportunities for growth.`,
        college: (
          gpa: number,
          majorRigorLevel: string,
          schoolSelectivityLevel: string,
          track: string,
        ) =>
          `Your ${gpa} GPA with a ${majorRigorLevel.toLowerCase()} major at a ${schoolSelectivityLevel.toLowerCase()} university suggests that you are, at best, on a ${track.toLowerCase()} track. However, we need your full transcript to accurately assess your academic strengths and opportunities for growth.`,
      },
      rigorLevels: {
        veryRigorous: 'Very Rigorous',
        rigorous: 'Rigorous',
        lessRigorous: 'Less Rigorous',
        veryLowRigor: 'Very Low Rigor',
      },
      scorecardSubtitle: (dimensionCount: number) => `${dimensionCount} dimensions across the `,
      scorecardSubtitleLinkText: 'Four Unlocks',
      scorecardSubtitleSuffix:
        '. Select any category to learn more about each standard and edit your status.',
      scorecardSubtitleSuffixReadOnly: '.',
      scorecardLevels: commonAppStrings.modules.assessMyTrack.scorecardLevels,
      scorecard: 'Scorecard',
      gradeScorecardTitles: {
        grade9: 'High School Freshman Scorecard',
        grade10: 'High School Sophomore Scorecard',
        grade11: 'High School Junior Scorecard',
        grade12: 'High School Senior Scorecard',
        postSecondaryY1: 'College Freshman Scorecard',
        postSecondaryY2: 'College Sophomore Scorecard',
        postSecondaryY3: 'College Junior Scorecard',
        postSecondaryY4: 'College Senior Scorecard',
      },
      getGradeAssessmentTitle: (gradeName: string) => `${gradeName} Assessment`,
      header: {
        title: 'Are You On Track?',
        description:
          'Check your progress and get personalized recommendations to help you achieve your goals.',
        tour: 'Tour',
      },
      shareText: {
        title: 'Are You On Track?',
        description: 'Check out my track assessment results!',
      },
      subheaderFourUnlocks: {
        title: 'The Four Unlocks',
        description:
          'Assess your progress across the four unlocks—each one reflects a key part of life and career readiness.',
      },
      assessmentChecklistAcademicType: {
        title: 'Current Enrollment',
        description: 'This information will be updated in your profile.',
        save: 'Save',
        thisFeatureIsOnlyForStudents: 'This feature is only for students.',
        unsupportedEnrollmentConfirmTitle: 'Heads up',
        unsupportedEnrollmentConfirmBody:
          "This feature is for high school and undergraduate students. Your selection will be saved, but you won't have access to Are You On Track.",
        unsupportedEnrollmentConfirmButton: 'Okay',
      },
      assessmentChecklist: {
        title: 'Assessment Checklist',
        description: 'The information we need to unlock your track status',
        thisInformationWillBeUpdatedInYourProfile:
          'This information will be updated in your profile.',
        recordedInThisAssessment: 'Recorded in this assessment.',
        notRecordedInThisAssessment: 'Not recorded in this assessment.',
        thisInformationWillBeUpdatedInYourProfileAndReassessed:
          'This information will be updated in your profile and your assessment will automatically re-run.',
        noEnrollmentType: 'You must select an enrollment type to continue',
        save: 'Save',
        assessMyTrack: 'Assess my track',
        assessError: 'Something went wrong. Please try again.',
        checklistItems: {
          lifeReadiness: {
            title: 'Life Readiness',
            description: 'Complete your life readiness assessment',
            descriptionComplete: 'Completed',
          },
          academicMetrics: {
            title: 'Test Scores',
            description: 'Add your test scores',
            editModal: {
              title: 'Test Scores',
              doneButtonText: 'Add Test Scores',
            },
          },
          gpa: {
            title: 'GPA',
            description: 'Add your GPA',
            editModal: {
              title: 'GPA',
              doneButtonText: 'Add GPA',
            },
          },
          postSecondaryEnrollment: {
            title: 'School',
            description: 'Add your school',
            editModal: {
              title: 'School',
              doneButtonText: 'Add School',
            },
          },
          coursework: {
            title: 'Rigor',
            description: 'Coursework level',
            editModal: {
              title: 'Rigor',
              doneButtonText: 'Add Rigor',
            },
          },
          major: {
            title: 'Major',
            description: 'Add your major',
            editModal: {
              title: 'Major',
              doneButtonText: 'Add Major',
            },
          },
          resume: {
            title: 'Resume',
            description: 'Upload your resume',
            descriptionComplete: 'Uploaded',
            editModal: {
              title: 'Upload Resume',
              doneButtonText: 'Add Resume',
            },
          },
          transcript: {
            title: 'Transcript (or report card)',
            description: 'Upload your transcript',
            descriptionComplete: 'Uploaded',
            editModal: {
              title: 'Transcript (or report card)',
              doneButtonText: 'Add Transcript',
            },
          },
          blueprint: {
            title: 'Blueprint',
            description: 'Find your track',
            descriptionComplete: 'Completed',
          },
          graduationYear: {
            title: 'Graduation Year',
            description: 'Add your graduation year',
            editModal: {
              title: 'Graduation Year',
              doneButtonText: 'Save Graduation Year',
            },
          },
        },
      },
      goTheExtraMile: {
        title: 'Go the Extra Mile',
        description: 'To get the best results, it helps to add:',
      },
      updateAssessment: 'Update Assessment',
      checklistReview: {
        title: 'Update Assessment',
        description: 'You can add or edit any of the items below to update your track.',
        backToChart: 'Back to My Track',
      },
      standardsByTrack: {
        title: 'Standards by Track',
        description:
          'Our standards reflect what it actually takes to succeed across different career paths. Explore the benchmarks that define readiness at each track.',
        highSchoolStandards: 'High School Standards',
        collegeStandards: 'College Standards',
      },
      timelineChart: {
        header: 'Are you on track?',
        lastAssessed: (date: string) => `Last assessed: ${date}`,
        description: (trackName: string) => `You\u2019re on the ${trackName} track, at best.`,
        blueprintDescription: {
          prefix: 'According to your ',
          linkText: 'Blueprint',
          suffix: (targetTrackName: string) => `, your target track is ${targetTrackName}.`,
        },
        forOther: {
          description: (name: string, trackName: string) =>
            `${name} is on the ${trackName} track, at best.`,
          blueprintDescription: (name: string, targetTrackName: string) =>
            ` According to ${name}\u2019s Blueprint, their target track is ${targetTrackName}.`,
        },
        gradeLabels: {
          earlyCareer: {
            early: 'Early',
            career: 'Career',
          },
          grade9: '9th',
          grade10: '10th',
          grade11: '11th',
          grade12: '12th',
          postSecondaryYear1: 'Y1 PS',
          postSecondaryYear2: 'Y2 PS',
          postSecondaryYear3: 'Y3 PS',
          postSecondaryYear4: 'Y4 PS',
        },
        dimensionScoreLabels: {
          unmet: 'Unmet',
          minimum: 'Minimum',
          advanced: 'Advanced',
        },
        unlockStatusLabels: {
          not_yet: 'Behind',
          developing: 'Developing',
          on_track: 'Strong',
        },
        currentMaxTooltip: 'Current Max',
        blueprintTrackTooltip: 'Target Min',
        projectionLabel: 'Range of possibilities',
        profileStrengthLabel: 'Non-Academic Profile',
        yourTrackLabel: 'Your Track',
        yourTrackLegend: {
          targetTracks: 'Target Track(s)',
          currentTrack: 'Current Track, at best',
          yourPotential: 'Your Potential',
        },
        assessmentSuffix: 'Assessment',
        editModal: {
          subtitle: "To override Lubav's assessment select a new status.",
          readOnlySubtitle: "Lubav's assessment and the criteria it was scored against.",
          lubavAssessment: "Lubav's Assessment",
          notYetDescription: "You haven't demonstrated this yet.",
          readOnlyNotYetDescription: 'Not demonstrated yet.',
          justificationLabel: 'Why are you changing this assessment? (optional)',
          justificationPlaceholder: 'Describe your experience or evidence...',
          submitButton: 'Submit',
          submitError: 'Failed to update score. Please try again.',
        },
        errorLoadingChart: 'Unable to load your track progress. Please try again later.',
        profileStrengthModal: {
          title: 'Profile Strength',
          description:
            "Think something's off? You can edit your score on any individual criteria by clicking on individual items in the rubric below.",
          statusDescriptions: {
            on_track:
              "You're demonstrating evidence of being on track across all three key unlock areas. Keep building on this momentum.",
            developing:
              "You're showing evidence of being on track in 1 or 2 of the three key unlock areas. You're making progress, but there's room to strengthen your profile before graduation.",
            not_yet:
              "You haven't yet demonstrated evidence of being on track in any of the three key unlock areas. This is common when you're just getting started, and it's exactly what coaching is for.",
          },
          ctaButton: 'Dig Deeper: How life-ready are you?',
        },
      },
    },
    college: {
      header: {
        buttons: {
          website: 'Website',
          share: 'Copied!',
        },
      },
      scoop: {
        title: 'The Scoop',
        enrollment: 'Enrollment',
        type: 'Type',
        size: 'Size',
        setting: 'Setting',
        bestKnownFor: 'Best Known For',
        vibeCheck: 'Vibe Check',
        opportunitiesNetworks: 'Opportunities & Networks',
      },
      whoGetsIn: {
        header: {
          title: 'Who gets in?',
          description: (collegeName: string) => `The average academic profile for ${collegeName}.`,
          admissionsButton: 'Admissions Website',
          askLubav: 'What are my chances?',
        },

        earlyDecision: 'Early Decision Deadline',
        applicationDeadline: 'Application Deadline',

        segmentChartTitle: 'School Selectivity Profile',
        selectivity: {
          title: 'Selectivity',
          modal: {
            title: 'What Does Selectivity Mean?',
            description:
              "Selectivity reflects how competitive a school's admissions process is based on acceptance rate, GPA and test score expectations, coursework rigor, and the strength of the applicant pool.",
            modalCards: [
              {
                iconName: 'LayersThree01',
                color: 'Brand/900',
                title: 'Highly Selective',
                subtitle: 'Top-Tier & Elite Universities',
                content:
                  'Very competitive admissions—these schools often have low acceptance rates. Admitted students typically have a 3.8+ unweighted GPA, top test scores, and have taken numerous AP, IB, or dual enrollment courses. Strong extracurriculars with leadership or national-level achievement are expected, and essays and recommendations carry significant weight.',
              },
              {
                iconName: 'LayersTwo01',
                color: 'Brand/400',
                title: 'Somewhat Selective',
                subtitle: 'Competitive Public & Private Universities',
                content:
                  'Competitive but less intense than highly selective schools. Admitted students typically have a 3.4–3.8 unweighted GPA with some AP, IB, or dual enrollment coursework. Test scores are often optional but helpful if strong. Extracurriculars showing depth and leadership can meaningfully improve chances.',
              },
              {
                iconName: 'LayerSingle',
                color: 'Brand/100',
                title: 'Not Very Selective',
                subtitle: 'Broadly Accessible Colleges & Universities',
                content:
                  'A more accessible admissions process. A solid GPA of 3.0–3.5 in standard college-prep courses is usually sufficient, and few AP or IB courses are needed. Test-optional policies are common, and strong essays or extracurriculars can make up for lower stats.',
              },
            ],
          },
        },

        charts: {
          acceptanceRate: {
            title: 'Acceptance Rate',
            tooltip: 'The percentage of applicants who were accepted.',
          },
          highSchoolGPA: {
            title: 'High School GPA',
            tooltip:
              'The 50th percentile GPA is the median—meaning, half of admitted students had higher GPAs, and half had lower. The GPA range shows where the middle 50% of admitted students fall, between the 25th and 75th percentiles.',
          },
          satScore: {
            title: 'SAT Score',
            tooltip:
              'The 50th percentile SAT score is the median—half of admitted students scored higher, and half scored lower. The SAT range shows where the middle 50% of admitted students fall, between the 25th and 75th percentiles.',
            switch: 'Switch to ACT',
          },
          actScore: {
            title: 'ACT Score',
            tooltip:
              'The 50th percentile ACT score is the median—half of admitted students scored higher, and half scored lower. The ACT range shows where the middle 50% of admitted students fall, between the 25th and 75th percentiles.',
            switch: 'Switch to SAT',
          },
          rigor: {
            title: 'Rigor',
            modal: {
              title: ' How Do Schools Consider Rigor?',
              description:
                'Colleges evaluate " rigor " —the difficulty of your high school coursework—in various ways. While good grades matter everywhere, selective institutions also pay close attention to whether you\'ve challenged yourself academically through advanced coursework such as AP, IB, or dual enrollment classes. How important rigor is varies significantly depending on the selectivity level and type of institution.',
              modalCards: [
                {
                  iconName: 'LayersThree01',
                  color: 'Brand/900',
                  title: 'Critically Important',
                  subtitle: 'Top-Tier Elite Universities',
                  content:
                    'Rigor is essential at these institutions. A schedule filled with the most challenging coursework—AP, IB, or dual enrollment—in all core subjects is typically expected, and strong performance in those courses is just as important as taking them.',
                },
                {
                  iconName: 'LayersThree01',
                  color: 'Brand/700',
                  title: 'Very Important',
                  subtitle: 'Highly Selective Public & Elite Private Universities',
                  content:
                    'Rigor plays a major role in admissions decisions. Taking advanced courses across core subjects and in areas aligned with your intended major is often a key differentiator—and performing well in those courses can significantly strengthen your application.',
                },
                {
                  iconName: 'LayersTwo01',
                  color: 'Brand/400',
                  title: 'Important',
                  subtitle: 'Competitive but Not Hyper-Selective Universities',
                  content:
                    'Rigor is a consideration, though it is typically not decisive. A balanced schedule featuring honors and AP, IB, or dual enrollment courses, particularly in your areas of interest, is generally sufficient.',
                },
                {
                  iconName: 'LayerSingle',
                  color: 'Brand/100',
                  title: 'Less Important',
                  subtitle: 'Moderately or Less Competitive Universities',
                  content:
                    'Rigor is reviewed but not heavily weighted. Meeting graduation requirements is typically sufficient, though taking a few advanced courses can still offer some admissions or scholarship benefits.',
                },
              ],
            },
          },
          extracurriculars: {
            title: 'Extracurriculars',
            modal: {
              title: ' How Do Colleges View Extracurricular Profiles?',
              description:
                'Colleges look at your activities outside the classroom to better understand your interests, leadership, and initiative. The categories below reflect common patterns of student involvement—along with the types of colleges where each profile is considered the norm (meaning around 50% of students admitted have similar levels of experience).',
              modalCards: [
                {
                  iconName: 'LayersThree01',
                  color: 'Brand/900',
                  title: 'Exceptional',
                  subtitle: 'Top-Tier Elite Universities',
                  content:
                    'Outstanding leadership or impact—often at the national or international level. Eg: Founded a nonprofit or business with measurable outcomes, won national awards in STEM, arts, or writing, created an app or platform used by thousands.',
                },
                {
                  iconName: 'LayersThree01',
                  color: 'Brand/700',
                  title: 'Very Strong',
                  subtitle: 'Highly Selective Public & Elite Private Universities',
                  content:
                    'Significant leadership and achievement with regional or high-level school impact. Eg: President of a large, active club, organized a TEDxYouth or major community event, varsity captain with state-level recognition.',
                },
                {
                  iconName: 'LayersTwo01',
                  color: 'Brand/400',
                  title: 'Strong',
                  subtitle: 'Competitive but Not Hyper-Selective Universities',
                  content:
                    'Consistent involvement with leadership or meaningful contributions. Eg: Editor of school newspaper or yearbook, Volunteered 100+ hours with increasing responsibility, lead role or stage manager in school theater production.',
                },
                {
                  iconName: 'LayerSingle',
                  color: 'Brand/100',
                  title: 'Typical',
                  subtitle: 'Moderately or Less Competitive Universities',
                  content:
                    'Takes part in a few activities, but with limited leadership or standout roles. Eg: Member of a school club or sports team, volunteered occasionally in community or religious groups, attended non-competitive enrichment programs.',
                },
                {
                  iconName: 'LayerSingle',
                  color: 'Brand/50',
                  title: 'Weak',
                  subtitle: 'Moderately or Less Competitive Universities',
                  content:
                    ' Little or inconsistent involvement in activities outside of class with no sustained impact or leadership. Eg: Listed a few activities with no roles or detail, sporadic volunteering or one-time events, no extracurriculars mentioned.',
                },
              ],
            },
          },
        },
      },
      theCost: {
        notAvailable: 'N/A',
        header: {
          title: 'The Cost',
          description: 'Tuition, financial aid, and school affordability.',
          netPriceButton: 'Calculate your personal net price',
          askLubav: 'Lower the cost of college',
          costByFamilyIncome: 'Cost by Family Income',
          costByFamilyIncomeTooltip:
            'What you actually pay for college depends on financial aid available for your income level. The average annual cost reflects what students receiving federal aid typically pay for one year, after subtracting grants and scholarships. It includes tuition, fees, books, and room & board.',
        },

        dataTable: {
          familyIncome: 'Family Income',
          averageAnnualCost: 'Average Annual Cost',

          net_price_income_level_0_30000: '$0 - $30,000',
          net_price_income_level_30001_48000: '$30,001 - $48,000',
          net_price_income_level_48001_75000: '$48,001 - $75,000',
          net_price_income_level_75001_110000: '$75,001 - $110,000',
          net_price_income_level_110001_plus: '$110,001 +',
        },

        costFigureTitles: {
          inStateTuition: 'In-State Tuition Cost',
          inStateTuitionTooltip:
            "A school's tuition cost reflects the base price for attending before financial aid, scholarships, or grants are applied. It includes charges for instruction but does not cover additional expenses like housing, meals, and fees.",

          outOfStateTuition: 'Out-of-State Tuition Cost',
          outOfStateTuitionTooltip:
            'Out-of-state tuition is the cost charged to students who do not reside in the same state as the public college or university they are attending. It is typically higher than in-state tuition since state residents benefit from government subsidies that reduce their educational expenses.',

          roomAndBoard: 'Room & Board Fees',
          roomAndBoardTooltip:
            'Room and board refer to the costs of housing and meals while attending college. These expenses vary based on factors such as on-campus or off-campus living, meal plan options, and the location of the school.',

          averageFinancialAid: 'Average Financial Aid Package',
          averageFinancialAidTooltip:
            'This is calculated by looking at the total amount of financial aid a school provides (need based and non-need based grants & scholarships), divided by the total number of students.',
        },
      },
      returnOnInvestment: {
        header: {
          title: 'Return on Investment',
          description: 'Earnings, graduation rate, and long-term return on investment.',
          askLubav: 'Explain this to me',
        },
        roiHorizon: {
          title: 'ROI Horizon',
        },
        cards: {
          successRate: {
            title: 'Success Rate',
            tooltip:
              '**What it is**\nThe share of students from low-income families who attended this school and later earned a high income as adults.\n\n**Why this matters**\nA higher success rate suggests the school provides pathways that help students translate education into strong earnings outcomes—through academic preparation, access to opportunity, and career-relevant support.\n\n**How it is calculated**\nCalculated by tracking students from families in the bottom 20% of U.S. income into adulthood using IRS tax records, and measuring the share who reach the top 20% of earners nationwide.\n\n**Source**\nMobility Report Cards: The Role of Colleges in Intergenerational Mobility, The Equality of Opportunity Project (Opportunity Insights).',
          },
          estimatedInvestment: {
            title: 'Estimated Investment',
            tooltip:
              'This total reflects not only your tuition, room and board, and school-related fees, but also the opportunity cost of attending college—approximately $166,000. That figure represents the income you might have earned by entering the workforce directly after high school, estimated at around $40,000 per year over four years.',
          },
          estimated10YearReturn: {
            title: 'Estimated 10 Year Earnings',
            tooltip:
              'Earnings are based on the cumulative 10 year median earnings for an individual that has both graduated from the school & received some type of federal aid.',
          },
          medianEarnings: {
            title: 'Median Earnings at 10 Years',
            tooltip:
              '**Source**: U.S. Department of Education, College Scorecard-median annual earnings of former students who received federal aid, measured 10 years after initial enrollment using linked administrative records.',
          },
          graduationRate: {
            title: 'Graduation Rate',
            tooltip: (yearsToGraduate: number) =>
              `Source: U.S. Department of Education, College Scorecard—the percentage of students who graduate within ${yearsToGraduate} years of starting at this college, based on federally reported completion data.`,
            nationalMidpointLabel: (schoolType: '2-year' | '4-year') =>
              `National Midpoint for ${schoolType} schools`,
            pellRecipientsLabel: 'Pell Recipients',
          },
        },
        successRateLevels: {
          high: {
            chipLabel: 'High',
            chipColor: 'Success/500',
          },
          medium: {
            chipLabel: 'Medium',
            chipColor: 'Warning/500',
          },
          low: {
            chipLabel: 'Low',
            chipColor: 'Error/400',
          },
          modal: {
            title: 'Success Rate Levels',
            description:
              'The success rate measures how likely students from lower-income families are to reach strong earnings outcomes after attending this school.',
            modalCards: [
              {
                iconName: 'ThumbsUp',
                color: 'Success/500',
                title: 'High — More likely than not',
                subtitle: 'Success Rate ≥ 50%',
                content:
                  'Students have better-than-even odds of reaching strong earnings outcomes in adulthood.',
              },
              {
                iconName: 'ThumbsUp',
                color: 'Warning/500',
                title: 'Medium — Meaningful chance',
                subtitle: 'Success Rate 25%–49%',
                content:
                  'Positive outcomes are real but not the majority. Results depend heavily on major choice, preparation, and access to opportunities.',
              },
              {
                iconName: 'ThumbsDown',
                color: 'Error/400',
                title: 'Low — Unfavorable odds',
                subtitle: 'Success Rate < 25%',
                content:
                  'Outcomes are challenging for most students, even with effort. Structural risk is high.',
              },
            ],
          },
        },
        medianEarningsLevels: {
          high: {
            chipLabel: 'High',
            chipColor: 'Success/500',
          },
          medium: {
            chipLabel: 'Medium',
            chipColor: 'Warning/500',
          },
          low: {
            chipLabel: 'Low',
            chipColor: 'Error/400',
          },
          modal: {
            title: 'Income Levels',
            description:
              'Median earnings reflect what former students typically earn 10 years after enrollment, compared to graduates from other institutions.',
            modalCards: [
              {
                iconName: 'ThumbsUp',
                color: 'Success/500',
                title: 'High',
                subtitle: 'Top 25% of schools nationally',
                content:
                  'Former students earn significantly more than peers at most other institutions.',
              },
              {
                iconName: 'ThumbsUp',
                color: 'Warning/500',
                title: 'Medium',
                subtitle: 'Middle 50% of schools',
                content:
                  'Earnings outcomes are typical compared to the national distribution of schools.',
              },
              {
                iconName: 'ThumbsDown',
                color: 'Error/400',
                title: 'Low',
                subtitle: 'Bottom 25% of schools',
                content:
                  'Former students tend to earn less than peers from most other institutions.',
              },
            ],
          },
        },
      },
      whatStudentsStudy: {
        header: {
          title: 'What Students Study',
          description:
            "Degree mix and program-level earnings for bachelor's and master's graduates.",
          askLubav: 'Explain this to me',
          lubavPrompt: (schoolName: string) =>
            `Explain what students study at ${schoolName}. Look at the "What Students Study" section on this page, including the Bachelor's and Master's tabs, the Degree Mix chart showing which fields graduates earn degrees in, and the program-level median earnings table.`,
        },
        levels: {
          bachelors: "Bachelor's",
          masters: "Master's",
        },
        degreeMix: {
          title: 'Degree Mix',
          largestField: 'Largest Field',
          largestProgram: 'Largest Program',
          info: {
            title: 'Degree Mix',
            definition:
              'Shows degrees completed, not students, grouped from federal program codes (CIP) into 16 broad fields. A double major counts as two; a school offering one broad degree with several specializations may show as a single category.',
            goodToKnow: 'Good to know',
            bullets: (
              awards: string,
              level: string,
              programCount: number,
              academicYear: string | null,
            ) => [
              `${awards} ${level} degrees awarded per year across ${programCount} program${programCount === 1 ? '' : 's'}.`,
              "Program count reflects CIP-4 programs reported to IPEDS at this credential level — may differ from the school's own program catalog.",
              academicYear
                ? `Figures reflect the most recently reported academic year (AY ${academicYear}).`
                : 'Figures reflect the most recently reported academic year.',
            ],
            source: 'Source: U.S. Department of Education, College Scorecard field-of-study data.',
          },
        },
        earnings: {
          title: 'Program Median Earnings',
          kpis: {
            median: {
              title: 'Median Program Earnings',
              subtext: (count: number) => `Across ${count} program${count === 1 ? '' : 's'}`,
            },
            iqr: {
              title: 'Middle 50% of Programs',
              subtext: 'Half of programs fall in this range',
            },
            range: {
              title: 'Range',
              subtext: (lowest: string, highest: string) => `${lowest} → ${highest}`,
            },
          },
          coverage: (reporting: number, total: number) =>
            `${reporting} of ${total} program${total === 1 ? '' : 's'} report earnings`,
          missingValue: '–',
          noEarnings: (level: string) =>
            `Earnings aren't available for ${level} programs at this school — each program had fewer than 30 graduates in the federal financial-aid cohort.`,
          table: {
            ariaLabel: 'Program earnings',
            program: 'Program',
            awards: 'Degrees/Year',
            earnings: 'Median Earnings',
          },
          info: {
            title: 'Program Earnings',
            definition: (cohortYears: readonly string[] | null, dollarYear: number | null) =>
              cohortYears && dollarYear
                ? `Shows median annual earnings of graduates who received federal financial aid, about four years after completing — for this data, the graduating classes of ${cohortYears.join(' and ')}, in ${dollarYear} dollars.`
                : 'Shows median annual earnings of graduates who received federal financial aid, about four years after completing.',
            goodToKnow: 'Good to know',
            bullets: [
              "Earnings aren't shown for programs with fewer than 30 graduates in that cohort, per federal privacy rules.",
              'A "–" in Degrees/Year means the program has no completions on file for the current year, even where an earlier class\'s earnings are shown.',
              "A program on both the Bachelor's and Master's tabs is offered at both levels — the two figures are independent, from different graduates.",
            ],
            source: 'Source: U.S. Department of Education, College Scorecard field-of-study data.',
          },
        },
      },
    },
    careerPath: {
      detailPage: {
        detailPageHeader: {
          careerPaths: 'Career Paths',
          careerPathsDescription:
            'Explore over 80 career fields and paths and get personalized recommendations.',
          myBlueprint: 'My Blueprint',
          viewAllCareerPaths: 'View all career paths',
        },
        description: {
          growth: 'Growth',
          growthModal: {
            title: 'Growth',
            subtitle:
              'We use growth levels to describe how job opportunities in each career path are expected to change over time. Growth levels are based on aggregated U.S. labor market projections and help indicate how dynamic or constrained a career path may be over time.',
            levels: [
              {
                'Negative Growth':
                  'Negative Growth paths are projected to shrink, with fewer roles available due to automation, consolidation, or declining demand',
                'Low Growth':
                  'Low Growth paths may see limited expansion, with slow increases in demand that tend to lag broader labor market growth.',
                'Stable Growth':
                  'Stable Growth paths are expected to remain relatively flat, with demand holding steady but limited change over time.',
                'Medium Growth':
                  'Medium Growth paths show steady increases in demand, often supported by ongoing economic, regulatory, or industry needs.',
                'High Growth':
                  'High Growth paths are expanding rapidly, with strong projected increases in hiring as industries evolve or scale.',
              },
            ],
          },
          aiDisruption: 'AI Disruption',
          caregiverFlexibility: 'Caregiver Flexibility',
          earlyCareerHires: 'Early Career Hires',
          earlyCareerHiresModalDescription:
            'We share early-career hiring numbers to help you understand how many opportunities exist for people just starting out in each career path. Fields with under 25,000 hires per year are considered small, 25,000 to 100,000 are medium, and over 100,000 are large, giving you a sense of how competitive or open each path might be.',
          discussLabel: 'Discuss with Lubav',
          discussPrompt: (careerPathName: string, fitLabel: string) =>
            `Explain why ${careerPathName} is a ${fitLabel} based on the Blueprint results. What interests, activities, and sources of meaning align or don't align with this career path? Keep it concise.`,
          lubavError: 'Failed to fetch career path fit explanation',
          noBlueprintError:
            'Want to know if this career is a good fit for you? Take the **Backrs Blueprint** to get personalized results.',
        },
        descriptionSidebar: {
          bestFitMajors: 'Best Fit Majors',
        },
        trackDistribution: {
          careerTracks: 'Career Tracks',
          sectionDescription: 'Key elements of achieving your life aspirations',
          careerTrackDistribution: 'Career Track Distribution',
          tierLabels: {
            trade: 'Trades/Technical',
            mid: 'Middle-Tier Professional',
            upper: 'Upper-Tier Professional',
            top: 'Top-Tier Professional',
            semiskilled: 'Semi-Skilled',
          },
          description:
            'Jobs within any field are spread across different career tracks, from semi-skilled roles to top-tier professional positions. Each track reflects a different level of education, training, responsibility, and compensation. The distribution of these tracks varies by field, depending on the types of work and expertise required.',
          tierPercentage: (percentage: number) => `${percentage}%`,
          headerCTA: 'What is a Career Track?',
        },
        earningPotential: {
          earningPotential: 'Earning Potential',
          description: 'Entry to late-career yearly earning estimates, based on track',
          ctaButtonTitle: 'Search jobs & internships',
          compensationTable: {
            rows: {
              careerTrack: 'Career Track',
              entryLevel: 'Entry Level',
              midCareer: 'Mid-Career',
              lateCareer: 'Late Career',
              sampleJobRoles: 'Sample Job Roles',
            },
            NA: 'N/A',
            dataSources: {
              description:
                'Compensation is compiled from the following resources and reviewed by our team for accuracy.',
              sources: [
                { name: 'Bureau of Labor Statistics (BLS)', url: 'https://www.bls.gov' },
                { name: 'Glassdoor', url: 'https://www.glassdoor.com' },
                { name: 'Levels.fyi', url: 'https://www.levels.fyi' },
                { name: 'Indeed', url: 'https://www.indeed.com' },
              ],
              feedback: {
                url: 'mailto:support@backrs.com',
                subject: (careerPathName: string) =>
                  `Feedback on compensation data - ${careerPathName}`,
                label: 'Want to give us feedback on our data?',
              },
            },
          },
        },
        resources: {
          title: 'Resources',
          loadingMore: 'Loading more...',
          noContentFound: (sectionName: string) => `No ${sectionName} found for this career path.`,
          filterButtons: {
            resources: {
              title: 'Resources',
            },
            opportunities: {
              title: 'Opportunities',
            },
            people: {
              title: 'Real People',
            },
            scholarships: {
              title: 'Scholarships',
            },
          },
          filter: 'Filter',
        },
        aiImpact: {
          impactOfAi: 'Impact of AI',
          description: 'How the rapidly changing AI landscape affects this career path',
          overallImpactOfAi: 'Overall Impact of AI',
          aiDisruption: 'AI Disruption',
          aiImpactDescription:
            'To what extent can AI perform the core functions of this job on its own?',
          modalSections: {
            automationRisk: 'Automation Risk',
            aiIntegrationIntensity: 'AI Integration Intensity',
            roleReinventionNeeded: 'Role Reinvention Needed',
            aiLeverageUpside: 'AI Leverage Upside',
            humanDifferentiatorIndex: 'Human Differentiator Index',
          },
          descriptions: {
            automationRisk: 'Can AI take over the core tasks?',
            integrationIntensity: 'Will AI tools be part of everyday work?',
            roleReinventionNeed: 'Will you need to learn new skills or totally reinvent your job?',
            leverageUpside: 'Can using AI give you a huge edge?',
            humanDifferentiatorIndex:
              'Is this job protected by human traits like empathy, creativity, or judgment?',
          },
          headerCTA: 'Boost your profile to beat AI',
        },
      },
      summaryPage: {
        title: 'Explore Career Paths',
        description:
          'A career path is a group of related jobs that share similar types of work, skills, and ways to succeed.',
        tour: 'Tour',
        myBlueprint: (name?: string) => (name ? `${name}'s Blueprint` : 'My Blueprint'),
        search: 'Search',
        strongFitPaths: {
          title: 'Strongest career paths based on your Blueprint',
          noBlueprintTitle: 'Find Your Best Fit Paths',
          description:
            'Each path is assessed for fit with preferred activities, environments, and sources of meaning.',
          strongFit: 'Strong Fit',
          mediumFit: 'Medium Fit',
          lowFit: 'Low Fit',
          showAll: 'Show All',
          showLess: 'Show Less',
          whatIsACareerPath: 'What is a Career Path?',
          takeBlueprintQuiz: 'Take the Blueprint Quiz',
          forPersonalizedResults: 'For personalized results, take the Backrs Blueprint!',
          noStrongOrMediumCareerFits:
            'No strong or medium career fits found. Try selecting another career field.',
        },
        allCareerPaths: {
          title: 'All Career Paths',
          description:
            'Explore each path including fit, compensation, and what it takes to succeed.',
        },
        whatIsACareerPathModal: {
          header: {
            title: 'What is a Career Path?',
            subtitle: 'Our guide to professional fields and roles',
          },
          topLeftSection: {
            title: 'Introduction to Career Paths',
            description:
              'A **career path** is a group of related jobs that share similar types of work, skills, and ways to succeed—even if the specific roles vary.\n\nMost career paths enable **progression**: you start in an entry-level role to build experience, and over time, you can take on more responsibility, earn more money, and gain more influence.\n\nBackrs organizes paths into  **fields** so you can explore what aligns with your interests, strengths, and goals.',
          },
          bottomRightSection: {
            title: 'How Tracks and Paths Intersect',
            description: `Your **track** is the overall level of career you're aiming for—like Upper-Tier Professional or Trade/Technical—based on things like income, recognition, and education required.\n\nYour **path** is the specific lane within that track—like Nursing, Accounting, or Software Development—that shapes the kind of work you’ll do and how you’ll grow over time.  Most paths includes jobs across tracks, but some paths are mostly trades/technical while others are mostly upper tier and top tier professional.\n\nYou target a track to match your life goals. You pick a path to match your interests, strengths, and where you find meaning.\n\nMost people switch paths three or more times. Less than 10% of people switch tracks after their early 20’s.`,
          },
          topRightSection: {
            business:
              'For people who like solving problems, leading teams, or using strategy to make things work better. Business careers are about creating and delivering goods and services that improve people’s lives—and doing it sustainably and profitably.',
            law: 'For students who care about justice, fairness, and serving their communities. These paths often involve rules, structure, and helping others through legal systems or public institutions.',
            stem: 'For curious thinkers who enjoy building systems, analyzing data, designing solutions—or doing research to discover how the world works. These paths shape the future through innovation and evidence.',
            'patient-care':
              'For people who want to make a direct impact on others’ physical or mental health. These paths require compassion, precision, and strong communication.',
            education:
              'For those who love helping others grow—whether it’s young kids, students, or adult learners. These paths often involve teaching, coaching, or guiding development.',
            arts: 'For creative minds who want to inspire, inform, or entertain others. These paths let you shape stories, visuals, or experiences through different forms of media.',
            agriculture:
              'For students interested in animals, food systems, and caring for the planet. These paths focus on sustainability, health, and the science of how we grow and distribute what we eat.',
            trades:
              'For people who like hands-on work and want to build, repair, or operate essential systems. These careers are often faster to enter and offer real skill-based progression.',
            'social-sector':
              'For students driven by purpose, community, and social impact. These paths focus on helping others, influencing change, and serving a mission greater than yourself.',
          },
        },
      },
    },
    majors: {
      header: {
        majorMalfunction: 'Major Malfunction',
        inputPlaceholder: 'Search all majors',
        noMajorsFound: 'No majors found',
      },
      descriptionSidebar: {
        typicalCollege: 'Typical College',
        typicalCollegeInfo: 'The type of college where this major is most commonly offered.',
        careerPaths: 'Associated Career Paths',
        careerPathsInfo: 'The career paths that this major is most commonly associated with.',
        nA: 'N/A',
      },
      academicExperience: {
        academicExperience: 'Academic Experience',
        sectionDescription: '6 key dimensions to measure against all majors.',
        competitiveness: 'Competitiveness',
        competitivenessInfo: 'Level of competition among students and attrition rates.',
        workloadIntensity: 'Workload Intensity',
        workloadIntensityInfo: 'Time committment and intensity of study required for success.',
        demand: 'Market Demand',
        demandInfo: 'Market demand and career security for graduates with this major.',
        specialization: 'Specialization',
        specializationInfo:
          'How specialized vs. general the major is in terms of skills and knowledge.',
        rigor: 'Rigor',
        rigorInfo: 'Academic difficulty and intellectual challenge of the coursework.',
        gradSchoolNecessity: 'Is graduate education needed for career advancement?',
        majorsAndCourses: 'Majors & Courses',
        optionValue: 'Option Value',
        optionValueInfo: 'The value and flexibility of career options available with this major.',
        keyAttributes: (major: string) => `Key Attributes: ${major}`,
        majorRigorText: {
          veryRigorous: 'Very Rigorous',
          rigorous: 'Rigorous',
          moderate: 'Moderate',
          notRigorous: 'Not Rigorous',
        },
      },
      graduateSchool: {
        graduateSchool: 'Graduate School',
        sectionDescription: 'How necessary is graduate school for this major?',
        necessity: 'Necessity',
        necessityInfo: 'How often graduate school is needed for career success in this field.',
      },
      careerOutcomes: {
        careerOutcomes: 'Career Outcomes',
        sectionDescription: '52% of graduates end up under-employed. How does this major stack up?',
        jobsForMe: 'Jobs and Roles for me',
        underEmployment: 'Under-employment',
        underEmploymentInfo: (rationale: string) => [
          'On average, 52% of post-secondary graduates end up under-employed. We define under-employment as jobs that do not require a college degree, are low paying, and have limited upward progression.',
          `Why this rating? ${rationale}`,
        ],
        average: 'Average',
        underemploymentPercentage: (value: number) => (value === 0 ? 0 : `${value}%`),
        sampleRoles: 'Sample Roles',
        successRate: 'Success Rate',
      },
      earnings: {
        title: 'Earnings',
        sectionDescription: 'Early and mid-career average earnings for this major.',
        rating: {
          muchBetterThanMost: 'Much better than most',
          betterThanMost: 'Better than most',
          notBad: 'Not Bad',
          average: 'Average',
          worseThanMost: 'Worse than most',
        },

        legend: {
          average: 'Average',
        },

        stages: {
          earlyCareer: 'Early Career',
          midCareer: 'Mid Career',
        },

        wages: {
          earlyCareerLabel: 'Median Early Career Wage',
          midCareerLabel: 'Median Mid Career Wage',
        },

        chart: {
          title: 'Early to Mid Career Wages',
          tooltip: 'Something about early to mid career wages',
        },

        comparisons: {
          earlyCareer: {
            significantlyMore: 'earn significantly more than',
            more: 'earn more than',
            aboutSame: 'earn about the same as',
            slightlyLess: 'earn slightly less than',
            less: 'earn less than',
          },
          midCareer: {
            significantlyHigher: 'their earnings are significantly higher than',
            higher: 'their earnings are higher than',
            aboutEqual: 'their earnings remain about equal to',
            justBelow: 'their earnings remain just below',
            below: 'their earnings remain below',
          },
        },

        description: (majorName: string, earlyComparison: string, midComparison: string) =>
          `In their early careers, ${majorName.toLowerCase()} majors ${earlyComparison} the overall median for new graduates, and by mid-career ${midComparison} the typical median for seasoned professionals.`,
      },
    },
    guidePage: {
      overview: 'Overview',
      jumpToSection: 'Jump to section',
    },
    commonAppEssayGuide: {
      prompts: {
        promptLabel: 'Prompt',
        good: 'Good',
        great: 'Great',
        quickTests: 'Quick Tests',
        riskToTake: 'Risk to Take',
      },
    },
    studyHabitsGuide: {
      workday: {
        ruleLabel: 'Rule',
      },
    },
    onlineReputationGuide: {
      steps: {
        stepLabel: 'Step',
      },
    },
    internshipGuide: {
      timeAllocation: {
        title: 'Ideal Time Allocation',
        networking: 'Networking',
        research: 'Research',
        applications: 'Applications',
        prepAndPractice: 'Prep & Practice',
        followUps: 'Follow-ups',
      },
      interviewCallbackRate: {
        title: 'Interview Callback Rate',
        withReferral: 'With Referral',
        tailoredApp: 'Tailored App',
        coldApply: 'Cold Apply',
        massApply: 'Mass Apply',
      },
    },
    majorsGuide: {
      careerRelevance: {
        whyWeBuiltThisTool: 'Why we built this tool',
        whyWeBuiltThisToolModal: {
          subtitle:
            "Choosing a college major is one of the most important decisions you'll make, but it's often unclear which majors actually lead to which careers. We analyzed thousands of real career outcomes to create the first comprehensive mapping between college majors and career paths.",
          dataDrivenInsightsTitle: 'Data-Driven Insights',
          dataDrivenInsightsDescription:
            'Our ratings are based on real graduate outcomes, industry requirements, and expert analysis - not just assumptions or outdated career guides.',
          makeInformedDecisionsTitle: 'Make Informed Decisions',
          makeInformedDecisionsDescription:
            'See exactly which majors give you the best shot at your dream career, or discover new career possibilities from your current major.',
        },
        relevanceLevelsModal: {
          title: 'Understanding Relevance Levels',
          subtitle:
            'Learn how we rate the connection between majors and career paths to help you make informed decisions.',
          highTitle: 'High Relevance',
          highDescription:
            'Graduates from this major often succeed in this career path. The knowledge and preparation line up closely, so choosing this major gives you a strong chance of entering the field.',
          mediumTitle: 'Medium Relevance',
          mediumDescription:
            'Graduates from this major sometimes succeed in this career path. The major provides useful skills, but most people will also need internships, graduate study, or extra training to break in.',
          lowTitle: 'Low Relevance',
          lowDescription:
            "Graduates from this major rarely succeed in this career path. It's not the usual route, and success usually requires significant additional steps or an unusual story.",
        },
      },
      majorsExplorerInstructionsModal: {
        slides: [
          {
            title: 'Start with Career Paths',
            description:
              'The best way to use this tool is to work backwards from career paths you may be interested in.',
          },
          {
            title: 'Which majors have flexibility?',
            description:
              'Select multiple career paths to see which majors give you flexibility to pursue different directions after graduation.',
          },
          {
            title: 'Play with Majors → Careers',
            description:
              'You can also start with a potential major(s) to discover which career paths are most accessible to you.',
          },
        ],
        skipButton: 'Skip',
        nextButton: 'Next',
        doneButton: 'Done',
      },
      majorToolsSection: {
        title: 'Major Tools',
        subtitle: 'Use these tools to explore all majors and map to career paths.',
      },
    },
    inputBox: {
      askLubav: 'Ask Lubav anything!',
      typeAMessage: 'Type a message...',
      send: 'Send',
    },
    mobileViewToggle: {
      lubav: 'Lubav',
      recs: 'Recs',
    },
    chatBox: {
      askLubav: 'Coach Lubav',
      copyErr: 'Failed to copy: ',
      commErr: 'Error communicating with AI.',
      blueprintRequired: (name: string) =>
        `Hi, ${name}! I noticed you haven't completed your Blueprint survey yet. [Complete the survey](/survey) so I can better understand your life aspirations and help guide you.`,
      clearSessionHeader: 'Start Over',
      clearSessionBody:
        'Are you sure you want to start over? This will permanently delete your session and all related content.',
      startOver: 'Start Over',
      newChat: {
        default: 'New Chat',
        mobile: 'New',
      },
      overloadErr:
        "We're experiencing exceptionally high demand at the moment. Please check back in a few minutes! In the meantime, check out Networks or Prosper.",
      fetchErr: 'Error fetching response: ',
      recallErr: "We couldn't load that session. Taking you back home.",
      attachmentOpenErr: "We couldn't open that file. Please try again.",
      attachmentBlockedErr: 'Allow pop-ups for this site to open attachments.',
      lubavThinking: 'Lubav is thinking',
      fileUpload: {
        submit: 'Submit',
        great: 'Great!',
        pasteDocumentLink: 'Or paste a document link (Google Doc, Drive, etc.)',
        uploadResume: (coachingUserName?: string) =>
          coachingUserName
            ? `Please upload ${coachingUserName}'s resume to continue!`
            : 'Please upload your resume to continue!',
        uploadTranscript: (coachingUserName?: string) =>
          coachingUserName
            ? `Please upload ${coachingUserName}'s transcript to continue!`
            : 'Please upload your transcript to continue!',
        uploadYouPersonalAssessment: (coachingUserName?: string) =>
          coachingUserName
            ? `Please upload ${coachingUserName}'s aptitude, personality, strengths, and/or career assessment to continue!`
            : 'Please upload your aptitude, personality, strengths, and/or career assessment to continue!',
        uploadOrConfirmResumeMultiple: (
          documents: { filename: string; url: string }[],
          coachingUserName?: string,
        ) => {
          const documentLinks = documents.map((doc) => `[${doc.filename}](${doc.url})`).join(', ');

          return coachingUserName
            ? `Looks like we already have ${coachingUserName}'s resume (${documentLinks}) on file, but if you'd like to upload a new one, please do so!`
            : `Looks like we already have your resume (${documentLinks}) on file, but if you'd like to upload a new one, please do so!`;
        },
        uploadOrConfirmTranscriptMultiple: (
          documents: { filename: string; url: string }[],
          coachingUserName?: string,
        ) => {
          const documentLinks = documents.map((doc) => `[${doc.filename}](${doc.url})`).join(', ');

          return coachingUserName
            ? `Looks like we already have ${coachingUserName}'s transcript (${documentLinks}) on file, but if you'd like to upload a new one, please do so!`
            : `Looks like we already have your transcript (${documentLinks}) on file, but if you'd like to upload a new one, please do so!`;
        },
        uploadOrConfirmPersonalAssessmentMultiple: (
          documents: { filename: string; url: string }[],
          coachingUserName?: string,
        ) => {
          const documentLinks = documents.map((doc) => `[${doc.filename}](${doc.url})`).join(', ');

          return coachingUserName
            ? `Looks like we already have a document for ${coachingUserName} (${documentLinks}) on file, but if you'd like to upload a new one, please do so!`
            : `Looks like we already have a document for you (${documentLinks}) on file, but if you'd like to upload a new one, please do so!`;
        },
        acceptedFileTypes: 'PDF, DOC, TXT, PNG, JPG',
        dragAndDrop: 'Drag and drop',
        uploading: 'Uploading...',
        uploadAFile: 'Upload a file',
        iveAttachedResume: 'I’ve just attached my resume to my profile for you.',
        uploadError:
          'I apologize, but there was an error processing your file. Please try again with a different file or format.',
        invalidLink: 'Please enter a valid URL',
        fileNameDisplay: (fileNames: string[]) => `File(s): **${fileNames.join(', ')}**`,
        fileContentForLLM: (fileNames: string[]) =>
          `File(s) ${fileNames.join(', ')} uploaded to profile.`,
        uploadUpdatedDocument: (documentType: string, coachingUserName?: string) =>
          coachingUserName
            ? `Great! Upload ${coachingUserName}'s updated ${documentType} to continue.`
            : `Great! Upload your updated ${documentType} to continue.`,
        uploadANewDocument: (documentType: string, coachingUserName?: string) =>
          coachingUserName
            ? `Upload a new ${documentType} for ${coachingUserName}`
            : `Upload a new ${documentType}`,
        iWantToUseMyCurrentDocument: (documentType: string, coachingUserName?: string) =>
          coachingUserName
            ? `Use ${coachingUserName}'s current ${documentType}`
            : `Use my current ${documentType}`,
        skipDocument: 'Skip',
      },
    },
    supercoach: {
      confirmSwitch: 'Are you sure you want to switch users? This will start a new session.',
      categories: {
        studentSnapshot: 'Student profile snapshot',
        coachingSessions: 'Coaching suggestions & follow-ups',
        deepDives: 'Solution sessions',
        emailDrafting: 'Email drafting',
      },
      selected: 'Selected',
    },
    rostersAdmin: {
      title: 'Cohorts',
      tabs: {
        operators: 'Operators',
        cohorts: 'Cohorts',
      },
      operators: {
        loading: 'Loading operators...',
        error: 'Could not load operators. Try again.',
        empty: 'No operators to show.',
        emptySearch: 'No operators match this search.',
        tableAria: 'Operators',
        searchLabel: 'Search operators',
        searchPlaceholder: 'Search operators and schools',
        openAria: (name: string) => `Open ${name}`,
        openChildAria: (child: string, operator: string) => `Open ${child} under ${operator}`,
        columns: {
          name: 'Operator',
          type: 'Type',
          agreements: 'Agreements',
          admins: 'Admins',
          rosters: 'Rosters',
          overCap: 'Over capacity',
          lapsingSoon: 'Lapsing soon',
          lapse: 'Lapsed',
          coverage: 'Seats',
        },
        coaches: (count: number) => `Coaches ${count}`,
        students: (count: number) => `Students ${count}`,
        coverageUnavailable: 'Capacity could not be read',
        overCap: 'Over cap',
        lapsingSoon: 'Lapsing soon',
        lapsingSoonHelp: "A roster's funding line ends within 60 days",
        lapsed: 'Lapsed',
        lapsedHelp: 'No funding line is inside the 30-day grace window',
        activeMarker: 'Current',
        homeMarker: 'Home',
        contextError: 'Your operator context could not be read.',
        matchedInstitutions: 'Matching schools',
      },
      columns: {
        actions: 'Actions',
      },
      actions: {
        rosterAria: (name: string) => `Actions for ${name}`,
        cohortAria: (name: string) => `Actions for ${name}`,
        createCode: 'Create code',
        creatingCode: 'Creating code...',
        createCohort: 'Create cohort',
        manageCoaches: 'Add coach to cohort',
        addMembers: 'Add users',
        removeMembers: 'Remove users',
        notCoachable: 'Coaches are assigned on student rosters only.',
        rename: 'Rename cohort',
        revoke: 'Revoke code',
      },
      columnsV2: {
        code: 'Active code',
        seats: 'Seats',
        program: 'Program',
        institution: 'Institution',
      },
      capacity: {
        atCapacity: 'At capacity',
        usage: (used: number, capacity: number | null) =>
          capacity === null
            ? used.toLocaleString()
            : `${used.toLocaleString()} / ${capacity.toLocaleString()}`,
      },
      seatSummary: {
        regionAria: 'Seats by program',
        contractLink: 'View contract',
        total: 'Total seats',
        percentUsed: (percent: number) => `${percent}%`,
        staffSeats: (seats: number) => `Staff: ${seats.toLocaleString()}`,
        percentUsedAria: (label: string, percent: number) => `${label}: ${percent}% of seats used`,
      },
      searchPlaceholder: 'Search',
      cohortSearchPlaceholder: 'Search cohorts',
      cohortSearchLabel: 'Search cohorts by name',
      rosterFilterLabel: 'Roster',
      rosterOptionLabel: (roster: string, institution: string) => `${roster} \u2014 ${institution}`,
      allRosters: 'All Rosters',
      allInstitutions: 'All Institutions',
      copy: {
        aria: (value: string) => `Copy code ${value}`,
        copied: 'Code copied',
        failed: 'Could not copy. Select the code and copy it by hand.',
      },
      codeState: {
        loading: 'Loading\u2026',
        unavailable: 'Codes unavailable',
      },
      createCohortDialog: {
        openButton: 'Create cohort',
        title: 'Create cohort',
        rosterLabel: 'Roster',
        rosterPlaceholder: 'Choose a roster',
        nameLabel: 'Cohort name',
        coachesLabel: 'Coaches',
        coachesPlaceholder: 'Search coaches',
        submit: 'Create cohort',
        submitting: 'Creating...',
        cancel: 'Cancel',
      },
      revokeDialog: {
        title: (name: string) => `Revoke the code for ${name}`,
        body: (value: string) =>
          `${value} stops working immediately. Creating a code again produces a new one \u2014 this one does not come back.`,
        submit: 'Revoke code',
        submitting: 'Revoking...',
        cancel: 'Cancel',
        failed: 'Could not revoke the code. Try again.',
        revoked: (value: string) => `Revoked ${value}. It no longer works.`,
        unreadable:
          'The code was revoked, but its details could not be read back. The line shows its current state.',
      },
      coachesColumn: {
        none: 'No coaches',
        list: (names: string) => `Coaches: ${names}`,
      },
      coachesDialog: {
        title: (cohort: string) => `Add coach to ${cohort}`,
        loading: 'Loading coaches...',
        error: 'Could not load this cohort. Try again.',
        gone: 'This cohort is no longer in the list.',
        current: 'Current coaches',
        empty: 'No coaches are assigned to this cohort yet.',
        addLabel: 'Add a coach',
        searchPlaceholder: 'Search coaches',
        noSearchResults: 'No coaches match your search',
        noneAvailable: 'No coaches available',
        searchUnavailable: 'Coaches could not be loaded, so none can be added right now.',
        add: 'Add coach',
        adding: 'Adding...',
        attached: (name: string, cohort: string) => `Added ${name} to ${cohort}.`,
        attachFailed: 'Could not add the coach. Try again.',
        removeAria: (name: string) => `Remove ${name}`,
        removeConfirm: (name: string, cohort: string) => `Remove ${name} from ${cohort}?`,
        remove: 'Remove',
        removing: 'Removing...',
        detached: (name: string, cohort: string) => `Removed ${name} from ${cohort}.`,
        detachFailed: 'Could not remove the coach. Try again.',
        cancel: 'Cancel',
        close: 'Close',
      },
      membersDialog: {
        addTitle: (cohort: string) => `Add users to ${cohort}`,
        removeTitle: (cohort: string) => `Remove users from ${cohort}`,
        searchPlaceholder: 'Search users',
        loading: 'Loading users...',
        error: 'Could not load users. Try again.',
        gone: 'This cohort is no longer in the list.',
        empty: 'Nobody is in this cohort yet.',
        emptyEligible: 'Everyone seated on this program is already in this cohort.',
        noSearchResults: 'Nobody matches your search',
        truncated: (shown: number, total: number) =>
          `Showing ${shown} of ${total}. Search to narrow the list.`,
        selected: (count: number) => `${count} selected`,
        addUsers: 'Add users',
        addSubmit: 'Add to cohort',
        adding: 'Adding...',
        addFailed: 'Could not add the users. Try again.',
        remove: 'Remove from cohort',
        removing: 'Removing...',
        removeFailed: 'Could not remove the users. Try again.',
        back: 'Back',
      },
      createCodeDialog: {
        title: (subject: string) => `Create code for ${subject}`,
        context: (roster: string) => `in ${roster}`,
        codeLabel: 'Code',
        codeHint: 'Enter 6 letters or numbers, or generate one.',
        generate: 'Generate random code',
        submit: 'Create code',
        submitting: 'Creating...',
        cancel: 'Cancel',
        failed: 'Could not create the code. Try again.',
      },
      renameDialog: {
        title: (cohort: string) => `Rename ${cohort}`,
        context: (roster: string) => `in ${roster}`,
        nameLabel: 'Cohort name',
        submit: 'Save name',
        submitting: 'Saving...',
        cancel: 'Cancel',
        failed: 'Could not rename the cohort. Try again.',
        renamed: (name: string) => `Renamed the cohort to ${name}.`,
        unreadable:
          'The cohort was renamed, but its details could not be read back. The row shows its current state.',
      },
      cohorts: {
        loading: 'Loading cohorts...',
        empty: 'No cohorts in this organization yet.',
        narrowedEmpty: 'No cohorts match the filters. Clear a filter to see more.',
        rostersUnavailable: 'Rosters could not be loaded, so cohorts are unavailable.',
      },
    },
    coachingAnalytics: {
      filters: {
        title: 'Filters',
        institution: 'Institution',
        programs: 'Programs',
        grade: 'Grade',
        enrollment: 'Enrollment',
        coaches: 'Coaches',
        all: 'All',
        allInstitutions: 'All institutions',
        platformOption: 'All institutions (platform)',
        unavailable: 'Filter options could not be loaded, so every section covers all students.',
        withCount: (label: string, count: number) => `${label} (${count.toLocaleString()})`,
        noEnrollmentType: 'No enrollment type provided',
        noGrade: 'No grade data provided',
        programNames: {
          foundations: 'Foundations',
          scholar: 'Scholar',
          student: 'Student',
        },
      },
      states: {
        loading: 'Loading…',
        error: 'This section could not be loaded.',
      },
      metrics: {
        students: 'Students',
        studentsInfo:
          'By Enrollment groups students into High School, Post-Secondary, Not currently a student, or Not recorded. By Program counts each program separately.',
        engagement: 'Engagement',
        engagementSubtitle: 'Active in last 30 days',
        engagementInfo: 'Students who have logged in within the last 30 days.',
        coaches: 'Coaches',
        coachesInfo:
          'Coaches at your institution assigned to at least one of your students. The Backrs Coaches count is the number of Backrs coaches assigned to at least one of your students.',
        backrsCoaches: (count: number) => `${count} Backrs Coaches`,
      },
      breakdown: {
        byEnrollment: 'By Enrollment',
        byProgram: 'By Program',
        highSchool: 'High School',
        postSecondary: 'Post-Secondary',
        notEnrolled: 'Not currently a student',
        unknown: 'Not recorded',
      },
      assessmentCoverage: {
        title: 'Assessment Coverage',
        info: "The share of students who've completed each assessment: Blueprint, Are You On Track?, and Life Readiness.",
        blueprint: 'Blueprint',
        areYouOnTrack: 'Are You On Track?',
        lifeReadiness: 'Life Readiness',
      },
      successSystem: {
        title: 'Backrs Success System',
        subtitle: 'Where students stand across all three assessments.',
        description:
          'The three assessments that make up the Backrs Success System: Blueprint, Are You On Track?, and Life Readiness.',
        discussWithLubav: 'Discuss with Lubav',
      },
      blueprint: {
        title: 'Blueprint',
        subtitle: 'What a student is aiming for — target career track and best-fit paths.',
        uniqueBlueprints: {
          title: 'Unique Blueprints',
          subtitle: (total: number) => `Out of ${total.toLocaleString()} students`,
          entrepreneurship: 'Entrepreneurship recommended',
          info: "Unique Blueprints is the number of students with a completed Blueprint, out of all students — one per student, so retakes don't add.",
          entrepreneurshipInfo:
            'Entrepreneurship recommended is the number of students whose Blueprint flagged entrepreneurship as a recommended path, out of completed Blueprints rather than all students.',
        },
        targetCareerTracks: {
          title: 'Target Career Tracks',
          subtitle: 'Best-fit fields across the cohort, by fit strength.',
          info: "The Target Career Track on each student's most recent Blueprint, out of students with a completed Blueprint.",
          topTier: 'Top-Tier Professional',
          upperTier: 'Upper-Tier Professional',
          middleTier: 'Middle-Tier Professional',
          tradesTechnical: 'Trades/Technical',
          semiSkilled: 'Semi-Skilled',
        },
        careerPathFit: {
          title: 'Career Path Fit',
          subtitle: (total: number) =>
            `Best-fit strength across career categories, out of ${total.toLocaleString()} students with a Blueprint.`,
          info: 'Each student is counted once per category, under the strongest fit they hold there — a student with both a strong and a low path in a category counts once, as strong. The bar splits those students into strong, medium, and low fit.',
          strong: 'Strong',
          medium: 'Medium',
          low: 'Low',
        },
      },
      onTrack: {
        title: 'Are You On Track?',
        subtitle: 'How students are tracking against their goals across the cohort.',
        legend: {
          onTrack: 'On Track',
          developing: 'Developing',
          notYet: 'Not Yet',
        },
        excluded: (count: number) => `${count.toLocaleString()} excluded — no Blueprint on file`,
        onTrackStatus: {
          title: 'Non-Academic Profile',
          subtitle: (total: number) => `Out of ${total.toLocaleString()} assessed students`,
        },
        statusByUnlock: {
          title: 'Status by Unlock',
          nonAcademicCategories:
            'Covers the non-academic categories — Achievement, Skills, and Communities.',
        },
        academicProfile: {
          title: 'Academic Profile',
          subtitle: 'Academic measures against the track each student can currently reach.',
          highSchool: 'High School',
          postSecondary: 'Post-Secondary',
          gpa: 'GPA',
          sat: 'SAT',
          act: 'ACT',
          trackReached: 'Track reached:',
          measure: 'Measure',
          levelTab: (label: string, count: number) => `${label} · ${count.toLocaleString()}`,
          sampleSize: (count: number) => `n = ${count.toLocaleString()} · bubble size = students`,
          bubble: (level: string, track: string, count: number, total: number, pct: number) =>
            `${level} · ${track} — ${count.toLocaleString()} of ${total.toLocaleString()} students at this level (${pct}%)`,
          collegeSelectivity: 'College Selectivity',
          noTrack: 'No track reached',
          binsTrimmed:
            "Bands cover the range where students actually fall. A band can hold students on different tracks — a student's track comes from all their academic measures together, not a single one alone.",
          enrollmentAtAssessment:
            'Students are grouped by their enrollment level at the time of the assessment (using their latest assessment), not their current enrollment.',
          noStudents: 'No students at this level in the current filter.',
          noScores: 'No scores on file.',
          noScore: (count: number) =>
            `${count.toLocaleString()} students have no score on file for this measure.`,
          unclassified: (count: number) =>
            `${count.toLocaleString()} assessed students have no high-school or post-secondary level recorded`,
          courseworkRigor: 'Coursework Rigor',
          majorRigor: 'Major Rigor',
          notOnFile: (count: number) => `${count.toLocaleString()} not on file`,
          courseworkRigorNote:
            'The GPA requirement table has no Very High rows: Very High is scored as High, so the top two bands are equivalent in the scoring.',
          majorRigorNote:
            'Major rigor is the summed rigor attributes of the student’s most rigorous declared major, banded at the thresholds the track scoring uses.',
          selectivityNote:
            'Selectivity is read from the college named on the assessment. Where that name matches no college on file the scoring treats the student as least selective, which lowers the track they can reach.',
        },
        aspiration: {
          title: 'Aspiration Vs Reality',
          subtitle: 'Academics vs. ambition — the gap no single report can show.',
          legend: {
            ahead: 'Ahead',
            aligned: 'Aligned',
            behind: 'Behind',
          },
          matrixTitle: 'Aspiration By Academic Track',
          matrixSubtitle:
            'Where each student’s aspiration sits against the track their academics reach.',
          matrixInfo:
            'Each cell counts students by their Blueprint Target Career Track (aspiration) against the track their academics currently reach. The highlighted diagonal is where the two agree; students above it are aiming beyond what their academics reach today, and below it are tracking beyond what they are aiming for.',
          matrixXAxis: 'Academic Track',
          matrixYAxis: 'Aspiration',
          caveat: "Aspiration is the Target Career Track on the student's most recent Blueprint.",
        },
      },
      lifeReadiness: {
        title: 'Life Readiness',
        subtitle: 'How students rate themselves across seven readiness categories',
        completion: {
          title: 'Life Readiness Completion',
          subtitle: (total: number) => `Out of ${total.toLocaleString()} students`,
          completed: 'Completed',
          inProgress: 'In Progress',
          notStarted: 'Not Started',
          caveat: (visibleQuestions: number) =>
            `Completion means answering all ${visibleQuestions.toLocaleString()} questions currently in the survey. If questions are added or removed, every student's progress shifts.`,
        },
        byCategory: {
          title: 'Self-rated Readiness by Category',
          subtitle: (started: number) =>
            `Answers from the ${started.toLocaleString()} students who have started.`,
          coverage: (answered: number, answerable: number) =>
            `${answered.toLocaleString()} of ${answerable.toLocaleString()} answers`,
          caveat:
            "Only each student's latest answer to a question counts. Unanswered questions are left out rather than counted as Not yet, so coverage shows how much of each category has been answered.",
        },
      },
      engagement: {
        title: 'Engagement',
        subtitle: 'Where student activity is concentrated and how it is trending.',
        description:
          'How deeply students are showing up across posts, comments, networks and tools.',
        discussWithLubav: 'Discuss with Lubav',
        recency: {
          title: 'Recently Active Students',
          subtitle: 'How recently students logged in.',
          info: 'Each student falls in exactly one band, based on their most recent login.',
          last30: 'Last 30 days',
          days31to60: '31–60 days',
          days61to90: '61–90 days',
          over90: 'Over 90 days',
        },
        activity: {
          title: 'Posts, Goals & Comments',
          subtitle: (months: number) =>
            `Student activity over the last ${months} ${months === 1 ? 'month' : 'months'}.`,
          info: 'Total number of posts, goals, & comments per calendar month.',
          month: (month: string, posts: number, goals: number, comments: number) =>
            `${month} — Posts ${posts.toLocaleString()} · Goals ${goals.toLocaleString()} · Comments ${comments.toLocaleString()}`,
          studentPosts: 'Posts',
          studentGoals: 'Goals',
          studentComments: 'Comments',
        },
        networkMembership: {
          title: 'Network Membership',
          subtitle: 'Ranked by members of this cohort.',
          info: (limit: number) =>
            `Each network counts the students who belong to it, so a student in several networks appears under each. Only the ${limit} largest networks are shown.`,
          empty: 'No students in this cohort belong to a network.',
          otherType: 'Other',
          members: (name: string, count: number) =>
            `${name} - ${count.toLocaleString()} ${count === 1 ? 'member' : 'members'}`,
        },
      },
      toolUsage: {
        title: 'Tool Usage',
        subtitle: 'Reach — distinct students who used each tool.',
        description:
          'Which Lubav tools students in this cohort have actually used, by distinct students and by sessions.',
        discussWithLubav: 'Discuss with Lubav',
        students: (count: number, total: number) =>
          `${count.toLocaleString()} of ${total.toLocaleString()} students used a tool`,
        empty: 'No tool activity for these students yet.',
        uniqueStudents: 'Unique students',
        totalSessions: 'Total sessions',
        info: 'Unique students counts total number of individual students who have used a tool at least once. Total sessions counts every time a student has used a tool.',
        usage: (tool: string, measure: string, count: number) =>
          `${tool} — ${measure}: ${count.toLocaleString()}`,
        tools: {
          essay_ideas: 'Essay ideas',
          majors: 'Majors for you',
          what_are_your_chances: 'What are your chances?',
          boost_your_profile: 'Strengthen your profile',
          boost_odds_of_an_offer: 'Boost odds of an offer',
          resume_optimizer: 'Resume optimizer',
          lower_cost_of_college: 'Lower the cost of college',
          jobs_you_might_love: 'Jobs for you',
          college_shortlist: 'College shortlist',
          improve_your_test_scores: 'Improve your SAT/ACT score',
          resume_builder: 'Resume builder',
        },
      },
      coaching: {
        title: 'Coaching',
        subtitle: 'How the caseload is spread across the coaching team.',
        description: 'How students are distributed across Backrs and institutional coaches.',
        discussWithLubav: 'Discuss with Lubav',
        load: {
          title: 'Coaching load',
          subtitle: 'Students in this cohort per coach.',
          institutional: 'Institutional',
          backrs: 'Backrs',
          avgStudentsPerCoach: 'Average students per coach',
          studentsWithCoach: (type: string) => `Students with ${type} coach`,
          institutionalCoach: 'an institutional',
          backrsCoach: 'a Backrs',
          empty: 'No coaches of this type have a student in this cohort.',
          info: 'Only coaches with at least one of your students are shown. A student with both a Backrs and an institutional coach appears under each.',
        },
      },
    },
    coachingDashboard: {
      coachNotes: {
        createButton: 'Create note',
        searchPlaceholder: 'Search notes',
        searchEmptyState: 'No notes match your search.',
        emptyState: 'No notes yet. Create your first note for this assignee.',
        loadError: "Couldn't load notes. Please refresh and try again.",
        saveError: 'Failed to save note. Please try again.',
        deleteError: 'Failed to delete note. Please try again.',
        emptyPreview: '(empty note)',
        editTooltip: 'Edit',
        deleteTooltip: 'Delete',
        deleteDialogHeader: 'Delete note',
        deleteDialogBody:
          'Are you sure you want to delete this note? This action cannot be undone.',
        editorSubtitle: 'This is only visible to you, not your student.',
        editorPlaceholder: 'Write your note…',
        editorTitleCreate: 'New note',
        editorTitleEdit: 'Edit note',
        editorDone: 'Done',
        editorMinimizeLabel: 'Minimize',
        editorCloseLabel: 'Close',
        minimizedCloseLabel: 'Close note',
        lastModifiedLabel: 'Last modified',
        editorUseTemplate: 'Use template',
        editorTemplateMenuLabel: 'Choose a template',
        imageTypeError: 'Only JPG and PNG images are supported.',
        attachNonImageError: 'Use the image button to add photos.',
        uploadError: 'Upload failed. Please try again.',
        fileTooLargeError: 'File is too large. Attachments must be 10 MB or smaller.',
        imageNotReadyError: 'An image is still uploading. Please wait and try again.',
        attachmentSlotsHint: (used: number, max: number) => `${used} of ${max} attachments used`,
      },
      coachesTabTitle: 'Coaches',
      studentsTabTitle: 'Students',
      programUnknown: 'Not available',
      noInstitution: '—',
      backrsCoach: 'Backrs Coach',
      unnamedPerson: 'Unnamed',
      loadError: 'Something went wrong loading the roster. Refresh to try again.',
      loadingRoster: 'Loading roster…',
      columns: {
        name: 'Name',
        coach: 'Coach',
        program: 'Program',
        grade: 'Grade',
        school: 'School',
        institution: 'Institution',
        assessments: 'Assessments',
        lastLogin: 'Last login',
        studentCount: 'Student count',
      },
      assessmentsCell: {
        blueprint: 'Blueprint',
        trackAssessment: 'Are You On Track?',
        lifeReadiness: 'Life Readiness',
        infoTitle: 'Assessments',
        blueprintNote: 'Complete once the student has at least one finished career-path result.',
        trackAssessmentNote:
          "Based on the student's latest scored result; some enrollment types (e.g. graduate school) are ineligible.",
        lifeReadinessNote:
          "Percent of the currently visible questions answered. The question set changes over time, so this isn't a fixed score.",
        summary: (completed: number, total: number) => `${completed}/${total} complete`,
      },
      coachAssignment: (name: string) => `Select coaches to assign to ${name}`,
      coachAssignmentCancel: 'Cancel',
      coachAssignmentSave: 'Save',
      coachAssignmentSaving: 'Saving',
      detailsTitle: 'Student Details',
      assessmentNotComplete: 'Not Complete',
      emulateLabel: 'Emulate',
      emulateConfirmation: (name: string) =>
        `Are you sure you want to emulate ${name}? You will be logged in as this user.`,
      gradeDisplay: (grade: string) => `Grade ${grade}`,
      lubavTabTitle: 'Lubav',
      lubavWelcome: 'Hi. How can I help?',
      mainTitle: 'Coaching Dashboard',
      verifiedBadge: 'Verified',
      bulkAssignDescription: (count: number) =>
        `Select coaches to assign to ${count} ${count === 1 ? 'member' : 'members'}.`,
      bulkAssignSelectedCount: (count: number) => `${count} selected`,
      bulkAssignClearSelection: 'Clear selection',
      bulkAssignError: 'Failed to assign coaches to one or more members. Please try again.',
      bulkAssignSuccess: 'Coaches assigned successfully.',
      bulkAssignPartialSuccess: (newCount: number, skipped: string) =>
        `Assigned to ${newCount} ${newCount === 1 ? 'member' : 'members'}. Skipped: ${skipped}.`,
      bulkAssignNoop: 'Those coaches are already assigned to the selected members.',
      bulkAssignAllSkipped: (skipped: string) =>
        `Couldn't assign any coaches. Skipped: ${skipped}.`,
      editCoachesAddFailed: 'Failed to add new coaches. Please try again.',
      editCoachesRemoveFailed: 'Failed to remove coaches. Please try again.',
      bulkAssignSearchPlaceholder: 'Search coaches',
      bulkAssignNoSearchResults: 'No coaches match your search',
      adminMainTitle: 'Users',
      manageCoaches: 'Manage Coaches',
      viewAllCoaches: 'View all',
      noCoachesAssigned: 'No coaches assigned',
      noCoachesAvailable: 'No coaches available',
      profileTabTitle: 'Profile',
      searchPlaceholder: 'Search',
      noSessions: 'No past sessions found',
      failedToLoadSessions: 'Failed to load sessions. Please try again.',
      failedToLoadMember: 'Failed to load student details. Please try again.',
      memberAccessRefused: "You don't have access to this student's details.",
      memberRefusedInOrganization:
        "You can't view this student's details in the organization you're working in.",
      noTranscript: 'No transcript',
      noRecording: 'No recording',
      processing: 'Processing',
      transcriptTitle: 'Transcript',
      transcriptLoading: 'Loading transcript...',
      transcriptError: 'Unable to load transcript',
      backToAllSessions: 'Back to all sessions',
      solutionSessionsTitle: 'Solution Sessions',
      failedToLoadSolutionSessions: 'Failed to load solution sessions. Please try again.',
      solutionSessionOutputError: 'Unable to load session output',
      solutionSessionOutputEmpty: 'This session finished without a saved output',
      blueprintError: "Unable to load this student's Blueprint",
      careerPathsTitle: 'Career Paths',
      backToBlueprint: 'Back to Blueprint',
      backToCareerPaths: 'Back to Career Paths',
      trackAssessmentError: "Unable to load this student's Are You On Track result",
      lifeReadinessError: "Unable to load this student's Life Readiness",
      view: 'View',
      copiedToClipboard: 'Copied to clipboard',
      seatActions: {
        menuAria: (name: string) => `Seat actions for ${name}`,
        assignCoaches: 'Assign coaches',
        move: 'Change seat',
        revoke: 'Revoke seat',
        addToCohort: 'Add to cohort',
        removeFromCohort: 'Remove from cohort',
        bulkMove: 'Change seat',
        withheld: {
          noSeat: 'They hold no seat on a roster you manage.',
          seatsUnknown: 'Their seats could not be read, so no seat action can be offered.',
          bulkSeatsUnknown:
            'Some of the selection\u2019s seats could not be read, so no seat action can be offered.',
          noMovableSeat: 'Only an active seat on a student roster can move.',
          noStudentSeat: 'They hold no live student seat to end.',
          noCoachSeat: 'They hold no live coach seat to end.',
          noCohortSeat: 'Cohorts are on student rosters only.',
          cohortSeatsSpanRosters: (rosters: string) =>
            `They hold seats on ${rosters}, and a cohort belongs to one roster.`,
          overBatchLimit: (limit: number) =>
            `Select ${limit} students or fewer \u2014 a larger batch is refused whole.`,
          bulkNoMovableSeat: 'No one selected holds an active student seat on a roster you manage.',
          bulkNoCohortSeat:
            'Cohorts are on student rosters only, and no one selected holds a seat on one you manage.',
        },
        seatPicker: {
          label: 'Which seat?',
          // A roster carries the offering's name, which repeats across operators, so a
          // seat set spanning roots names the operator alongside it.
          option: (roster: string) => roster,
          scopedOption: (roster: string, operator: string) => `${roster} \u2014 ${operator}`,
          unnamedRoster: (rosterId: number) => `Roster ${rosterId}`,
          unnamedOperator: (operatorId: number) => `Organization ${operatorId}`,
          unknownOperator: 'Unknown organization',
        },
        linePicker: {
          label: 'Draw the seat from',
          placeholder: 'Choose an allocation line',
          unnamedLine: (grantId: number) => `Line ${grantId}`,
        },
        destinationPicker: {
          label: 'Move to',
          loading: 'Loading rosters\u2026',
          error: 'Rosters could not be loaded, so none can be chosen.',
          empty: 'There is no other roster you manage that this seat can move to.',
          noLine: 'no allocation line to draw a seat from',
          withheldOption: (destination: string, reason: string) => `${destination} (${reason})`,
          unasked: 'No seat here can move, so there is nothing to move it to.',
        },
        consequence: {
          loading: 'Reading their cohorts on this roster\u2026',
          coachLoading: 'Reading the cohorts they coach\u2026',
          cohorts: (cohorts: string) => `This removes them from ${cohorts}.`,
          membershipsKept: 'Network and group memberships are kept.',
          membershipsKeptEitherWay: 'Network and group memberships are kept either way.',
          coachCohorts: (cohorts: string) => `This removes them as coach from ${cohorts}.`,
          coachNone: 'They coach no cohort.',
          // A read that could not resolve says so in its own words. Standing in the
          // wording for a seat in no cohort would tell an operator nothing is lost
          // when something may be.
          unknown:
            'Their cohorts on this roster could not be read, so what this removes them from is not shown.',
          coachUnknown:
            'The cohorts they coach could not be read, so what this removes them from is not shown.',
        },
        moveDialog: {
          title: (name: string) => `Move ${name} to another roster`,
          bulkTitle: (count: number) => `Move ${count} students to another roster`,
          submit: 'Change seat',
          bulkSubmit: 'Change seats',
          submitting: 'Moving\u2026',
          moved: (name: string, roster: string) => `Moved ${name} to ${roster}.`,
          movedOverCap: (name: string, roster: string) =>
            `Moved ${name} to ${roster}. That roster is now over its seat cap.`,
          failed: 'Could not move the seat. Try again.',
        },
        revokeDialog: {
          title: (name: string) => `End ${name}\u2019s seat`,
          submit: 'End seat',
          submitting: 'Ending\u2026',
          revoked: (name: string) => `Ended ${name}\u2019s seat.`,
          failed: 'Could not end the seat. Try again.',
        },
        cohortPalette: {
          addTitle: (name: string) => `Add ${name} to a cohort`,
          removeTitle: (name: string) => `Remove ${name} from a cohort`,
          searchPlaceholder: 'Search cohorts',
          loading: 'Loading cohorts\u2026',
          error: 'Cohorts could not be loaded, so none can be chosen.',
          addEmpty: 'They are already in every cohort on this roster.',
          removeEmpty: 'They are in no cohort on this roster.',
          added: (name: string, cohort: string) => `Added ${name} to ${cohort}.`,
          removed: (name: string, cohort: string) => `Removed ${name} from ${cohort}.`,
          addFailed: 'Could not add them to the cohort. Try again.',
          removeFailed: 'Could not remove them from the cohort. Try again.',
        },
        cohortOutcome: {
          added: (count: number, cohort: string) =>
            `Added ${count} ${count === 1 ? 'person' : 'people'} to ${cohort}.`,
          removed: (count: number, cohort: string) =>
            `Removed ${count} ${count === 1 ? 'person' : 'people'} from ${cohort}.`,
          partiallyAdded: (landed: number, total: number, cohort: string, refused: string) =>
            `Added ${landed} of ${total} people to ${cohort}. Not added: ${refused}.`,
          partiallyRemoved: (landed: number, total: number, cohort: string, refused: string) =>
            `Removed ${landed} of ${total} people from ${cohort}. Not removed: ${refused}.`,
          noneChanged: (name: string, reason: string) => `Nothing changed. ${name}: ${reason}.`,
          noneChangedMany: (count: number, refused: string) =>
            `Nothing changed for ${count} ${count === 1 ? 'person' : 'people'}: ${refused}.`,
          person: (name: string, reason: string) => `${name} (${reason})`,
          andMore: (count: number) => `and ${count} more`,
          nothingReported: 'The batch came back with nothing to say about anyone.',
        },
        report: {
          heading: 'What happened',
          summary: (landed: number, total: number) =>
            `${landed} of ${total} ${total === 1 ? 'student' : 'students'} succeeded.`,
          cohortsRemoved: (count: number) =>
            `${count} ${count === 1 ? 'membership' : 'memberships'} removed from cohorts.`,
          added: 'Added',
          removed: 'Removed',
          moved: 'Moved',
          seat_not_on_cohort_roster: 'Skipped \u2014 not on that cohort\u2019s roster',
          membership_exists: 'Skipped \u2014 already in this cohort',
          membership_not_found: 'Skipped \u2014 not in this cohort',
          seat_not_active: 'Skipped \u2014 their seat is not active',
          roster_capacity_exhausted: 'Refused \u2014 that roster is full',
          privileged_kind_move: 'Refused \u2014 only students move between rosters',
          user_already_has_live_seat: 'Refused \u2014 they already hold a seat there',
          destination_is_origin: 'Refused \u2014 they are already on that roster',
          roster_seat_not_active: 'Refused \u2014 their seat is not active',
          roster_seat_not_found: 'Refused \u2014 that seat is no longer there',
          // Neither of these is a refusal, and neither should read as one: the first is a
          // fault with no reason to give, the second is a seat the batch never got to.
          move_failed: 'Did not move \u2014 the reason is not available',
          not_attempted: 'Not attempted \u2014 the batch stopped before this one',
          roster_seat_not_live: 'Refused \u2014 their seat has already ended',
          pending_invite_exists:
            'Refused \u2014 that roster is holding an unclaimed invitation for them',
          overCap: 'That roster is now over its seat cap.',
          otherLanded: (count: number) =>
            count === 1 ? '(their other seat succeeded)' : '(their other seats succeeded)',
          noEligibleSeat: 'Skipped \u2014 no seat this could act on',
          // A word this client does not know is shown as it arrived rather than folded
          // into a familiar one, which would report the wrong reason with confidence.
          unknown: (outcome: string) =>
            `Reported as \u201c${outcome}\u201d \u2014 this client does not know that outcome`,
        },
        landedUnreadable: {
          revoke: (name: string) =>
            `Ended ${name}\u2019s seat. The details could not be read back \u2014 refresh to see the roster as it now is.`,
          move: (name: string, roster: string) =>
            `Moved ${name} to ${roster}. The details could not be read back \u2014 refresh to see the roster as it now is.`,
          batch:
            'The change went through, but what happened to each person could not be read back. Refresh to see the roster as it now is.',
        },
        cancel: 'Cancel',
        close: 'Close',
      },
      filters: {
        grade: 'Grade',
        school: 'School',
        role: 'Role',
        program: 'Program',
        institutionRole: 'Institution Role',
        cohort: 'Cohort',
        coaches: 'Coaches',
        selectedCount: (count: number) => `${count} selected`,
        moreFilters: 'More filters',
        verified: 'Verified',
        verifiedOnly: 'Verified',
        notVerified: 'Not verified',
        all: 'All',
        clear: 'Clear',
      },
      institutionAdmin: {
        sections: {
          people: 'People',
          rosters: 'Rosters',
          cohorts: 'Cohorts',
          codes: 'Codes',
          groups: 'Groups',
        },
        inScopeFilterLabel: 'Institution',
        unnamed: (id: number) => `Organization ${id}`,
        unnamedOffering: (id: number) => `Offering ${id}`,
        unnamedCohort: (id: number) => `Cohort ${id}`,
        unnamedCoach: (id: number) => `Coach ${id}`,
        missingValue: 'Not available',
        mintLineAmbiguous:
          'This roster has more than one line that can issue codes, so none was chosen. Retrying will not help until a line is chosen for it.',
        codeNeedsMintLine:
          'This roster cannot issue codes, so a code cannot be chosen for the cohort. Clear the code to create it without one.',
        incompleteList: (dropped: number) =>
          `${dropped} ${dropped === 1 ? 'row' : 'rows'} could not be read and ${dropped === 1 ? 'is' : 'are'} not shown.`,
        filtersUnavailable: 'Institution filter unavailable right now.',
        workingIn: 'Working in',
        switcher: {
          ariaLabel: 'Change the organization you are working in',
          unavailableOrganization: 'Organization unavailable',
        },
        operatingAs: {
          label: (operator: string) => `Operating as ${operator}`,
          labelUnnamed: 'Operating as another operator',
          exit: 'Exit',
        },
        picker: {
          title: 'Choose an organization',
          body: 'You administer more than one organization. Pick the one to work in.',
        },
        rosters: {
          title: 'Rosters',
          loading: 'Loading rosters...',
          error: 'Could not load rosters. Try again.',
          empty: 'No rosters in this organization yet.',
          columns: {
            roster: 'Roster',
            institution: 'Managed by',
            offering: 'Offering',
            capacity: 'Seats',
            sourceLines: 'Funding lines',
          },
          uncapped: 'Uncapped',
          overCap: 'Over cap',
          capMode: {
            hard: 'Hard cap',
            soft: 'Soft cap',
            none: 'No cap',
          },
          seats: (used: number, capacity: number | null) =>
            capacity === null ? `${used} used` : `${used} of ${capacity}`,
        },
        cohorts: {
          title: 'Cohorts',
          loading: 'Loading cohorts...',
          error: 'Could not load cohorts. Try again.',
          empty: 'This roster has no cohorts yet.',
          rostersUnavailable: 'Rosters could not be loaded, so cohorts are unavailable.',
          columns: {
            cohort: 'Cohort',
            members: 'Members',
            coaches: 'Coaches',
          },
          create: {
            title: 'Create cohort',
            nameLabel: 'Cohort name',
            submit: 'Create cohort',
            submitting: 'Creating...',
            error: 'Could not create the cohort. Try again.',
            rostersUnavailable: 'Rosters could not be loaded, so a cohort cannot be created yet.',
            created: (name: string, shareCode: string) =>
              `Created ${name}. Its share code is ${shareCode}.`,
            createdWithoutShareCode: (name: string) => `Created ${name}. It has no share code yet.`,
            unreadable:
              'The cohort was created, but its details could not be read back. It is in the list below.',
          },
        },
        codes: {
          title: 'Codes',
          loading: 'Loading codes...',
          error: 'Could not load codes. Try again.',
          empty: 'This roster has no codes yet.',
          noRoster: 'Choose a roster in the Rosters section to see its codes.',
          rostersUnavailable: 'Rosters could not be loaded, so codes are unavailable.',
          columns: {
            code: 'Code',
            status: 'Status',
            cohort: 'Cohort',
            reserved: 'Reserved',
          },
          noCohort: 'Roster-wide',
          reserved: 'Reserved',
          reservedWithEmail: 'Reserved for an invited address',
          shared: 'Shared',
          mint: {
            title: 'Mint a join code',
            submit: 'Mint code',
            submitting: 'Minting...',
            error: 'Could not mint a code. Try again.',
            noRoster: 'Choose a roster in the Rosters section before minting a code.',
            rostersUnavailable: 'Rosters could not be loaded, so a code cannot be minted yet.',
            minted: (value: string) => `Minted ${value}.`,
            taken: 'That code is already in use. Choose a different one.',
            recycled: (value: string) => `This cohort already has an active code: ${value}.`,
            recycledRosterWide: (value: string) =>
              `This roster already has an active code: ${value}.`,
            unreadable:
              'The code was minted, but its details could not be read back. It is in the list below.',
          },
          cohortPin: {
            label: 'Pin to a cohort',
            unpinned: 'Roster-wide (no cohort)',
            loading: 'Loading cohorts...',
            error: 'Cohorts could not be loaded, so a code cannot be minted yet.',
            empty: 'This roster has no cohorts, so codes mint roster-wide.',
            noRoster: 'Choose a roster to see the cohorts a code can pin to.',
            rostersUnavailable: 'Rosters could not be loaded, so no cohort can be pinned.',
          },
        },
      },
      reports: {
        title: 'Reports',
        modalTitle: 'Select a report to download',
        subtitle: 'Export your data as a CSV.',
        cancel: 'Cancel',
        download: 'Download',
        preparing: 'Preparing…',
        zeroRows: 'No rows to export for this report.',
        retryError: "Couldn't export the report. Try again.",
        tryAgain: 'Try again',
        items: {
          studentSummary: {
            label: 'Student Summary',
            description: "Your students' profile and academic data",
          },
          networkMembership: {
            label: 'Network Membership',
            description: 'Which networks your students are part of',
          },
          coachMatches: {
            label: 'Coach Matches',
            description: 'Who is coaching your students',
          },
          bookmarks: {
            label: 'Bookmarks',
            description: 'The opportunities and content your students have saved',
          },
          postsComments: {
            label: 'Posts & Comments',
            description: "Your students' activity across the community",
          },
          blueprint: {
            label: 'Blueprint',
            description: "Each student's target career and ranked career-path fit",
          },
          ayot: {
            label: 'Are You On Track?',
            description: "Every scored attempt, with each student's level per dimension",
          },
          lifeReadiness: {
            label: 'Life Readiness',
            description: "Your students' self-ratings on the competencies they answered",
          },
        },
      },
    },
    guidanceCounselor: {
      prompts: {
        collegeWishlist: 'Build a college wishlist for my student',
        careerOpportunities: 'Help my student explore career opportunities',
        letterOfRecommendation: 'Write me a letter of recommendation',
        classRecommendations: 'Help me recommend classes for my student',
        scholarshipSearch: 'Find me scholarships for my students',
        resumeFeedback: "Give feedback on my student's resume",
        personalStatementImprovement: "Help me improve my student's personal statement",
      },
    },
    defaultSuggestedPrompts: {
      recommendColleges: 'Recommend colleges based on my academic profile',
      exploreCareerOpportunities: 'Help me explore career opportunities',
      topUniversityChances: 'What are my chances of getting into a top university in my field',
      findScholarship: 'Find me a scholarship based on my qualifications',
      studyTips: 'Give me tips to study better',
    },
    header: {
      askLubav: 'Lubav',
      solutionSession: 'Solution Sessions',
      modelTitle: 'Model',
      modelMap: {
        smartest: 'Smartest',
        fastest: 'Fastest',
        thinking: 'Thinking (o1)',
        flagship: 'Flagship',
      },

      language: 'Language',
      english: 'English',
      spanish: 'Spanish',
      french: 'French',
      totalCost: 'Total Cost',
      inputTokens: 'Input Tokens',
      outputTokens: 'Output Tokens',
    },
    infoBoxFormatters: {
      youCouldAsk: 'You could ask: ',
    },
    officeHours: {
      categories: {
        current: 'Current',
        member_profile: 'Student',
        member_sessions: 'Past Meetings',
        member_solution_session: 'Solution Session',
        member_blueprint: 'Blueprint',
        member_ayot: 'Are You On Track?',
        member_life_readiness: 'Life Readiness',
        member_notes: 'Notes',
        featured: 'Featured',
        suggested: 'Suggested',
        tools: 'Tools',
        majors: 'Majors',
        stories: 'Stories',
        skills: 'Skills',
        connections: 'Connections',
        opportunities: 'Opportunities',
        resources: 'Resources',
        profiles: 'Profiles',
        learning: 'Learning',
        career_paths: 'Career Paths',
        internships: 'Positions',
        scholarships: 'Scholarships',
        employers: 'Employers',
        colleges: 'Universities',
        industries: 'Industries',
        coaches: 'Coaching',
        deep_dives: 'Deep Dives',
        career_path_selector: 'Career Path',
        college_type_selector: 'College Type',
      },
      sessionSwitch: {
        fallbackTitle: 'this session',
        prompt: {
          none: (title: string) => `Want to start a ${title}? This tool will start a new thread.`,
          mid: (title: string) =>
            `You have a ${title} session in progress. Want to pick up where you left off? This will start a new thread.`,
          complete: (title: string) =>
            `You've already done a ${title} session. Want to revisit it or start a new one? This will start a new thread.`,
        },
        chatActions: {
          start: (title: string) => `Start ${title}`,
          continue: (title: string) => `Continue ${title}`,
          restart: (title: string) => `Start ${title} over`,
          discuss: 'Open my most recent session',
        },
        leaveModal: {
          header: (title: string) => `Leave ${title}?`,
          body: 'Your progress is saved, so you can pick this session back up anytime.',
          leave: 'Leave',
          cancel: 'Cancel',
        },
      },
      infoBox: {
        heading: 'Personalized Recommendations',
        refresh: 'Refresh',
        loadingMessages: [
          'Gathering resources...',
          'Finding opportunities...',
          'Following the money...',
          'Checking under the floorboards...',
          'Rummaging through the couch cushions...',
          'Peeking behind the curtains...',
        ],
        studyPlan: {
          priorities: 'Priorities',
          goals: 'Goals',
          week: 'Week',
          daysOfWeek: {
            monday: 'Monday',
            tuesday: 'Tuesday',
            wednesday: 'Wednesday',
            thursday: 'Thursday',
            friday: 'Friday',
            saturday: 'Saturday',
            sunday: 'Sunday',
          },
        },
        collegeShortlist: {
          safety: 'Safety',
          target: 'Target',
          reach: 'Reach',
          safetySchools: 'Safety schools',
          targetSchools: 'Target schools',
          reachSchools: 'Reach schools',
          highlights: 'Highlights:',
          considerations: 'Considerations:',
        },
      },
      supercoachDropdown: {
        findAStudent: 'Find a student',
        selectStudent: 'Select student',
        searchPlaceholder: 'Search students...',
      },
      tabs: {
        resources: 'Resources',
        profiles: 'Profiles',
        posts: 'Posts',
        competencies: 'Competencies',
        scholarships: 'Scholarships',
        career: 'Career',
        professionals: 'Professionals',
        students: 'Students',
        providers: 'Providers',
        community: 'Community',
      },
      loadingMessages: [
        'Getting resources...',
        'Looking for relevant contacts...',
        'Finding useful posts...',
        'Fetching interesting videos...',
        'Getting resources...',
      ],
    },
    solutionSessions: {
      questions: {
        requiredFieldError: 'This field is required',
        skipTooltip: 'This question is required. Please provide your best answer!',
        skip: 'Skip',
        submit: 'Submit',
        skipThisQuestion: 'Skip this question.',
        press: 'Press',
        enter: 'Enter ⏎',
        userSearch: {
          searchUsers: 'Search Users',
          searchUsersPlaceholder: 'Type to search for users by name',
          noUsersFound: 'No users found',
          loadFailed: 'Error loading users',
        },
        education: {
          fields: {
            institutionName: {
              label: 'Name',
            },
            institutionCity: {
              label: 'City',
              placeholder: 'New York',
            },
            institutionState: {
              label: 'State',
              placeholder: 'NY',
            },
            academicDegrees: {
              label: 'Current Degree',
            },
            academicMajors: {
              label: 'Majors',
            },
            didGraduate: {
              label: 'Did you graduate?',
            },
            graduationDate: {
              labelGraduated: 'Graduation Date',
              labelExpectedGraduation: 'Expected Graduation Date',
              placeholder: 'MM/YYYY',
            },
            highSchoolGpa: {
              label: 'GPA',
            },
            majorGpa: {
              label: 'Major GPA',
            },
            awardsAccomplishments: {
              label: 'Academic Awards/Accomplishments',
              placeholder:
                'Dean’s List for 4 consecutive semesters (GPA 3.8/4.0) and recipient of the University Merit Scholarship.',
            },
          },
        },
        workExperience: {
          fields: {
            employerName: {
              label: 'Employer Name',
            },
            jobTitle: {
              label: 'Job Title',
              placeholder: 'Accounting Intern',
            },
            employerCity: {
              label: 'City',
              placeholder: 'New York',
            },
            employerState: {
              label: 'State',
              placeholder: 'NY',
            },
            startDate: {
              label: 'Start Date',
              placeholder: 'MM/YYYY',
            },
            endDate: {
              label: 'End Date',
              placeholder: 'MM/YYYY',
              helperText: 'Leave blank if you currently work here',
            },
            achievements: {
              label: 'Achievements',
              placeholder:
                'Increased social media engagement by 40% in 6 months by launching a new content calendar and managing a 4-person team.',
            },
          },
        },
        engagement: {
          fields: {
            organizationName: {
              label: 'Organization Name',
              placeholder: 'Student Government',
            },
            role: {
              label: 'Role',
              placeholder: 'Treasurer',
            },
            city: {
              label: 'City',
              placeholder: 'New York',
            },
            state: {
              label: 'State',
              placeholder: 'NY',
            },
            startDate: {
              label: 'Start Date',
              placeholder: 'MM/YYYY',
            },
            endDate: {
              label: 'End Date',
              placeholder: 'MM/YYYY',
              helperText: 'Leave blank if this is ongoing',
            },
            achievements: {
              label: 'Achievements',
              placeholder:
                'Organized a community fundraiser that raised $5,000 for local shelters and recruited 30 volunteers to help run the event.',
            },
          },
        },
      },
      selectionBox: {
        coming: 'coming',
        soon: 'soon!',
      },
      solutionBox: {
        refresh: 'Refresh',
        stop: 'Stop',
      },
      resumeBuilderSolutionBox: {
        education: 'Education',
        workExperience: 'Work Experience',
        leadership: 'Achievement',
        skills: 'Skills',
        heading: 'Your Resume',
        viewResume: 'View Resume',
      },
    },
    login: {
      title: 'Log in to your account',
      userNotFound: 'Error getting user information, please try again.',
      skip: "I don't have an account",
    },

    dashboard: {
      prosperPageName: 'prosper',
      header:
        'Welcome! Prosper helps you build the competencies you need to succeed in school, work, and life.',
    },
    search: {
      title: 'Search Backrs',
      searchPlaceholder: 'Search',
      searchPageLabels: {
        [SEARCH_PAGE_CATEGORIES.DEFAULT]: 'Search Backrs',
        [SEARCH_PAGE_CATEGORIES.SCHOLARSHIPS]: 'Search Scholarships',
        [SEARCH_PAGE_CATEGORIES.INTERNSHIPS]: 'Search Jobs and Internships',
        [SEARCH_PAGE_CATEGORIES.COLLEGES]: 'Search Universities',
        [SEARCH_PAGE_CATEGORIES.EMPLOYERS]: 'Search Employers',
        [SEARCH_PAGE_CATEGORIES.CAREER_PATHS]: 'Search Career Paths',
      },
      searchPagePlaceholders: {
        [SEARCH_PAGE_CATEGORIES.DEFAULT]: 'Search opportunities, resources, tools, and more',
        [SEARCH_PAGE_CATEGORIES.SCHOLARSHIPS]: 'Search scholarships',
        [SEARCH_PAGE_CATEGORIES.INTERNSHIPS]: 'Search roles, companies, and locations',
        [SEARCH_PAGE_CATEGORIES.COLLEGES]: 'Search colleges, universities, and locations',
        [SEARCH_PAGE_CATEGORIES.EMPLOYERS]: 'Search employers, companies, and locations',
        [SEARCH_PAGE_CATEGORIES.CAREER_PATHS]: 'Search career paths, industries, and locations',
      },
      noResults: 'Your search term did not yield any results.\nTry something else!',
      clear: 'Clear',
      filters: {
        careerOpportunities: 'Career Opportunities',
        scholarships: 'Scholarships',
        resources: 'Resources',
        people: 'People',
      },
      moreFilters: 'More Filters',
    },
    competency: {
      breadcrumb: 'View all competencies',
      comingSoon: 'Coming Soon',
      noResults: 'Looks like there are no resources for this competency yet.\nCheck back soon!',
      noSearchResults: 'Your search term did not yield any results.\nTry something else!',
      filterButtons: {
        allResources: 'All Resources',
        learning: 'Learning',
        realPeople: 'Real People',
        tools: 'Tools',
      },
      actionTabs: {
        share: 'Share',
        addTask: 'Add Task',
        copyToolTipShareText: (competencyTitle: string, categoryName: string) =>
          `Copied link to ${categoryName} - ${competencyTitle}!`,
      },
      pageErrors: {
        returnHome: 'Return Home',
        invalidId: 'Invalid competency ID format.',
        competencyNotFound: 'Competency not found.',
      },
      columnHeaders: {
        learnAndExplore: NUGGET_THEMES.LEARN_AND_EXPLORE,
        growByDoing: NUGGET_THEMES.GROW_BY_DOING,
        realPeopleRealStories: NUGGET_THEMES.REAL_PEOPLE_REAL_STORIES,
      },
    },
    bookmarks: {
      mostRecent: 'Most Recent',
      noResults: 'You have no bookmarks yet.',
      header: {
        title: 'Bookmarks',
        subtitle: 'Your bookmarks',
      },
      filter: 'Filter',
    },
    markersOfSuccess: {
      pageTitle: 'Markers of Success',
      collegeText: 'Post-Secondary',
      highSchoolText: 'High School',
      button: {
        popoverTitle: 'Compare with...',
        education: 'Education',
        tracks: 'Career Tracks',
      },
    },
    backrsPageShell: {
      menuItems: {
        sidebar: {
          lubav: 'Lubav',
          newChat: 'New Chat',
          dashboard: 'Dashboard',
          blueprint: 'Blueprint',
          careerPaths: 'Career Paths',
          assessMyTrack: 'Are You On Track?',
          lifeReadiness: 'Life Readiness',
          collegeShortList: 'College Shortlist',
          findScholarships: 'Scholarships',
          searchUniversities: 'Explore Universities',
          improveMyOdds: 'Improve My Odds',
          more: 'More',
          coaching: 'Coaching',
          priorities: 'Playbooks',
          bookmarks: 'Bookmarks',
          gettingStarted: 'Get Started',
          library: 'Competency Library',
          tools: 'Tools',
          networks: 'Networks',
          coachingDashboard: 'Coaching Dashboard',
          adminDashboard: 'Users',
          coachingAnalytics: 'Analytics',
          rostersAdmin: 'Cohorts',
          superuser: 'Superuser',
          newVersion: {
            heading: 'We released a new update!',
            subtitleTemplate: 'Backrs {version}',
            ctaLabel: 'Relaunch to update',
          },
        },
        profile: {
          viewProfile: 'View Profile',
          accountSettings: 'Account Settings',
          myStory: 'My Story',
          signOut: 'Sign Out',
          logIn: 'Log In',
        },
      },
    },
    pageShell: {
      header: {
        defaultTitle: 'New Chat',
        description: 'Your personal college and career AI coach',
        newChatButton: 'New Chat',
        newChatButtonShort: 'New',
        searchPlaceholder: 'Search',
        successSystemButton: 'Success System',
        keyLeversButton: 'Key Levers',
        gettingStartedButton: 'Get Started',
        confirmNewChat: {
          header: 'Start over?',
          body: "You're in a solution session. Choose how you'd like to continue.",
          newChat: 'Start a new chat',
          restart: 'Restart this session',
        },
      },
      lubavPanel: {
        reopenTooltip: 'Click or drag to reopen Lubav',
      },
      gettingStartedModal: {
        title: 'Getting Started with Backrs',
        subtitle: 'The best way to get the most out of your Backrs experience.',
        skipButton: 'Skip',
        getStartedButton: 'Get Started',
        steps: {
          blueprint: {
            title: 'Build a Blueprint',
            description:
              'What are your life goals? This assessment helps you define your aspirations and then gives you the building blocks to reach them.',
          },
          lifeReadiness: {
            title: 'Measure your life-readiness',
            description:
              'This 10 minute quiz will give you a better idea of what skills, mindsets, and achievements you should focus on to meet your goals.',
          },
          onTrack: {
            title: 'Are you on track?',
            description:
              "Check whether you're on the right path toward your goals and identify the areas to strengthen your trajectory.",
          },
          explore: {
            title: 'Explore tools, guides, and more',
            description:
              'Browse the full library of resources, guides, and content nuggets to help you reach your goals.',
          },
        },
        sandboxSteps: {
          dashboard: {
            title: 'Explore your coaching dashboard',
            description:
              'Your full roster of students is accessible from here. Filter by grade, school, or program (scholars or foundations).',
          },
          profile: {
            title: "Analyze a student's profile",
            description:
              'Read their summary, then click "Blueprint," "Life Readiness," or "Are You On Track?" for insight into their goals and progress.',
          },
          suggestions: {
            title: 'Get advising suggestions',
            description:
              'Click "Coaching suggestions & follow-ups" or "Email drafting" to see how your AI assistant coach can amplify your advising.',
          },
        },
      },
      signOut: {
        backrsProfile: 'Backrs Profile',
        logOut: 'Log Out',
        logIn: 'Log In',
      },
      menuItems: {
        prosperDashboard: 'Prosper',
        bookmarks: 'Bookmarks',
        search: 'Search',
        myResults: 'My Blueprint',
        startOver: 'Start Over',
        start: 'Start',
        backrs: 'Backrs',
        prosper: 'Prosper',
        lubav: 'Lubav',
        account: 'Account',
        careerTracks: 'Career Tracks',
        careerPaths: 'Career Paths',
        markersOfSuccess: 'Markers of Success',
      },
    },
    intro: {
      facts: 'FACTS.',
      navigation: 'Click the next fact or use the arrows to navigate',
      slides: {
        slide1: {
          title: '90,000 hours',
          description:
            '–that’s how much you’ll work.  Make it count with a career that gives you purpose, joy, and a solid paycheck.',
        },
        slide2: {
          title: 'You have options.',
          description:
            ' There are dozens of career paths that could make you happy and successful, not just one or two.',
        },
        slide3: {
          title: 'Start strong.',
          description:
            ' Getting on the right track early matters; less than 20% of young adults who start off-track ever move up.',
        },
        slide4: {
          title: 'Create your Blueprint',
          description:
            '– Explore careers, learn what it takes, and set goals to achieve the life you want.',
        },
      },
      header: 'Explore careers, learn what it takes, and set goals to achieve the life you want.',
      buttonTitle: 'Start Here',
    },
    generating: {
      body: {
        one: 'Great news! Your aspirations are within reach.\n\nBefore we share your Blueprint we want you know that...\n\nWe’ve identified ',
        two: ' career paths that could be enjoyable, give you meaning, and pay the bills. That may seem like a lot. But there are many paths that could be good fit for most people. And adults these days change career paths 4-7 times over their lives.\n\nTo achieve your aspirations you’ll need to get on (and stay on) at least the ',
        three:
          ' track.\n\nSo please make sure your review the “markers of success” associated with that track.',
      },
      role: {
        whatBestDescribesYou: 'What best describes you?',
        student: 'Student',
        parent: 'Parent',
        educator: 'Educator',
        employer: 'Employer',
      },
      actionButton: 'See My Blueprint',
      unauthenticatedFooter: { one: 'Have an account? ', two: 'Log in', three: ' here.' },
      error:
        'There was an error with your submission. Please try again or contact support@backrs.com',
    },
    start: {
      getStarted: 'Get Started',
      before: 'Before you begin:\nwhich applies to you?',
      highSchool: 'Blueprint for High School',
      college: 'Blueprint for College',
      startHereButtonText: 'Start Here',
      letsGoButtonText: "Let's Go",
      careerTrackDialog: {
        title: "What's a Career Track?",
        bottomContentTop: 'increased income, recognition, opportunity',
        bottomContentBottom: 'higher investment required, competition',
        content: [
          'A career "track" represents your current standing in terms of skill level, achievement, and marketability within the professional landscape. More selective tracks often lead to greater rewards, including higher compensation, increased autonomy, recognition, and opportunities for personal growth and development. However, these tracks typically come with heightened demands, requiring a higher level of education, substantial experience, and the ability to thrive in competitive environments.',
          'It’s crucial to understand that while moving "down" to a less selective track can be relatively straightforward, moving "up" to a more selective track is far more challenging and requires deliberate effort and strategic planning.',
          'By defining a "target track" that aligns with your personal and professional aspirations, you can make more informed decisions regarding your education, work experiences, skill development, and resume building. This clarity not only guides your immediate choices but also helps you create a long-term plan to achieve your career goals.',
        ],
      },
      whatIsACareerTrackModal: {
        header: {
          title: 'What is a Career Track?',
          subtitle: 'Our guide to academic and professional trajectories',
        },
        topLeftSection: {
          title: 'Introduction to Career Tracks',
          description: `Career tracks are academic and professional trajectories that lead to certain levels of pay and growth potential.\n\nThe decisions you make--starting even before high school--often determine which track you may end up on. This track will influence many of your life’s outcomes: what kind of house you can afford, how much you travel, the amount of savings you put away, and more. Most people change careers several times in their lives, but it is rare to change tracks.`,
        },
        middleSection1: {
          title: 'In other words...',
          description: `Imagine a subway map. There are specific inflection points where you can change tracks, but it takes planning and careful timing. \n\nGoing from middle-tier to upper-tier might look like enrolling in graduate school, taking on new responsibilities at work, or gaining a new certification in your field.`,
        },
        middleSection2: {
          title: "Higher isn't always better.",
          description: `“Higher” tracks are more selective, offer greater pay, and will give you more opportunity to make decisions, grow, and lead.\n\nThat doesn't necessarily mean they're better. Higher tracks often require larger investments of time and money and greater amounts of sacrifice in other areas of life.`,
        },
        bottomLeftSection: {
          title: 'How Tracks and Paths Intersect',
          description: `Your **track** is the overall level of career you're aiming for—like Upper-Tier Professional or Trade/Technical—based on things like income, recognition, and education required.\n\nYour **path** is the specific lane within that track—like Nursing, Accounting, or Software Development—that shapes the kind of work you’ll do and how you’ll grow over time.  Most paths includes jobs across tracks, but some paths are mostly trades/technical while others are mostly upper tier and top tier professional.\n\nYou target a track to match your life goals. You pick a path to match your interests, strengths, and where you find meaning.\n\nMost people switch paths three or more times. Less than 10% of people switch tracks after their early 20’s.`,
        },
        tierSalaryData: {
          'semi-skilled': '> $35k',
          'trades-technical': '$33k - $70k',
          'middle-tier-pro': '$50k - $150k',
          'upper-tier-pro': '$250k - $400k',
          'top-tier-pro': '$400k +',
        },
      },
      header: [
        'Define the life you want through a few questions.',
        'If you aren’t sure, share your best guess.',
      ],
    },
    results: {
      minTrackExplanationHeader: 'Your Minimum Track',
      startOver: 'Start Over',
      tour: 'Tour',
      startOverModal: {
        title: 'Start Over?',
        description:
          'This will reset your Blueprint and take you back to the beginning of the survey. Your current Blueprint and saved answers will be replaced once you complete the survey again.',
        cancel: 'Go back',
        confirm: 'Start over',
      },
      feedbackMailto:
        'mailto:support@backrs.com?subject=Blueprint Feedback&body=Share any feedback you have about Blueprint. Make sure to export and include your PDF results in the email as well!',
      diagram: {
        'Top-Tier Professional': {
          title: 'TOP',
          tooltip:
            'A top-tier career includes very prestigious jobs like management consultants, investment bankers, top law firm partners, world-class researchers, and specialist doctors. These require the best education, advanced degrees, and significant achievements. The benefits are excellent pay, professional respect, intellectual challenges, and access to powerful networks. The downsides are long hours, frequent travel, high stress, and constant pressure to stay on top, which can affect personal well-being and family life.\n\nAnnual compensation ranges from $90,000 to over $500,000 per year over a career.',
        },
        'Upper-Tier Professional': {
          title: 'UPPER-TIER',
          subtitle: 'professional',
          tooltip:
            'An upper-tier professional career includes selective jobs in business, law, medicine, engineering, and teaching at universities. These roles usually need advanced degrees, lots of experience, and specific skills. The positives include high salaries, chances for promotions, respect in the industry, and decision-making power. The challenges include long hours, high stress, the possibility of burnout, and the need for ongoing education and financial investment.\n\nAnnual compensation ranges from $80,000 to over $350,000 over a career.',
        },
        'Middle-Tier Professional': {
          title: 'MIDDLE-TIER',
          subtitle: 'professional',
          tooltip:
            "Middle-tier jobs include roles like paralegals, project managers, nurses, and teachers. These typically need a bachelor's degree and sometimes extra certifications. The benefits include decent pay, a good balance between work and personal life, and job security. The downsides can be limited opportunities for promotions and the need to keep learning in some fields.\n\nAnnual compensation ranges from $40,000 to $120,000 over a career.",
        },
        'Trades/Technical': {
          title: 'TRADES\n/TECHNICAL',
          tooltip:
            'A trades or technical career includes specialized training in fields like plumbing, electrical work, welding, HVAC, and healthcare technician jobs. These usually need certifications instead of a four-year degree. The good side includes moderate pay, job security, and shorter training times. However, the work can be physically tough, sometimes dangerous, and requires ongoing education to keep up with changes in the industry.\n\nAnnual compensation ranges from $40,000 to $110,000 over a career.',
        },
        'Semi-skilled': {
          title: 'SEMI-SKILLED',
          subtitle: '(the default)',
          tooltip:
            'Over 120 million Americans hold lower-skilled or semi-skilled jobs across retail, food service, manual labor, call centers, child care, security, care givers, drivers, receptionists, etc. They earn $25,000-$45,000 per year with limited career advancement opportunities and financial challenges, especially in high-cost living areas.\n\nAround 50% of people in these roles have at least some college education.',
        },
        Entrepreneurship: {
          title: 'ENTREPRENEURSHIP',
          tooltip:
            'Entrepreneurship is about starting and running businesses, owning part of a business, or doing side jobs, focusing on new ideas, taking risks, and making the most of opportunities. The positives are having lots of freedom, the chance to make a lot of money, personal satisfaction, and the ability to influence industries. The negatives include financial risks, uneven income, a heavy workload, and difficulties in balancing work and personal life.\n\nAnnual compensation can range widely from very little to over $500,000 over a career.',
        },
      },
      PDF: {
        saveAsPDF: 'Save as PDF',
        suggestedCareerPaths: 'Potential Career Paths',
      },
      minimumTrackTitle: 'Your Blueprint',
      moreInfo: {
        title: 'Want more info?',
        forSupport: 'For support to stay on track, join ',
        forSupportLink: 'Backrs.',
        button: 'Join Backrs',
        information: {
          dashboard: '50+ tools to help you succeed',
          networks: 'Networks to engage successful professionals',
          lifeReadiness: 'Use your strengths to be life-ready',
        },
      },
      sectionDescriptions: {
        bestFit:
          "These paths are among the best potential fits for you. Select a path to learn more. Don't be surprised if they seem more different than similar!",
        suggested:
          'Each path is rated based on alignment to activities you do (or don’t) enjoy, where you might find meaning, and if you can achieve your financial aspirations. Click a specific path to learn more.',
        markersOfSuccess: (track: Tier, schoolLevel: SchoolLevel) =>
          `Below you'll find key activities and milestones for a student in ${schoolLevel === 'high_school' ? 'high school' : 'college'} that will ensure you get on and stay on at least the ${track} track.`,
      },
      bestFitCareerPath: 'Best Fit Career Paths',
      suggestedCareerPaths: 'Potential Career Paths',
      suggestedFits: {
        strong: 'Strong Fit',
        medium: 'Medium Fit',
        low: 'Low Fit',
      },
      aspirationsTitle: 'Your Aspirations',
      aspirationsExplanation:
        'Your responses indicated the following aspirations. To get different results, feel free to take the Blueprint quiz again!',
      'Semi-skilled': {
        aspirationsIndicate:
          'To achieve your aspirations, you likely need to get on and stay on a semi-skilled track or higher.',
        description:
          'Lower-skilled and semi-skilled career tracks include jobs that require little to no formal education or specialized training. Lower-skilled jobs typically involve manual labor or routine tasks, such as working as a cashier, fast food worker, or janitor, with median annual incomes ranging from $25,000 to $30,000. Semi-skilled jobs require some training or experience but not a college degree, with roles like administrative assistants, truck drivers, and customer service representatives earning median incomes of $35,000 to $45,000. These roles are essential to the economy but don’t provide the same opportunities as more skilled careers.\n\nThere are some advantages to these career paths, like starting work right after high school and having job opportunities in various locations. However, the downsides are significant. Lower-skilled and semi-skilled jobs usually pay low wages, offer limited career growth, and can be physically demanding. People in these jobs often struggle to afford basic necessities, like housing and healthcare. These roles also lack job security and benefits, making it difficult to plan for the future or build long-term financial stability.\n\nMany people who end up in these jobs start without a college degree, but surprisingly, a large number have some post-secondary education. Nearly half of workers in these roles have attended college or earned a degree, but for various reasons they end up in lower-paying jobs. While these jobs are common, they are not attractive long-term paths for those seeking financial stability or career growth, and young people should aim for careers that offer more opportunities for success and security.',
        entrepreneurshipText: '',
      },
      'Trades/Technical': {
        aspirationsIndicate:
          'To achieve your aspirations, you likely need to get on and stay on a trades or technical track or higher.',
        description:
          "A trades or technical career path involves getting special training in areas like plumbing, electrical work, welding, HVAC systems, and healthcare jobs like medical assistants, radiologic technologists, and dental hygienists. Some good things about this career path include the chance to earn a good salary, job stability because there is always a need for skilled workers, and the fact that you don’t need to spend as much time in school as you would for a four-year college degree. Starting pay is usually between $40,000 and $55,000, and with experience, it can go up to $55,000 to $85,000, and sometimes as high as $110,000. While this pay offers some chance to build wealth, it's not huge. However, if you combine this career with owning your own business, you could make a lot more money and build more wealth.\n\nSome downsides include the work being physically tough, possibly dangerous, and needing to keep learning new skills as things change in your industry. But even with these challenges, many people find this kind of hands-on work very satisfying because it lets them develop real skills.",
        entrepreneurshipText:
          '\n\nEntrepreneurship can significantly enhance a trades or technical career path by offering skilled workers opportunities beyond traditional employment. For example, a plumber, electrician, or HVAC technician can start their own business, allowing them to set their own rates, choose their projects, and potentially employ others, leading to higher earning potential and more control over their work-life balance. In addition to running their own businesses, tradespeople can engage in side hustles, offering services during off-hours or weekends to supplement their income without leaving full-time jobs, providing flexibility with lower risk. Furthermore, trades professionals can build equity in existing firms by becoming part-owners or partners, enabling them to share in the company’s growth and profits without managing every aspect of the business. Whether through business ownership, side hustles, or equity-building, entrepreneurship allows trades professionals to expand their financial potential while maintaining the hands-on, practical work they find fulfilling.',
      },
      'Middle-Tier Professional': {
        aspirationsIndicate:
          'To achieve your aspirations, you likely need to get on and stay on a mid-tier professional track or higher.',
        description:
          'Middle-tier professionals include jobs like paralegals, some lawyers, project managers, marketing specialists, human resources managers, nurses, teachers, many sales roles, and IT support specialists. These jobs usually require a bachelor’s degree and sometimes extra certifications. Some benefits of this path are the chance to earn a decent salary, some chances for promotions, and jobs that often provide a good balance between work and personal life. Starting pay is usually between $40,000 and $50,000, and with more experience, it can rise to $55,000 to $100,000, and sometimes up to $120,000. While this salary can help you build some wealth, it’s not a huge amount. However, if you combine this career with starting your own business, you could earn a lot more and build more wealth.\n\nSome downsides include the need to keep learning and getting new certifications, the possibility of too many people competing for the same jobs in some fields, and sometimes being stuck at certain job levels unless you get more education. Despite these challenges, many people enjoy the steady career path, the variety of job choices, and the chance to make a difference in roles like nursing, teaching, and IT support.',
        entrepreneurshipText:
          '\n\nMiddle-tier professionals can incorporate entrepreneurship in several ways. They might start a consulting business in areas like project management, marketing, or HR, using their expertise to serve clients, potentially while keeping their full-time job. They could also launch a side business, such as freelance IT support or tutoring, to boost income. Another option is building equity in an existing firm by becoming a partner or investor, benefiting from its growth while applying their skills. Professionals like nurses or teachers might also create and sell online courses or resources, increasing their earnings and career flexibility.',
      },
      'Upper-Tier Professional': {
        aspirationsIndicate:
          'To achieve your aspirations, you likely need to get on and stay on an upper-tier professional track or higher.',
        description:
          'An upper-tier professionals track includes selective careers in business, accounting, consulting, selective technology and engineering roles, corporate law, medicine, and top positions in education. These jobs often require advanced degrees, experience, and high skill levels. Starting pay usually ranges from $80,000 to $200,000, with experienced professionals earning $150,000 to $350,000 or more, sometimes exceeding $500,000. This pay allows for a wealthy lifestyle and significant savings.\n\nPeople in these roles hold decision-making power and influence, earning high respect in their industries. They often advance to leadership positions and receive major recognition.\n\nHowever, these jobs come with challenges like heavy responsibility and stress. Upper-tier professionals often work long, unpredictable hours, making work-life balance difficult. They must keep learning to stay current in their fields, which requires time and money. The combination of stress, long hours, and responsibility can lead to burnout if not managed well. Despite these challenges, many find the rewards make it worthwhile.',
        entrepreneurshipText:
          '\n\nUpper-tier professionals can incorporate entrepreneurship into their careers in several impactful ways. For instance, corporate lawyers or physicians can establish their own firms or private practices, giving them more control over their client base and fees while leveraging their expertise. Professionals in accounting, consulting, or engineering might start boutique firms or niche consulting businesses, providing specialized services and building their reputation independently of large corporations. Additionally, they can become investors or equity partners in startups or existing firms, using their industry knowledge to guide business strategy while generating passive income. Some might also create thought leadership platforms, such as writing books, developing online courses, or hosting webinars, to monetize their expertise while enhancing their professional brand. These entrepreneurial ventures offer increased financial freedom, greater flexibility, and the opportunity to shape their careers on their terms.',
      },
      'Top-Tier Professional': {
        aspirationsIndicate:
          'To achieve your aspirations, you likely need to get on and stay on a top-tier professional track or higher.',
        description:
          'A top-tier career track includes highly competitive jobs like management consulting, investment banking, top law firms, world-class research, and specialist medicine. These roles require the best educational qualifications, advanced degrees, and major professional achievements.\n\nPeople in these careers earn significant pay. Starting salaries range from $90,000 to $200,000, and with experience, can reach $250,000 to $500,000, or even over $1,000,000. This level of income allows for a wealthy lifestyle and substantial savings.\n\nThese jobs come with respect and recognition in their fields and society. The work is often interesting and impactful, offering opportunities to collaborate with influential people and build valuable connections.\n\nHowever, they are demanding. Long hours, frequent travel, and high-pressure environments make work-life balance difficult. Competition is fierce, requiring constant high performance, which can be mentally and emotionally draining. Continuous skill development is necessary, consuming time and energy. The stress, long hours, and performance demands can lead to burnout, but many find working at the top level and making a difference worthwhile.',
        entrepreneurshipText:
          '\n\nTop-tier professionals can incorporate entrepreneurship by starting boutique consulting firms, private practices, or surgery centers, giving them more control over clients and earnings. They can also invest in or advise startups, using their expertise and networks to build equity and foster innovation. Additionally, they may create thought leadership platforms through books, keynote speeches, or exclusive training programs, enhancing their reputation and generating extra income. These entrepreneurial ventures offer greater financial rewards, increased autonomy, and the chance to expand their influence while maintaining their primary high-demand careers.',
      },
      Entrepreneurship: {
        aspirationsIndicate:
          'Your aspirations indicate that you would have to be at minimum an entrepreneur to achieve your ideal lifestyle.',
        description:
          'An entrepreneurship track encompasses a wide spectrum of business ownership and ventures, including starting and running a business, becoming a partial owner in a professional practice, or engaging in side hustles alongside a primary career. This path is defined by its emphasis on innovation, risk-taking, and the pursuit of new market opportunities. Entrepreneurs often create their own opportunities rather than following a traditional career path, leveraging their skills, passion, and creativity to generate income.\n\nFinancial outcomes in entrepreneurship can vary widely. Successful entrepreneurs can achieve significant wealth, potentially earning incomes that surpass those in traditional upper-tier professional roles. However, earnings can also be inconsistent, particularly in the early stages of a venture. Many entrepreneurs experience periods of financial uncertainty and must be willing to reinvest in their businesses to achieve long-term success. While some may reach multi-million-dollar revenues, others may supplement their income with smaller-scale side hustles that provide flexibility and additional revenue streams.\n\nEntrepreneurship offers considerable autonomy, allowing individuals to make key decisions and directly shape the direction of their ventures. This control can lead to a high level of personal satisfaction and the fulfillment of creating something unique. Entrepreneurs also have the opportunity to influence their industry, drive innovation, and potentially make a positive impact on society through their products, services, or business practices.\n\nHowever, this path comes with significant challenges. Entrepreneurs often face considerable risks, including financial losses, market competition, and the pressure of managing all aspects of a business. The workload can be intense, especially during the startup phase, with long hours and a lack of guaranteed income. Success requires adaptability, resilience, and a continuous commitment to learning and growth. Balancing work and personal life can be difficult, as the demands of running a business can be all-consuming.\n\nDespite these challenges, many entrepreneurs find the potential rewards—financial independence, personal growth, and the satisfaction of building a successful venture—to be well worth the risks and efforts involved.',
        entrepreneurshipText: '',
      },
      markersOfSuccessSectionTitle: 'Markers of Success',
    },
    survey: {
      facts: 'FACTS.',
      clickNextSkippableQuestion: 'If none of these apply, just click "Next".',
      highSchoolCollegeQuestion: {
        title: 'Current Enrollment',
        question:
          'Before we begin, we need to know: are you currently enrolled in school? If so, what kind?',
        options: {
          high_school: 'High School',
          college: 'College/University',
          trade: 'Trades/Technical School',
          graduate_school: 'Graduate School',
          community_college: 'Community College',
          no_current_enrollment: 'I am not currently a student',
        },
      },
      blueprintAccountFields: {
        title: 'A few more details',
        subtitle: 'We use these to personalize your Blueprint.',
        zipLabel: 'Zip code',
        zipPlaceholder: 'Enter your zip code',
        zipError: 'Enter a valid 5-digit zip code',
        schoolLabel: 'School',
        schoolPlaceholder: 'Search for your school',
        graduationYearLabel: 'Expected graduation year',
        graduationYearPlaceholder: 'Select a year',
        startTermLabel: 'Start term',
        startTermPlaceholder: 'Select a term',
        startYearLabel: 'Start year',
        startYearPlaceholder: 'Select a year',
      },
      header: {
        title: 'Blueprint',
        subtitle: 'Build a personalized plan for college, career, and life.',
        careerTracks: 'Career Tracks',
        careerPaths: 'Career Paths',
        myBlueprint: 'My Blueprint',
      },
      pageCount: (page: number, total: number) => `Page ${page} of ${total}`,
      calculatingBlueprint: 'Calculating Your Blueprint...',
      popupMessage:
        'Try not to choose every option. Being selective helps us build a blueprint that aligns more closely with your preferences.',
      startYourBlueprint: 'Start Your Blueprint',
      output: {
        title: (name?: string) => (name ? `${name}'s Blueprint` : 'Your Blueprint'),
        subtitle: (date: string, name?: string) =>
          `Based on ${name ? `${name}'s` : 'your'} aspirations as of ${date}`,
        aspirationCategories: {
          sectionTitle: 'Summary of Your Life Aspirations',
          lifestyleAndHome: 'Lifestyle & Home',
          workSettings: 'Work Environments',
          workSettingsTradeoff: 'Work Environments (Preferred)',
          sourcesOfMeaning: 'Sources of Meaning',
          locationsAndOptions: 'Locations & Options',
          careerAttributes: 'Career Attributes',
          activities: 'Activities',
          familyAndCaregiving: 'Family & Caregiving',
          buildingWealth: 'Building Wealth',
          notSurePrompt: 'Not sure about your summary?',
          takeItAgain: 'Take it again',
          discussLabel: 'Discuss with Coach Lubav',
        },
        outputCards: {
          careerPath: {
            key: 'career_path',
            title: (strongFitPaths: number) =>
              `${strongFitPaths > 0 ? strongFitPaths : ''} Career Paths`,
            description: (strongFitPaths: number, mediumFitPaths: number) =>
              `We found ${strongFitPaths > 1 || strongFitPaths !== 0 ? `${strongFitPaths} strong-fit` : ''} career paths that fit your preferences, and ${mediumFitPaths > 0 ? mediumFitPaths : ''} more that would likely be a good fit.`,
            shortTitle: 'Career Path',
          },
          education: {
            key: 'education',
            shortTitle: 'Education',
            title: (education: string) => `${education}`,
            description: (education: string) =>
              `To achieve your aspirations, you likely need to attend a ${education}.`,
          },
          minimumTrack: {
            key: 'minimum_track',
            shortTitle: 'Minimum Track',
            title: (tier: Tier) => `${tier}`,
            description: (tier: Tier) =>
              `To achieve your aspirations, you likely need to get on and stay on an ${tier.toLowerCase()} track or higher.`,
          },
        },
        blueprintSection: {
          title: 'Your Blueprint',
          subtitle: 'Key elements of achieving your life aspirations',
          exploreTradeoffs: 'Explore Tradeoffs',
          discuss: 'Discuss',
          exploreTradeoffsCard: {
            collapsed: {
              title: "How it's all connected...",
              description: 'Explore how your aspirations and requirements are connected.',
            },
            expanded: {
              title: "How it's all connected",
              description:
                'Explore how your aspirations and requirements are connected. Changes here won’t affect your official blueprint unless you select “lock in my changes” after adjusting your responses.',
            },
          },
          exploreTradeoffsView: {
            lockInCta: 'Lock in my changes',
            closeCta: 'Close',
            careerPathsLabel: 'Career Paths',
            lockInError: 'Something went wrong. Please try again.',
            leftPanelTitle: 'What you want',
            rightPanelTitle: 'What it takes',
            careerTrackLabel: 'Career Track',
            educationLabel: 'Educational Investments',
            educationUnavailable: 'Education unavailable',
            retry: 'Retry',
            ownershipLabel: 'Ownership',
            infoIconAriaLabel: 'About this section',
            seeAllFits: 'See all fits →',
            showFewer: 'Show fewer',
          },
          lockInModal: {
            title: 'Update Your Official Blueprint?',
            description:
              'This will replace your locked-in answers with the changes you made in the explorer. Your official Blueprint results — including Career Track, Education Investments, and Career Paths — will update to reflect these new aspirations.',
            cancel: 'Cancel',
            confirm: 'Lock in my changes',
          },
          lockInSuccess:
            'Answers updated: your official blueprint has been updated with your changes',
          minimumTrack: {
            title: 'Minimum Career Track',
            detail: {
              heading: 'Your Minimum Track',
              dateStamp: (date: string) => `Based on your Blueprint as of ${date}`,
              viewCta: 'View All Career Tracks',
            },
            careerTracksModal: {
              title: 'Career Tracks',
            },
          },
          educationalInvestments: {
            title: 'Educational Investments',
            detail: {
              heading: 'Your Education Investments',
              exploreCta: 'Explore Universities',
              toSucceedHeading: 'To Succeed',
            },
          },
          careerPaths: {
            title: (count: number) => `${count} Strong-Fit Career Paths`,
            detail: {
              heading: 'Career Paths for You',
              strongFit: 'Strong Fit',
              mediumFit: 'Medium Fit',
              lowFit: 'Low Fit',
              showMore: 'Show more',
              showLess: 'Show less',
              viewCta: 'View All Career Paths',
            },
          },
          ownership: {
            title: 'Ownership',
            modalTitle: 'Ownership Levels',
            levels: {
              critical: 'Critical',
              important: 'Important',
              lessImportant: 'Less',
            },
            detail: {
              heading: 'Ownership',
              definingOwnershipBody:
                "Ownership means having a financial stake in something beyond your paycheck. That could mean starting your own business. It could also mean picking up a side hustle, investing the money you save, working for a company that gives you equity, or eventually buying into a practice or property. You don't have to be an entrepreneur to think like an owner.",
              definingOwnershipWhyItMatters:
                'Most people who build real wealth don\u2019t do it primarily by working harder. They do it by owning something that grows in value while they work. A salary pays your bills. Ownership builds your future.',
              whyItMattersLabel: "Here's why it matters: ",
              definingOwnershipBadge: 'Defining Ownership',
              rewardsHeading: 'The Rewards',
              rewards: [
                'A path to wealth that goes beyond what any salary can deliver',
                'Your money and assets can grow even when you\u2019re not working',
                'Greater control over your financial future and long-term options',
              ],
              challengesHeading: 'The Challenges',
              challenges: [
                'Ownership requires patience \u2014 returns often come years later, not right away',
                'There is real risk involved, whether you\u2019re investing savings or building something',
                'It takes discipline to prioritize ownership when short-term spending is easier',
              ],
              taglines: {
                critical:
                  "Based on the lifestyle you described, ownership isn't just a nice-to-have \u2014 it's likely necessary. Your goals are ambitious, and a salary alone will probably fall short of getting you there. The earlier you start thinking like an owner, the better your odds.",
                important:
                  "Ownership isn't urgent right now, but it's worth understanding. Building a habit of investing, being open to equity, or keeping a side hustle on the table can meaningfully improve where you end up financially over time.",
                less_important:
                  'Your lifestyle goals are more modest, and a solid career track will likely get you there without requiring ownership as a strategy. That said, an ownership mindset never hurts \u2014 even small habits like investing early compound significantly over time.',
              },
            },
          },
        },
        ctas: {
          takeItAgain: {
            title: 'Your Responses',
            action: 'Take it again',
            description:
              'Want to see how your choices affect your target career track, educational investment, and potential career paths? Take the Blueprint quiz again and compare how different preferences and aspirations can change your trajectory.',
          },
          areYouOnTrack: {
            title: 'You know where you want to go. Are you on track to get there?',
            description:
              'Use our tool to determine where you stand and what it will take to level up.',
            action: 'Are you on track?',
          },
        },
      },
    },
  },
  common: {
    coachLubav: 'Coach Lubav',
    careerTrackShortNames: {
      'semi-skilled': 'Semi-Skilled',
      trades: 'Trades',
      'middle-tier': 'Middle-Tier',
      'upper-tier': 'Upper-Tier',
      'top-tier': 'Top-Tier',
    } as const satisfies Readonly<Record<StandardTrackCode, string>>,
    careerTrackShortestNames: {
      'semi-skilled': 'Semi',
      trades: 'Trades',
      'middle-tier': 'Middle',
      'upper-tier': 'Upper',
      'top-tier': 'Top',
    } as const satisfies Readonly<Record<StandardTrackCode, string>>,
    navigation: {
      myPlaybooks: 'My Playbooks',
    },
    button: {
      next: 'Next',
      back: 'Back',
      save: 'Save',
      letsGo: "Let's Go!",
      viewMore: 'View More +',
      viewLess: 'View Less -',
      showMore: 'Show more',
      showLess: 'Show less',
      previous: 'Previous',
      complete: 'Complete',
      cancel: 'Cancel',
      saveAndContinue: 'Save and Continue',
      tour: 'Tour',
      download: 'Download',
      share: 'Share',
      skip: 'Skip',
    },
  },
  tours: {
    restartTourLater: 'Click here anytime to take this tour again.',
    introPage: {
      step1:
        'We’re going to ask a few questions about the life you want, what you do (and don’t) enjoy doing, how you find meaning, and more. You probably won’t know some of the answers for sure, but share your best guess!',
      step2:
        'Based on the answers you gave on the quiz, we’ll suggest a minimum career track you should aim for. Click here to learn more about what “minimum career track” means.',
      step3:
        'Career tracks are academic and career trajectories that involve different levels of investment, reward, and leadership opportunity. Learn more about the 6 career tracks here.',
      step4: 'Click here to return to this screen whenever you need a refresher on Career Tracks.',
      step5:
        'After suggesting a minimum career track, we’ll share some career paths we think you’ll enjoy exploring. You can click here to learn how we define a “career path.”',
      step6:
        'Career paths are professional trajectories within specific fields. They each encompass many different roles that have related skill requirements, functions, or responsibilities. Learn more here, then click on any of these 70 career paths to get to know them!',
      step7: 'Click here to return to this screen whenever you want to explore more career paths.',
      step8:
        'Use your Blueprint results to create some school, work, or life goals for yourself. These should help you stay on track to build a career that will support your lifestyle aspirations. Don’t forget to record your goals on the Backrs app!',
      step9:
        'You can repeat this quiz as many times as you like. Ready to get started? Click here!',
    },
    results: {
      step1:
        'Here’s a summary of your responses to the Blueprint quiz. These aspirations will form the basis of the recommendations on your blueprint.',
      step2:
        'Are you on track to get what you want out of life? We recommend pursuing educational or professional opportunities that require this level of investment and offer this level of reward in order to achieve your lifestyle aspirations.',
      step3:
        'The decisions you make as a young adult impact which of the following 6 career tracks you end up on as you get older. Once you’re established on a particular track, it’s difficult to move up to one that’s more selective and requires more investment.',
      step4:
        'Here, you’ll find the career paths we think would be strong, medium, or low fits for you based on the aspirations you shared during your quiz. Click one to learn more!',
      step5:
        'Here, you can review some key stats regarding job availability, role distribution across career tracks, and industry trends in this career path.',
      step6:
        'Get personalized recommendations from Lubav on roles within this career path that would suit your aspirations.',
      step7:
        'Here are the steps we recommend you take to ensure you get on the minimum career track you need to achieve your lifestyle aspirations.',
      step8: 'Click on the “plus” button for details on each marker of success we recommend.',
      step9:
        'Make sure to save and/or share your Blueprint. You can send it to your backrs, guidance counselor, or another person supporting you on your academic and career journey.',
    },
  },
} as const;
